import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  useCallback,
} from "react";
import {
  Dimensions,
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type ImageSourcePropType,
} from "react-native";
import {
  ArrowRight,
  CalendarCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock3,
  CircleMinus,
  FlaskConical,
  Funnel,
  Info,
  Map as MapIcon,
  Paperclip,
  TrendingUp,
  XCircle,
  type LucideIcon,
} from "lucide-react-native";
import {
  CardHeader,
  ClinicalDataStatus,
  Collapsible,
  Dialog,
  DialogHeader,
  EmptyStateSubtitleText,
  EmptyStateTitleText,
  DialogIcon,
  DialogPrimaryButton,
  ActionRowCard,
  MetricLabelText,
  MetricValueText,
  MetaText,
  PrimaryTitleText,
  SoftSecondaryButton,
  SectionTitleRow,
  SupportingSubtitleText,
  haptic,
  useToast,
} from "@louise/moon-ui";
import { ConfettiBurst } from "@louise/moon-ui/primitives/ConfettiBurst";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  getTestByName,
  getMedicationByBrandName,
  getProcedureByName,
  type ContentBlock,
} from "@src/packages/content";
import {
  useLocale,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import { useCycleHeaderState } from "@src/packages/cycles/hooks/useCycleHeaderState";
import { presentCycleHeaderForHero } from "@src/packages/cycles/presenters/cycleHeaderHero";
import { buildHeroMetrics } from "@src/packages/cycles/presenters/cycleHeroMetrics";
import { getPostProcedureWaitContext } from "@src/packages/cycles/presenters/postProcedureWait";
import type {
  PostProcedureOutcomeContentKey,
  PostProcedureOutcomeRoute,
} from "@src/packages/cycles/presenters/postProcedureOutcome";
import type { PostProcedureOutcomeResult } from "@src/packages/cycles/presenters/postProcedureOutcomeState";
import {
  formatPhaseTime,
  formatRelativePhaseDate,
} from "@src/packages/cycles/presenters/formatPhaseDate";
import {
  useActiveCycleWithPhases,
  useCycles,
  useCyclePhases,
  type Cycle,
  type CycleType,
  type Phase,
} from "@src/packages/cycles/hooks/useCycles";
import { extractObservedResults } from "@src/packages/cycles/presenters/postCycleState";
import { useTreatmentDashboard } from "../hooks/useTreatmentDashboard";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { calculateAge } from "@src/packages/homepage/utils/patientCalculations";
import { toLocalDateString, getTodayString } from "@src/packages/utils/date";
import { makeTFunc } from "@src/packages/i18n/translations";
import enTranslations from "@src/packages/i18n/locales/en.json";
import type {
  ResultInterpretation,
  TestResultInsightsQueryState,
} from "../intelligence/useTestResultInsights";
import { resultInterpretationKey } from "../intelligence/useTestResultInsights";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { PlanAction } from "../plan.types";
import type { PostCycleHandoffJourney } from "../postCycleHandoff";
import { OrganicMiniIcon, type OrganicMiniIconKind } from "./OrganicMiniIcon";
import {
  AppointmentRow,
  getTodayRowIllustration,
  isCompletableTodayRow,
} from "./AppointmentRow";
import {
  OrganicExplanatoryIllustration,
  OutcomeEstimateFunnelSection,
} from "./EggFreezingBiologyDeepCarouselCard";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "./OutcomeDotFunnelRows";
import {
  OUTCOME_FUNNEL_ILLUSTRATIONS,
  type OutcomeFunnelIllustrationKey,
} from "./OutcomeFunnelIllustrations";
import {
  CryoVaultOverviewCard,
  useCryoSpecimenSnapshotsBuilder,
} from "@src/packages/care/cryovault/CryoVaultOverviewCard";
import {
  useFrozenSpecimens,
  useFrozenSpecimensForCycle,
  useOocyteCohorts,
} from "@src/packages/care/cryovault/useSpecimens";
import { JourneyHandoffCard } from "./JourneyHandoffCard";
import { FutureUseEstimateTabs } from "./FutureUseEstimateTabs";
import { SpermCryosurvivalEstimateWidget } from "./SpermCryosurvivalEstimateCard";
import {
  SpermAnalysisSkeletonCard,
  type SpermAnalysisResults,
} from "./SpermAnalysisSkeletonCard";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";
import { CarePillActionButton } from "./CarePillActionButton";
import { CollapsibleChevronButton } from "./CollapsibleChevronButton";
import {
  Hero,
  HeroWaitProgress,
  type HeroMenuAction,
  type HeroPrimaryAction,
} from "./hero/Hero";
import {
  MedicationRow,
  type MedicationAvailabilityStatus,
  type MedicationForm,
} from "./MedicationRow";
import TestResultInsightCard from "./TestResultInsightCard";
import {
  useLogMedicationDose,
  useMedicationLogs,
  type CreateMedicationLogPayload,
  type MedicationLog,
} from "@src/packages/care/hooks/useMedicationLogs";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import { useTestResults } from "@src/packages/test-results";
import {
  getTodayDoseLog,
  getTodayDoseStatus,
  type DoseStatus,
} from "@src/packages/care/medications";
import {
  getTodayActionCompletion,
  serializeTodayActionCompletion,
  type TodayActionCompletionStatus,
} from "@src/packages/care/todayActionCompletion";
import {
  useCreatePatientNote,
  usePatientNotes,
  type CreateNotePayload,
} from "@src/packages/care/hooks/usePatientNotes";
import { useTimezone } from "@src/packages/preferences";
import {
  usePostProcedureOutcomeStage,
  type PostProcedureOutcomeStage,
} from "../hooks/usePostProcedureOutcomeStage";
import {
  buildEarlyPregnancyDating,
  gestationFromReportedWeeks,
  useCyclePregnancyFollowUp,
} from "../../pregnancy";
import { summarisePregnancyStatus } from "@src/packages/cycles/presenters/pregnancyStatusUpdate";
import { shouldShowEarlyPregnancyGuidance } from "../showEarlyPregnancyGuidance";
import { standingPregnancyCopyKeys } from "../standingPregnancyCopy";
import {
  closedWithoutResultCopyKeys,
  wasClosedWithoutARecordedEnding,
} from "../closedWithoutResultCopy";
import { resolveOutcomeHeroLinks } from "../outcomeHeroLinks";
import { recordedBirthCopyKeys, resolveRecordedBirth } from "../recordedBirth";
import { buildEarlyPregnancyWhatToKnowCards } from "../earlyPregnancyCards";
import { pregnancyTrackingSetupStatusLabel } from "./PregnancyTrackingSetupDialog";
import MonitoringEmptyStateCard from "@src/packages/care/MonitoringEmptyStateCard";

const inCycleHeroBackground =
  require("@src/apps/moon/assets/images/precycle-hero-bronze-gradient.webp") as ImageSourcePropType;

const handoffBackground =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;

const medicationPillIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;
const medicationVialIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;
const medicationPenIllustration =
  require("@src/apps/moon/assets/images/injection-pen.png") as number;
const testTubesIllustration =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

export type HomepageReviewConceptVariant =
  | "setup"
  | "learn-explorer"
  | "trying-naturally"
  | "egg-freezing-active"
  | "egg-freezing-postcycle"
  | "embryo-freezing-active"
  | "embryo-freezing-postcycle"
  | "sperm-freezing-active"
  | "sperm-freezing-wait"
  | "sperm-freezing-postcycle"
  | "ivf-precycle"
  | "ivf-active"
  | "post-retrieval"
  | "fet-active"
  | "beta-wait"
  | PostProcedureOutcomeRoute
  | "iui-precycle"
  | "iui-active"
  | "iui-wait"
  | "oi-wait"
  | "oi-active"
  | "cycle-status-review"
  | "amab-family-building-precycle"
  | "egg-donor"
  | "sperm-donor"
  | "carrier"
  | "intended-parent";

export type HomepageMedicationFactSheetPayload = {
  item: ContentBlock<"medication"> | null;
  fallbackName: string;
  medicationId?: string;
  /** Opens the shared taken/late/missed logger for this scheduled occurrence. */
  onLogDose?: () => void;
  userContext: {
    originalName?: string;
    dosage?: string;
    route?: string | null;
    timeTaken?: string[];
  };
};

export type HomepageProcedureFactSheetPayload = {
  item: ContentBlock<"procedure">;
  appointment: {
    id: string;
    /** Raw appointment UUID — required for delete/update API calls. */
    appointmentId?: string;
    title: string;
    subtitle: string;
    time?: string;
    date?: string;
    notes?: string;
    /** Ownership metadata — required for the shared appointment-management
     * predicate; sheets fail closed without it (#2232). */
    ownership?: string | null;
    ownerId?: string | null;
  };
};

export type HomepageTestFactSheetPayload = {
  item: ContentBlock<"test">;
  testName: string;
};

export type Metric = {
  label: string;
  value: string;
  note?: string;
};

export type Task = {
  title: string;
  detail: string;
  owner: string;
  due: string;
  status: "done" | "now" | "waiting" | "locked";
};

export type TimelineItem = {
  label: string;
  detail: string;
  status: "done" | "now" | "next" | "waiting";
};

export type FunnelStage = {
  label: string;
  count: number;
  detail: string;
};

type PostRetrievalMetricKey =
  | "retrieved_oocytes"
  | "m2_count"
  | "fertilized_oocytes"
  | "embryos_day_3"
  | "embryos_day_5"
  | "frozen_embryo";

type PostRetrievalMetric = NonNullable<Phase["fertilityMetrics"]>[number];

type PostRetrievalMilestone = {
  key: PostRetrievalMetricKey;
  label: string;
  waitingDetail: string;
  illustrationKey: OutcomeFunnelIllustrationKey;
};

function latestPostRetrievalMetric(
  phases: Phase[],
  type: PostRetrievalMetricKey,
): PostRetrievalMetric | null {
  return (
    phases
      .flatMap((phase) => phase.fertilityMetrics ?? [])
      .filter((metric) => metric.type === type)
      .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))
      .at(-1) ?? null
  );
}

function postRetrievalSourceLabel(
  source: PostRetrievalMetric["source"],
  t: TranslationFunction,
): string {
  switch (source) {
    case "provider":
      return t("homepage.review_concept.inline.post_retrieval.from_clinic");
    case "patient":
      return t("homepage.review_concept.inline.post_retrieval.added_by_you");
    case "partner":
      return t(
        "homepage.review_concept.inline.post_retrieval.added_by_partner",
      );
    default:
      return t("homepage.review_concept.inline.post_retrieval.recorded");
  }
}

function buildPostRetrievalMilestones(
  cycleType: CycleType | undefined,
  t: TranslationFunction,
): PostRetrievalMilestone[] {
  const shared: PostRetrievalMilestone[] = [
    {
      key: "retrieved_oocytes",
      label: t(
        "homepage.review_concept.inline.post_retrieval.retrieved_eggs_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.retrieved_eggs_waiting",
      ),
      illustrationKey: "retrieved-eggs",
    },
    {
      key: "m2_count",
      label: t(
        "homepage.review_concept.inline.post_retrieval.mature_eggs_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.mature_eggs_waiting",
      ),
      illustrationKey: "mature-eggs",
    },
  ];

  if (cycleType === "egg_freezing") {
    return [
      ...shared,
      {
        key: "frozen_embryo",
        label: t(
          "homepage.review_concept.inline.post_retrieval.frozen_eggs_label",
        ),
        waitingDetail: t(
          "homepage.review_concept.inline.post_retrieval.frozen_eggs_waiting",
        ),
        illustrationKey: "frozen-eggs",
      },
    ];
  }

  const embryoMilestones: PostRetrievalMilestone[] = [
    {
      key: "fertilized_oocytes",
      label: t(
        "homepage.review_concept.inline.post_retrieval.fertilized_eggs_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.fertilized_eggs_waiting",
      ),
      illustrationKey: "fertilized-eggs",
    },
    {
      key: "embryos_day_3",
      label: t(
        "homepage.review_concept.inline.post_retrieval.day_three_embryos_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.day_three_embryos_waiting",
      ),
      illustrationKey: "day-3-embryos",
    },
    {
      key: "embryos_day_5",
      label: t(
        "homepage.review_concept.inline.post_retrieval.day_five_embryos_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.day_five_embryos_waiting",
      ),
      illustrationKey: "day-5-embryos",
    },
  ];

  if (cycleType === "embryo_freezing") {
    embryoMilestones.push({
      key: "frozen_embryo",
      label: t(
        "homepage.review_concept.inline.post_retrieval.frozen_embryos_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.frozen_embryos_waiting",
      ),
      illustrationKey: "frozen-embryos",
    });
  }

  return [...shared, ...embryoMilestones];
}

function buildPostRetrievalFunnelStages(
  phases: Phase[],
  cycleType: CycleType | undefined,
  t: TranslationFunction,
  onPress: () => void,
): OutcomeDotFunnelStage[] {
  return buildPostRetrievalMilestones(cycleType, t).map((milestone) => {
    const metric = latestPostRetrievalMetric(phases, milestone.key);
    const recordedValue = metric?.value;
    const isRecorded = recordedValue != null;

    return {
      id: milestone.key,
      label: milestone.label,
      value: recordedValue ?? 0,
      detail: isRecorded
        ? postRetrievalSourceLabel(metric?.source ?? "system", t)
        : milestone.waitingDetail,
      missing: !isRecorded,
      illustration: OUTCOME_FUNNEL_ILLUSTRATIONS[milestone.illustrationKey],
      onPress,
    };
  });
}

type TodayRowKind = "medication" | "appointment" | "monitoring" | "task";
type MedicationLogOptionId = "on-time" | "late" | "missed" | "skipped";

export type TodayRow = {
  id: string;
  time: string;
  title: string;
  detail: string;
  /** Free-form notes set by the user when they created the appointment / med. */
  notes?: string;
  partnerName?: string;
  icon: OrganicMiniIconKind;
  status?: string;
  kind: TodayRowKind;
  /** Calendar day for persisted Today action events. */
  date?: string;
  /** Explicitly marks a non-medication row as a patient action. */
  completable?: boolean;
  medicationName?: string;
  medicationId?: string;
  medicationDose?: string;
  medicationRoute?: string | null;
  scheduledFor?: string | null;
  medicationFormat?: string;
  medicationForm?: MedicationForm;
  medicationAvailabilityStatus?: MedicationAvailabilityStatus;
  medicationIsStopped?: boolean;
  medicationEndDate?: string | null;
  /** Raw appointment UUID — required for delete/update API calls.
   * The `id` field above is a composite like `appt-<uuid>` used for React
   * keys and routing; the bare UUID needs to be plumbed separately. */
  appointmentId?: string;
  /** Ownership metadata for the shared appointment-management predicate —
   * sheets fail closed without it (#2232). */
  ownership?: "patient" | "provider";
  ownerId?: string;
  procedureName?: string;
  appointmentResultType?:
    "monitoring" | "bloodwork" | "collection" | "lab" | "call" | "storage";
};

export type DecisionOption = {
  title: string;
  detail: string;
  status: string;
};

const MEDICATION_LOG_VISUALS: Record<
  MedicationLogOptionId,
  {
    icon: LucideIcon;
    color: string;
    iconBackgroundColor: string;
    selectedBackgroundColor: string;
    selectedBorderColor: string;
  }
> = {
  "on-time": {
    icon: CheckCircle2,
    color: "#3F7E58",
    iconBackgroundColor: "rgba(130,212,168,0.2)",
    selectedBackgroundColor: "rgba(130,212,168,0.15)",
    selectedBorderColor: "rgba(63,126,88,0.42)",
  },
  late: {
    icon: Clock3,
    color: "#926123",
    iconBackgroundColor: "rgba(245,200,130,0.24)",
    selectedBackgroundColor: "rgba(245,200,130,0.18)",
    selectedBorderColor: "rgba(146,97,35,0.4)",
  },
  missed: {
    icon: XCircle,
    color: "#A44B3B",
    iconBackgroundColor: "rgba(232,146,124,0.18)",
    selectedBackgroundColor: "rgba(232,146,124,0.15)",
    selectedBorderColor: "rgba(164,75,59,0.4)",
  },
  skipped: {
    icon: CircleMinus,
    color: EMBER.colors.textLight,
    iconBackgroundColor: `${EMBER.colors.textLight}20`,
    selectedBackgroundColor: `${EMBER.colors.textLight}18`,
    selectedBorderColor: `${EMBER.colors.textLight}66`,
  },
};

/**
 * A step on a "what to know" card.
 *
 * A plain string is a line of prose. The object form carries a `tag` — a short
 * value that belongs to *this* patient rather than to the copy, such as the
 * calendar window a milestone falls in for her. It is rendered as a pill so it
 * reads as hers at a glance instead of dissolving into the sentence.
 */
export type WhatToKnowBullet = {
  label: string;
  tag?: string | null;
  /**
   * One of the app's own organic icons, standing in for the bullet's dot.
   *
   * Presentational only, and never carrying meaning the words do not: a reader
   * who sees none of them loses nothing. It is here to give a list of steps a
   * shape the eye can move down, which four identical dots do not — drawn from
   * the set the rest of the app uses rather than from emoji, which belong to the
   * platform's voice and not to Louise's.
   */
  icon?: OrganicMiniIconKind | null;
};

export type WhatToKnowCard = {
  badge: string;
  title: string;
  body: string;
  bullets: (string | WhatToKnowBullet)[];
  tone: "steady" | "caution";
  /**
   * One of the app's own glyphs, drawn large beside the badge.
   *
   * Optional: a card without one reads exactly as it did. Where it is set it
   * says what kind of thing the card is about — a scan, a hormone test, a call
   * to the clinic — in Louise's hand rather than the platform's.
   */
  illustration?: OrganicMiniIconKind;
};

type LabUpdateMetric = {
  label: string;
  value: string;
  note: string;
};

export type LabUpdateCard = {
  title: string;
  timeframe: string;
  status: "next" | "pending";
  body: string;
  metrics: LabUpdateMetric[];
};

type CycleLogisticsChecklistItem = {
  label: string;
  detail: string;
  status: "done" | "next" | "waiting";
};

export type CycleLogisticsCard = {
  title: string;
  timeframe: string;
  body: string;
  icon: OrganicMiniIconKind;
  items: CycleLogisticsChecklistItem[];
  action?: PlanAction;
};

export type TestResultsVariant = "default" | "iui-active";

export type Widget =
  | {
      type: "metrics";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      metrics: Metric[];
    }
  | {
      type: "today";
      title: string;
      subtitle?: string;
      rows: TodayRow[];
    }
  | {
      type: "action-rows";
      rows: OutcomeActionRow[];
    }
  | {
      type: "tasks";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      tasks: Task[];
      action?: PlanAction;
    }
  | {
      type: "timeline";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      items: TimelineItem[];
    }
  | {
      type: "funnel";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      stages: FunnelStage[];
      action?: PlanAction;
      /** When true, renders just the dot rows (no card title/icon/action)
       *  so the section title is the only heading. */
      bare?: boolean;
    }
  | {
      type: "outcome-estimate-funnel";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      variant: "egg-freezing" | "embryo-freezing";
    }
  | {
      type: "lab-carousel";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      cards: LabUpdateCard[];
    }
  | {
      type: "cycle-logistics";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      cards: CycleLogisticsCard[];
    }
  | {
      type: "decision";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      options: DecisionOption[];
      action?: PlanAction;
    }
  | {
      type: "what-to-know";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      cards: WhatToKnowCard[];
    }
  | {
      type: "test-results";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      variant?: TestResultsVariant;
    }
  | {
      type: "data-status";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      detail: string;
      action?: PlanAction;
    }
  | {
      type: "monitoring-chart";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      primaryAction: PlanAction;
    }
  | {
      type: "cryo-vault";
      title?: string;
      subtitle?: string;
    }
  | {
      type: "future-use-estimate";
      primaryUnit?: "eggs" | "embryos";
    }
  | {
      type: "sperm-cryosurvival-estimate";
    }
  | {
      type: "handoff";
      title: string;
      body: string;
      ctaLabel: string;
      checklist: string[];
      action: PlanAction;
    }
  | {
      type: "semen-analysis";
      title: string;
      subtitle?: string;
      results?: SpermAnalysisResults | null;
    };

export type ConceptSection = {
  title: string;
  subtitle: string;
  widgets: Widget[];
  action?: PlanAction;
};

type Concept = {
  eyebrow: string;
  title: string;
  summary: string;
  icon: OrganicMiniIconKind;
  heroMetrics?: Metric[];
  primaryAction?: PlanAction;
  sections: ConceptSection[];
};

type OutcomeActionRow = {
  title: string;
  detail: string;
  icon: OrganicMiniIconKind;
  action?: PlanAction;
};

const REVIEW_VARIANTS = new Set<HomepageReviewConceptVariant>([
  "setup",
  "learn-explorer",
  "trying-naturally",
  "egg-freezing-active",
  "egg-freezing-postcycle",
  "embryo-freezing-active",
  "embryo-freezing-postcycle",
  "sperm-freezing-active",
  "sperm-freezing-wait",
  "sperm-freezing-postcycle",
  "ivf-precycle",
  "ivf-active",
  "post-retrieval",
  "fet-active",
  "beta-wait",
  "ivf-positive",
  "ivf-negative",
  "ivf-uncertain",
  "ivf-adverse",
  "ivf-unavailable",
  "fet-positive",
  "fet-negative",
  "fet-uncertain",
  "fet-adverse",
  "fet-unavailable",
  "iui-positive",
  "iui-negative",
  "iui-uncertain",
  "iui-adverse",
  "iui-unavailable",
  "oi-positive",
  "oi-negative",
  "oi-uncertain",
  "oi-adverse",
  "oi-unavailable",
  "iui-precycle",
  "iui-active",
  "iui-wait",
  "oi-wait",
  "oi-active",
  "cycle-status-review",
  "amab-family-building-precycle",
  "egg-donor",
  "sperm-donor",
  "carrier",
  "intended-parent",
]);

const POST_PROCEDURE_OUTCOME_VARIANTS = new Set<PostProcedureOutcomeRoute>([
  "ivf-positive",
  "ivf-negative",
  "ivf-uncertain",
  "ivf-adverse",
  "ivf-unavailable",
  "fet-positive",
  "fet-negative",
  "fet-uncertain",
  "fet-adverse",
  "fet-unavailable",
  "iui-positive",
  "iui-negative",
  "iui-uncertain",
  "iui-adverse",
  "iui-unavailable",
  "oi-positive",
  "oi-negative",
  "oi-uncertain",
  "oi-adverse",
  "oi-unavailable",
]);

export type CycleStatusReviewKind = "cancelled" | "converted";

const POST_CYCLE_VARIANTS = new Set<HomepageReviewConceptVariant>([
  "egg-freezing-postcycle",
  "embryo-freezing-postcycle",
  "sperm-freezing-postcycle",
]);

export function isPostCycleVariant(
  value: HomepageReviewConceptVariant | null | undefined,
): boolean {
  return value != null && POST_CYCLE_VARIANTS.has(value);
}

export function isPostProcedureOutcomeVariant(
  value: HomepageReviewConceptVariant | null | undefined,
): value is PostProcedureOutcomeRoute {
  return (
    value != null &&
    POST_PROCEDURE_OUTCOME_VARIANTS.has(value as PostProcedureOutcomeRoute)
  );
}

export function getPostProcedureOutcomeContentKey(
  value: HomepageReviewConceptVariant | null | undefined,
): PostProcedureOutcomeContentKey | null {
  if (!isPostProcedureOutcomeVariant(value)) return null;
  return value.replace(
    /-(positive|negative|uncertain|adverse|unavailable)$/,
    "",
  ) as PostProcedureOutcomeContentKey;
}

export function getHomepageReviewConceptVariant(
  value: string | null,
): HomepageReviewConceptVariant | null {
  return REVIEW_VARIANTS.has(value as HomepageReviewConceptVariant)
    ? (value as HomepageReviewConceptVariant)
    : null;
}

/**
 * The post-cycle Home's closing card: "decide your next step".
 *
 * It is not a widget in the variant's list any more, because it has to be the
 * last block of the page and a list renders above the records section
 * `PlanHomeScreen` appends after this component. It lives here rather than in
 * either screen because both render it — `PlanHomeScreen` at the end of its
 * page, `HomepageCanvasScreen` through `endOfPageSlot` — and a second copy of
 * its ten copy keys is exactly how the two would drift apart.
 */
export function PostCycleHandoffCard({
  journey,
  onAction,
}: {
  journey: PostCycleHandoffJourney;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const RC = "homepage.review_concept.inline";
  const ctaLabel = t(`${RC}.handoff_${journey}_cta`);

  return (
    <JourneyHandoffCard
      title={t(`${RC}.handoff_decide_title`)}
      body={t(`${RC}.handoff_${journey}_body`)}
      ctaLabel={ctaLabel}
      checklist={[1, 2, 3].map((item) =>
        t(`${RC}.handoff_${journey}_check_${item}`),
      )}
      backgroundImage={handoffBackground}
      illustration={<OrganicExplanatoryIllustration kind="follicle-cohort" />}
      onPress={() =>
        onAction({
          label: ctaLabel,
          signal: "open-ai-chat",
          params: {
            initialPrompt: t(`${RC}.handoff_${journey}_chat_prompt`),
          },
        })
      }
    />
  );
}

// Static English fallback `t()` used by module-level data builders so the
// exported `*Cards` constants stay typed and stable. Localized rendering
// goes through React hooks that pass a real `t` into the same builders.
const tEn = makeTFunc(enTranslations);

function buildActions(t: TranslationFunction) {
  const RC2 = "homepage.review_concept.inline";
  return {
    calendar: {
      label: t(`${RC2}.action_calendar`),
      screen: "CareOverview",
    },
    medicalHistory: {
      label: t(`${RC2}.action_complete_history`),
      screen: "MedicalHistory",
    },
    testing: {
      label: t(`${RC2}.action_check_tests`),
      screen: "AtHomeFertilityTesting",
    },
    findCare: {
      label: t(`${RC2}.action_find_care`),
      screen: "FindCareScreen",
    },
    coverage: {
      label: t(`${RC2}.action_check_coverage`),
      signal: "open-coverage-status-modal",
    },
    programs: {
      label: t(`${RC2}.action_open_program`),
      screen: "ProgramsScreen",
    },
    documents: {
      label: t(`${RC2}.action_open_documents`),
      screen: "DocumentsScreen",
    },
    medications: {
      label: t(`${RC2}.action_review_meds`),
      screen: "CareOverview",
      params: { focus: "medications" },
    },
    careTeam: {
      label: t(`${RC2}.action_open_care_team`),
      screen: "CareOverview",
      params: { focus: "care-team" },
    },
    logMonitoring: {
      label: t(`${RC2}.action_log_monitoring`),
      screen: "CycleDetailScreen",
      params: { focus: "monitoring" },
    },
    logSymptoms: {
      label: t(`${RC2}.action_log_symptoms`),
      screen: "CycleDetailScreen",
      params: { focus: "symptoms" },
    },
  } satisfies Record<string, PlanAction>;
}

export function buildEggLabUpdateCards(
  t: TranslationFunction,
): LabUpdateCard[] {
  const RC2 = "homepage.review_concept.egg_lab";
  return [
    {
      title: t(`${RC2}.retrieval.title`),
      timeframe: t(`${RC2}.retrieval.timeframe`),
      status: "next",
      body: t(`${RC2}.retrieval.body`),
      metrics: [
        {
          label: t(`${RC2}.retrieval.m1_label`),
          value: t(`${RC2}.retrieval.m1_value`),
          note: t(`${RC2}.retrieval.m1_note`),
        },
        {
          label: t(`${RC2}.retrieval.m2_label`),
          value: t(`${RC2}.retrieval.m2_value`),
          note: t(`${RC2}.retrieval.m2_note`),
        },
        {
          label: t(`${RC2}.retrieval.m3_label`),
          value: t(`${RC2}.retrieval.m3_value`),
          note: t(`${RC2}.retrieval.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.maturity.title`),
      timeframe: t(`${RC2}.maturity.timeframe`),
      status: "pending",
      body: t(`${RC2}.maturity.body`),
      metrics: [
        {
          label: "MII",
          value: t(`${RC2}.maturity.m1_value`),
          note: t(`${RC2}.maturity.m1_note`),
        },
        {
          label: t(`${RC2}.maturity.m2_label`),
          value: t(`${RC2}.maturity.m2_value`),
          note: t(`${RC2}.maturity.m2_note`),
        },
        {
          label: t(`${RC2}.maturity.m3_label`),
          value: t(`${RC2}.maturity.m3_value`),
          note: t(`${RC2}.maturity.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.vitrification.title`),
      timeframe: t(`${RC2}.vitrification.timeframe`),
      status: "pending",
      body: t(`${RC2}.vitrification.body`),
      metrics: [
        {
          label: t(`${RC2}.vitrification.m1_label`),
          value: t(`${RC2}.vitrification.m1_value`),
          note: t(`${RC2}.vitrification.m1_note`),
        },
        {
          label: t(`${RC2}.vitrification.m2_label`),
          value: t(`${RC2}.vitrification.m2_value`),
          note: t(`${RC2}.vitrification.m2_note`),
        },
        {
          label: t(`${RC2}.vitrification.m3_label`),
          value: t(`${RC2}.vitrification.m3_value`),
          note: t(`${RC2}.vitrification.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.storage.title`),
      timeframe: t(`${RC2}.storage.timeframe`),
      status: "pending",
      body: t(`${RC2}.storage.body`),
      metrics: [
        {
          label: t(`${RC2}.storage.m1_label`),
          value: t(`${RC2}.storage.m1_value`),
          note: t(`${RC2}.storage.m1_note`),
        },
        {
          label: t(`${RC2}.storage.m2_label`),
          value: t(`${RC2}.storage.m2_value`),
          note: t(`${RC2}.storage.m2_note`),
        },
        {
          label: t(`${RC2}.storage.m3_label`),
          value: t(`${RC2}.storage.m3_value`),
          note: t(`${RC2}.storage.m3_note`),
        },
      ],
    },
  ];
}

export const eggLabUpdateCards: LabUpdateCard[] = buildEggLabUpdateCards(tEn);

function buildEmbryoLabUpdateCards(t: TranslationFunction): LabUpdateCard[] {
  const RC2 = "homepage.review_concept.embryo_lab";
  const pending = t("homepage.review_concept.pending_label");
  // Reuse "Pending" via fall-back since several embryo_lab subkeys omit value
  const P = pending.charAt(0).toUpperCase() + pending.slice(1);
  return [
    {
      title: t(`${RC2}.retrieval.title`),
      timeframe: t(`${RC2}.retrieval.timeframe`),
      status: "next",
      body: t(`${RC2}.retrieval.body`),
      metrics: [
        {
          label: t(`${RC2}.retrieval.m1_label`),
          value: P,
          note: t(`${RC2}.retrieval.m1_note`),
        },
        {
          label: t(`${RC2}.retrieval.m2_label`),
          value: P,
          note: t(`${RC2}.retrieval.m2_note`),
        },
        {
          label: t(`${RC2}.retrieval.m3_label`),
          value: "ICSI",
          note: t(`${RC2}.retrieval.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.day1.title`),
      timeframe: t(`${RC2}.day1.timeframe`),
      status: "pending",
      body: t(`${RC2}.day1.body`),
      metrics: [
        {
          label: "2PN",
          value: P,
          note: t(`${RC2}.day1.m1_note`),
        },
        {
          label: t(`${RC2}.day1.m2_label`),
          value: P,
          note: t(`${RC2}.day1.m2_note`),
        },
        {
          label: t(`${RC2}.day1.m3_label`),
          value: t(`${RC2}.day1.m3_value`),
          note: t(`${RC2}.day1.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.day2_3.title`),
      timeframe: t(`${RC2}.day2_3.timeframe`),
      status: "pending",
      body: t(`${RC2}.day2_3.body`),
      metrics: [
        {
          label: t(`${RC2}.day2_3.m1_label`),
          value: P,
          note: t(`${RC2}.day2_3.m1_note`),
        },
        {
          label: t(`${RC2}.day2_3.m2_label`),
          value: P,
          note: t(`${RC2}.day2_3.m2_note`),
        },
        {
          label: t(`${RC2}.day2_3.m3_label`),
          value: t(`${RC2}.day2_3.m3_value`),
          note: t(`${RC2}.day2_3.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.day5_7.title`),
      timeframe: t(`${RC2}.day5_7.timeframe`),
      status: "pending",
      body: t(`${RC2}.day5_7.body`),
      metrics: [
        {
          label: t(`${RC2}.day5_7.m1_label`),
          value: P,
          note: t(`${RC2}.day5_7.m1_note`),
        },
        {
          label: t(`${RC2}.day5_7.m2_label`),
          value: P,
          note: t(`${RC2}.day5_7.m2_note`),
        },
        {
          label: t(`${RC2}.day5_7.m3_label`),
          value: P,
          note: t(`${RC2}.day5_7.m3_note`),
        },
      ],
    },
    {
      title: "PGT-A",
      timeframe: t(`${RC2}.pgta.timeframe`),
      status: "pending",
      body: t(`${RC2}.pgta.body`),
      metrics: [
        {
          label: t(`${RC2}.pgta.m1_label`),
          value: P,
          note: t(`${RC2}.pgta.m1_note`),
        },
        {
          label: t(`${RC2}.pgta.m2_label`),
          value: P,
          note: t(`${RC2}.pgta.m2_note`),
        },
        {
          label: t(`${RC2}.pgta.m3_label`),
          value: t(`${RC2}.pgta.m3_value`),
          note: t(`${RC2}.pgta.m3_note`),
        },
      ],
    },
  ];
}

const embryoLabUpdateCards: LabUpdateCard[] = buildEmbryoLabUpdateCards(tEn);

function buildMedSupplyCard(t: TranslationFunction): CycleLogisticsCard {
  const RC2 = "homepage.review_concept.logistics.med_supply";
  return {
    title: t(`${RC2}.title`),
    timeframe: t(`${RC2}.timeframe`),
    icon: "hormones",
    body: t(`${RC2}.body`),
    action: buildActions(t).medications,
    items: [
      {
        label: t(`${RC2}.i1_label`),
        detail: t(`${RC2}.i1_detail`),
        status: "done",
      },
      {
        label: t(`${RC2}.i2_label`),
        detail: t(`${RC2}.i2_detail`),
        status: "next",
      },
      {
        label: t(`${RC2}.i3_label`),
        detail: t(`${RC2}.i3_detail`),
        status: "next",
      },
    ],
  };
}

function buildRetrievalPrepCard(t: TranslationFunction): CycleLogisticsCard {
  const RC2 = "homepage.review_concept.logistics.retrieval_prep";
  return {
    title: t(`${RC2}.title`),
    timeframe: t(`${RC2}.timeframe`),
    icon: "start",
    body: t(`${RC2}.body`),
    items: [
      {
        label: t(`${RC2}.i1_label`),
        detail: t(`${RC2}.i1_detail`),
        status: "next",
      },
      {
        label: t(`${RC2}.i2_label`),
        detail: t(`${RC2}.i2_detail`),
        status: "waiting",
      },
      {
        label: t(`${RC2}.i3_label`),
        detail: t(`${RC2}.i3_detail`),
        status: "waiting",
      },
    ],
  };
}

function buildCareTeamCard(t: TranslationFunction): CycleLogisticsCard {
  const RC2 = "homepage.review_concept.logistics.care_team";
  return {
    title: t(`${RC2}.title`),
    timeframe: t(`${RC2}.timeframe`),
    icon: "partner",
    body: t(`${RC2}.body`),
    action: buildActions(t).careTeam,
    items: [
      {
        label: t(`${RC2}.i1_label`),
        detail: t(`${RC2}.i1_detail`),
        status: "done",
      },
      {
        label: t(`${RC2}.i2_label`),
        detail: t(`${RC2}.i2_detail`),
        status: "next",
      },
      {
        label: t(`${RC2}.i3_label`),
        detail: t(`${RC2}.i3_detail`),
        status: "next",
      },
    ],
  };
}

function buildEmbryoPaperworkCard(t: TranslationFunction): CycleLogisticsCard {
  const RC2 = "homepage.review_concept.logistics.embryo_paperwork";
  return {
    title: t(`${RC2}.title`),
    timeframe: t(`${RC2}.timeframe`),
    icon: "panel",
    body: t(`${RC2}.body`),
    action: buildActions(t).documents,
    items: [
      {
        label: t(`${RC2}.i1_label`),
        detail: t(`${RC2}.i1_detail`),
        status: "done",
      },
      {
        label: t(`${RC2}.i2_label`),
        detail: t(`${RC2}.i2_detail`),
        status: "next",
      },
      {
        label: t(`${RC2}.i3_label`),
        detail: t(`${RC2}.i3_detail`),
        status: "waiting",
      },
    ],
  };
}

function buildEggConsentCard(t: TranslationFunction): CycleLogisticsCard {
  const RC2 = "homepage.review_concept.logistics.egg_consent";
  return {
    title: t(`${RC2}.title`),
    timeframe: t(`${RC2}.timeframe`),
    icon: "cryo",
    body: t(`${RC2}.body`),
    action: buildActions(t).documents,
    items: [
      {
        label: t(`${RC2}.i1_label`),
        detail: t(`${RC2}.i1_detail`),
        status: "next",
      },
      {
        label: t(`${RC2}.i2_label`),
        detail: t(`${RC2}.i2_detail`),
        status: "waiting",
      },
      {
        label: t(`${RC2}.i3_label`),
        detail: t(`${RC2}.i3_detail`),
        status: "waiting",
      },
    ],
  };
}

function buildEmbryoCycleLogisticsCards(
  t: TranslationFunction,
): CycleLogisticsCard[] {
  return [
    buildMedSupplyCard(t),
    buildRetrievalPrepCard(t),
    buildEmbryoPaperworkCard(t),
    buildCareTeamCard(t),
  ];
}

function buildEggFreezingCycleLogisticsCards(
  t: TranslationFunction,
): CycleLogisticsCard[] {
  return [
    buildMedSupplyCard(t),
    buildRetrievalPrepCard(t),
    buildEggConsentCard(t),
    buildCareTeamCard(t),
  ];
}

const embryoCycleLogisticsCards: CycleLogisticsCard[] =
  buildEmbryoCycleLogisticsCards(tEn);

const eggFreezingCycleLogisticsCards: CycleLogisticsCard[] =
  buildEggFreezingCycleLogisticsCards(tEn);

export function buildEmbryoFunnel(t: TranslationFunction): FunnelStage[] {
  const RC2 = "homepage.review_concept.embryo_funnel";
  return [
    { label: t(`${RC2}.s1_label`), count: 12, detail: t(`${RC2}.s1_detail`) },
    { label: t(`${RC2}.s2_label`), count: 10, detail: t(`${RC2}.s2_detail`) },
    { label: t(`${RC2}.s3_label`), count: 8, detail: t(`${RC2}.s3_detail`) },
    {
      label: t(`${RC2}.s4_label`),
      count: 4,
      detail: t(`${RC2}.s4_detail`),
    },
    {
      label: t(`${RC2}.s5_label`),
      count: 2,
      detail: t(`${RC2}.s5_detail`),
    },
  ];
}

export const embryoFunnel: FunnelStage[] = buildEmbryoFunnel(tEn);

export function buildBetaMetrics(t: TranslationFunction): Metric[] {
  const RC2 = "homepage.review_concept.beta";
  return [
    {
      label: t(`${RC2}.date_label`),
      value: "May 24",
      note: t(`${RC2}.date_note`),
    },
    {
      label: t(`${RC2}.dpt_label`),
      value: "6",
      note: t(`${RC2}.dpt_note`),
    },
    {
      label: t(`${RC2}.meds_label`),
      value: t(`${RC2}.meds_value`),
      note: t(`${RC2}.meds_note`),
    },
    {
      label: t(`${RC2}.symptoms_label`),
      value: t(`${RC2}.symptoms_value`),
      note: t(`${RC2}.symptoms_note`),
    },
  ];
}

export const betaMetrics: Metric[] = buildBetaMetrics(tEn);

function buildStimulationWhatToKnowCards(
  t: TranslationFunction,
): WhatToKnowCard[] {
  const RC2 = "homepage.review_concept.stim_cards";
  return [
    {
      badge: t(`${RC2}.support.badge`),
      title: t(`${RC2}.support.title`),
      body: t(`${RC2}.support.body`),
      bullets: [
        t(`${RC2}.support.b1`),
        t(`${RC2}.support.b2`),
        t(`${RC2}.support.b3`),
        t(`${RC2}.support.b4`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.safety.badge`),
      title: t(`${RC2}.safety.title`),
      body: t(`${RC2}.safety.body`),
      bullets: [
        t(`${RC2}.safety.b1`),
        t(`${RC2}.safety.b2`),
        t(`${RC2}.safety.b3`),
        t(`${RC2}.safety.b4`),
      ],
      tone: "caution",
    },
    {
      badge: t(`${RC2}.nutrition.badge`),
      title: t(`${RC2}.nutrition.title`),
      body: t(`${RC2}.nutrition.body`),
      bullets: [
        t(`${RC2}.nutrition.b1`),
        t(`${RC2}.nutrition.b2`),
        t(`${RC2}.nutrition.b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.movement.badge`),
      title: t(`${RC2}.movement.title`),
      body: t(`${RC2}.movement.body`),
      bullets: [
        t(`${RC2}.movement.b1`),
        t(`${RC2}.movement.b2`),
        t(`${RC2}.movement.b3`),
      ],
      tone: "caution",
    },
    {
      badge: t(`${RC2}.medication.badge`),
      title: t(`${RC2}.medication.title`),
      body: t(`${RC2}.medication.body`),
      bullets: [
        t(`${RC2}.medication.b1`),
        t(`${RC2}.medication.b2`),
        t(`${RC2}.medication.b3`),
      ],
      tone: "caution",
    },
    {
      badge: t(`${RC2}.supplements.badge`),
      title: t(`${RC2}.supplements.title`),
      body: t(`${RC2}.supplements.body`),
      bullets: [
        t(`${RC2}.supplements.b1`),
        t(`${RC2}.supplements.b2`),
        t(`${RC2}.supplements.b3`),
      ],
      tone: "steady",
    },
  ];
}

const stimulationWhatToKnowCards: WhatToKnowCard[] =
  buildStimulationWhatToKnowCards(tEn);

function buildIuiActiveWhatToKnowCards(
  t: TranslationFunction,
): WhatToKnowCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      badge: t(`${RC2}.iui_what_to_know_timing_badge`),
      title: t(`${RC2}.iui_what_to_know_timing_title`),
      body: t(`${RC2}.iui_what_to_know_timing_body`),
      bullets: [
        t(`${RC2}.iui_what_to_know_timing_b1`),
        t(`${RC2}.iui_what_to_know_timing_b2`),
        t(`${RC2}.iui_what_to_know_timing_b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.iui_what_to_know_sperm_badge`),
      title: t(`${RC2}.iui_what_to_know_sperm_title`),
      body: t(`${RC2}.iui_what_to_know_sperm_body`),
      bullets: [
        t(`${RC2}.iui_what_to_know_sperm_b1`),
        t(`${RC2}.iui_what_to_know_sperm_b2`),
        t(`${RC2}.iui_what_to_know_sperm_b3`),
      ],
      tone: "caution",
    },
    {
      badge: t(`${RC2}.iui_what_to_know_after_badge`),
      title: t(`${RC2}.iui_what_to_know_after_title`),
      body: t(`${RC2}.iui_what_to_know_after_body`),
      bullets: [
        t(`${RC2}.iui_what_to_know_after_b1`),
        t(`${RC2}.iui_what_to_know_after_b2`),
        t(`${RC2}.iui_what_to_know_after_b3`),
      ],
      tone: "steady",
    },
  ];
}

const iuiActiveWhatToKnowCards: WhatToKnowCard[] =
  buildIuiActiveWhatToKnowCards(tEn);

function buildIuiCycleLogisticsCards(
  t: TranslationFunction,
): CycleLogisticsCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      title: t(`${RC2}.iui_logistics_trigger_title`),
      timeframe: t(`${RC2}.iui_logistics_trigger_timeframe`),
      icon: "hormones",
      body: t(`${RC2}.iui_logistics_trigger_body`),
      action: buildActions(t).medications,
      items: [
        {
          label: t(`${RC2}.iui_logistics_trigger_i1_label`),
          detail: t(`${RC2}.iui_logistics_trigger_i1_detail`),
          status: "done",
        },
        {
          label: t(`${RC2}.iui_logistics_trigger_i2_label`),
          detail: t(`${RC2}.iui_logistics_trigger_i2_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.iui_logistics_trigger_i3_label`),
          detail: t(`${RC2}.iui_logistics_trigger_i3_detail`),
          status: "waiting",
        },
      ],
    },
    {
      title: t(`${RC2}.iui_logistics_sperm_title`),
      timeframe: t(`${RC2}.iui_logistics_sperm_timeframe`),
      icon: "sperm",
      body: t(`${RC2}.iui_logistics_sperm_body`),
      items: [
        {
          label: t(`${RC2}.iui_logistics_sperm_i1_label`),
          detail: t(`${RC2}.iui_logistics_sperm_i1_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.iui_logistics_sperm_i2_label`),
          detail: t(`${RC2}.iui_logistics_sperm_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.iui_logistics_sperm_i3_label`),
          detail: t(`${RC2}.iui_logistics_sperm_i3_detail`),
          status: "waiting",
        },
      ],
    },
    {
      title: t(`${RC2}.iui_logistics_after_title`),
      timeframe: t(`${RC2}.iui_logistics_after_timeframe`),
      icon: "start",
      body: t(`${RC2}.iui_logistics_after_body`),
      items: [
        {
          label: t(`${RC2}.iui_logistics_after_i1_label`),
          detail: t(`${RC2}.iui_logistics_after_i1_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.iui_logistics_after_i2_label`),
          detail: t(`${RC2}.iui_logistics_after_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.iui_logistics_after_i3_label`),
          detail: t(`${RC2}.iui_logistics_after_i3_detail`),
          status: "waiting",
        },
      ],
    },
  ];
}

const iuiCycleLogisticsCards: CycleLogisticsCard[] =
  buildIuiCycleLogisticsCards(tEn);

export function buildBetaWaitResultPlanCards(
  t: TranslationFunction,
  betaDateLabel?: string | null,
): CycleLogisticsCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      title: t(`${RC2}.beta_wait_result_clinic_title`),
      timeframe:
        betaDateLabel ?? t(`${RC2}.beta_wait_result_clinic_timeframe_pending`),
      icon: "start",
      body: t(`${RC2}.beta_wait_result_clinic_body`),
      items: [
        {
          label: t(`${RC2}.beta_wait_result_clinic_i1_label`),
          detail: t(`${RC2}.beta_wait_result_clinic_i1_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.beta_wait_result_clinic_i2_label`),
          detail: t(`${RC2}.beta_wait_result_clinic_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.beta_wait_result_clinic_i3_label`),
          detail: t(`${RC2}.beta_wait_result_clinic_i3_detail`),
          status: "next",
        },
      ],
    },
    {
      title: t(`${RC2}.beta_wait_result_positive_title`),
      timeframe: t(`${RC2}.beta_wait_result_positive_timeframe`),
      icon: "hormones",
      body: t(`${RC2}.beta_wait_result_positive_body`),
      items: [
        {
          label: t(`${RC2}.beta_wait_result_positive_i1_label`),
          detail: t(`${RC2}.beta_wait_result_positive_i1_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.beta_wait_result_positive_i2_label`),
          detail: t(`${RC2}.beta_wait_result_positive_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.beta_wait_result_positive_i3_label`),
          detail: t(`${RC2}.beta_wait_result_positive_i3_detail`),
          status: "waiting",
        },
      ],
    },
    // The "if negative" branch is intentionally NOT surfaced proactively during
    // the wait — leading with the loss path mid-wait is the wrong tone. That
    // content moves behind the result flow (RecordBetaResultModal) instead.
  ];
}

export const betaWaitResultPlanCards: CycleLogisticsCard[] =
  buildBetaWaitResultPlanCards(tEn);

function buildIuiWaitResultPlanCards(
  t: TranslationFunction,
): CycleLogisticsCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      title: t(`${RC2}.iui_wait_result_window_title`),
      timeframe: t(`${RC2}.iui_wait_result_window_timeframe`),
      icon: "hormones",
      body: t(`${RC2}.iui_wait_result_window_body`),
      items: [
        {
          label: t(`${RC2}.iui_wait_result_window_i1_label`),
          detail: t(`${RC2}.iui_wait_result_window_i1_detail`),
          status: "done",
        },
        {
          label: t(`${RC2}.iui_wait_result_window_i2_label`),
          detail: t(`${RC2}.iui_wait_result_window_i2_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.iui_wait_result_window_i3_label`),
          detail: t(`${RC2}.iui_wait_result_window_i3_detail`),
          status: "waiting",
        },
      ],
    },
    {
      title: t(`${RC2}.iui_wait_result_bleeding_title`),
      timeframe: t(`${RC2}.iui_wait_result_bleeding_timeframe`),
      icon: "panel",
      body: t(`${RC2}.iui_wait_result_bleeding_body`),
      items: [
        {
          label: t(`${RC2}.iui_wait_result_bleeding_i1_label`),
          detail: t(`${RC2}.iui_wait_result_bleeding_i1_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.iui_wait_result_bleeding_i2_label`),
          detail: t(`${RC2}.iui_wait_result_bleeding_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.iui_wait_result_bleeding_i3_label`),
          detail: t(`${RC2}.iui_wait_result_bleeding_i3_detail`),
          status: "waiting",
        },
      ],
    },
  ];
}

function buildOiWaitResultPlanCards(
  t: TranslationFunction,
): CycleLogisticsCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      title: t(`${RC2}.oi_wait_result_window_title`),
      timeframe: t(`${RC2}.oi_wait_result_window_timeframe`),
      icon: "hormones",
      body: t(`${RC2}.oi_wait_result_window_body`),
      items: [
        {
          label: t(`${RC2}.oi_wait_result_window_i1_label`),
          detail: t(`${RC2}.oi_wait_result_window_i1_detail`),
          status: "done",
        },
        {
          label: t(`${RC2}.oi_wait_result_window_i2_label`),
          detail: t(`${RC2}.oi_wait_result_window_i2_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.oi_wait_result_window_i3_label`),
          detail: t(`${RC2}.oi_wait_result_window_i3_detail`),
          status: "waiting",
        },
      ],
    },
    {
      title: t(`${RC2}.oi_wait_result_bleeding_title`),
      timeframe: t(`${RC2}.oi_wait_result_bleeding_timeframe`),
      icon: "panel",
      body: t(`${RC2}.oi_wait_result_bleeding_body`),
      items: [
        {
          label: t(`${RC2}.oi_wait_result_bleeding_i1_label`),
          detail: t(`${RC2}.oi_wait_result_bleeding_i1_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.oi_wait_result_bleeding_i2_label`),
          detail: t(`${RC2}.oi_wait_result_bleeding_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.oi_wait_result_bleeding_i3_label`),
          detail: t(`${RC2}.oi_wait_result_bleeding_i3_detail`),
          status: "waiting",
        },
      ],
    },
  ];
}

function buildOiWaitWhatToKnowCards(t: TranslationFunction): WhatToKnowCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      badge: t(`${RC2}.oi_wait_wtk_ovulation_badge`),
      title: t(`${RC2}.oi_wait_wtk_ovulation_title`),
      body: t(`${RC2}.oi_wait_wtk_ovulation_body`),
      bullets: [
        t(`${RC2}.oi_wait_wtk_ovulation_b1`),
        t(`${RC2}.oi_wait_wtk_ovulation_b2`),
        t(`${RC2}.oi_wait_wtk_ovulation_b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.oi_wait_wtk_meds_badge`),
      title: t(`${RC2}.oi_wait_wtk_meds_title`),
      body: t(`${RC2}.oi_wait_wtk_meds_body`),
      bullets: [
        t(`${RC2}.oi_wait_wtk_meds_b1`),
        t(`${RC2}.oi_wait_wtk_meds_b2`),
        t(`${RC2}.oi_wait_wtk_meds_b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.oi_wait_wtk_next_badge`),
      title: t(`${RC2}.oi_wait_wtk_next_title`),
      body: t(`${RC2}.oi_wait_wtk_next_body`),
      bullets: [
        t(`${RC2}.oi_wait_wtk_next_b1`),
        t(`${RC2}.oi_wait_wtk_next_b2`),
        t(`${RC2}.oi_wait_wtk_next_b3`),
      ],
      tone: "steady",
    },
  ];
}

export function buildBetaWaitWhatToKnowCards(
  t: TranslationFunction,
): WhatToKnowCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      badge: t(`${RC2}.beta_wait_wtk_symptoms_badge`),
      title: t(`${RC2}.beta_wait_wtk_symptoms_title`),
      body: t(`${RC2}.beta_wait_wtk_symptoms_body`),
      bullets: [
        t(`${RC2}.beta_wait_wtk_symptoms_b1`),
        t(`${RC2}.beta_wait_wtk_symptoms_b2`),
        t(`${RC2}.beta_wait_wtk_symptoms_b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.beta_wait_wtk_testing_badge`),
      title: t(`${RC2}.beta_wait_wtk_testing_title`),
      body: t(`${RC2}.beta_wait_wtk_testing_body`),
      bullets: [
        t(`${RC2}.beta_wait_wtk_testing_b1`),
        t(`${RC2}.beta_wait_wtk_testing_b2`),
        t(`${RC2}.beta_wait_wtk_testing_b3`),
      ],
      tone: "caution",
    },
    {
      badge: t(`${RC2}.beta_wait_wtk_hometest_badge`),
      title: t(`${RC2}.beta_wait_wtk_hometest_title`),
      body: t(`${RC2}.beta_wait_wtk_hometest_body`),
      bullets: [
        t(`${RC2}.beta_wait_wtk_hometest_b1`),
        t(`${RC2}.beta_wait_wtk_hometest_b2`),
        t(`${RC2}.beta_wait_wtk_hometest_b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.beta_wait_wtk_support_badge`),
      title: t(`${RC2}.beta_wait_wtk_support_title`),
      body: t(`${RC2}.beta_wait_wtk_support_body`),
      bullets: [
        t(`${RC2}.beta_wait_wtk_support_b1`),
        t(`${RC2}.beta_wait_wtk_support_b2`),
        t(`${RC2}.beta_wait_wtk_support_b3`),
      ],
      tone: "steady",
    },
  ];
}

export const betaWaitWhatToKnowCards: WhatToKnowCard[] =
  buildBetaWaitWhatToKnowCards(tEn);

function buildIuiWaitWhatToKnowCards(t: TranslationFunction): WhatToKnowCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      badge: t(`${RC2}.iui_wait_wtk_trigger_badge`),
      title: t(`${RC2}.iui_wait_wtk_trigger_title`),
      body: t(`${RC2}.iui_wait_wtk_trigger_body`),
      bullets: [
        t(`${RC2}.iui_wait_wtk_trigger_b1`),
        t(`${RC2}.iui_wait_wtk_trigger_b2`),
        t(`${RC2}.iui_wait_wtk_trigger_b3`),
      ],
      tone: "caution",
    },
    {
      badge: t(`${RC2}.iui_wait_wtk_meds_badge`),
      title: t(`${RC2}.iui_wait_wtk_meds_title`),
      body: t(`${RC2}.iui_wait_wtk_meds_body`),
      bullets: [
        t(`${RC2}.iui_wait_wtk_meds_b1`),
        t(`${RC2}.iui_wait_wtk_meds_b2`),
        t(`${RC2}.iui_wait_wtk_meds_b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.iui_wait_wtk_next_badge`),
      title: t(`${RC2}.iui_wait_wtk_next_title`),
      body: t(`${RC2}.iui_wait_wtk_next_body`),
      bullets: [
        t(`${RC2}.iui_wait_wtk_next_b1`),
        t(`${RC2}.iui_wait_wtk_next_b2`),
        t(`${RC2}.iui_wait_wtk_next_b3`),
      ],
      tone: "steady",
    },
  ];
}

export function todayRows(
  kind:
    | "stimulation"
    | "collection"
    | "wait"
    | "iui-wait"
    | "oi-wait"
    | "donor"
    | "iui-active",
  t: TranslationFunction = tEn,
): TodayRow[] {
  const RC = "homepage.review_concept.today_rows";
  const RC2 = "homepage.review_concept.inline";
  if (kind === "collection") {
    return [
      {
        id: "collection-appointment",
        time: "8:30 AM",
        title: t(`${RC}.collection.appt_title`),
        detail: t(`${RC}.collection.appt_detail`),
        icon: "sperm" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Sperm collection",
        appointmentResultType: "collection",
      },
      {
        id: "sample-processing",
        time: "11:00 AM",
        title: t(`${RC}.collection.proc_title`),
        detail: t(`${RC}.collection.proc_detail`),
        icon: "panel" as const,
        status: "lab",
        kind: "appointment",
        procedureName: "Semen analysis",
        appointmentResultType: "lab",
      },
      {
        id: "storage-confirmation",
        time: "4:00 PM",
        title: t(`${RC}.collection.storage_title`),
        detail: t(`${RC}.collection.storage_detail`),
        icon: "cryo" as const,
        status: "pending",
        kind: "appointment",
        procedureName: "Sperm Cryopreservation",
        appointmentResultType: "storage",
      },
    ];
  }

  if (kind === "wait") {
    return [
      {
        id: "progesterone-am",
        time: "8:00 AM",
        title: t(`${RC}.wait.prog_title`),
        detail: t(`${RC}.wait.prog_detail`),
        icon: "hormones" as const,
        status: "med",
        kind: "medication",
        medicationName: "Progesterone",
      },
      {
        id: "symptom-check",
        time: t(`${RC}.wait.any_time`),
        title: t(`${RC}.wait.symptom_title`),
        detail: t(`${RC}.wait.symptom_detail`),
        icon: "panel" as const,
        status: "support",
        kind: "task",
      },
      {
        id: "beta-blood-draw",
        time: "May 24",
        title: t(`${RC}.wait.beta_title`),
        detail: t(`${RC}.wait.beta_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Hormonal testing",
        appointmentResultType: "bloodwork",
      },
    ];
  }

  if (kind === "iui-wait") {
    return [
      {
        id: "luteal-support",
        time: "8:00 AM",
        title: t(`${RC2}.today_rows_iui_wait_luteal_title`),
        detail: t(`${RC2}.today_rows_iui_wait_luteal_detail`),
        icon: "hormones" as const,
        status: "med",
        kind: "medication",
        medicationName: "Progesterone",
      },
      {
        id: "avoid-early-testing",
        time: t(`${RC2}.today_rows_iui_wait_this_week`),
        title: t(`${RC2}.today_rows_iui_wait_avoid_title`),
        detail: t(`${RC2}.today_rows_iui_wait_avoid_detail`),
        icon: "panel" as const,
        status: "support",
        kind: "task",
      },
      {
        id: "iui-beta-blood-draw",
        time: "May 31",
        title: t(`${RC2}.today_rows_iui_wait_beta_title`),
        detail: t(`${RC2}.today_rows_iui_wait_beta_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Hormonal testing",
        appointmentResultType: "bloodwork",
      },
    ];
  }

  if (kind === "oi-wait") {
    return [
      {
        id: "oi-luteal-support",
        time: "8:00 AM",
        title: t(`${RC2}.today_rows_oi_wait_luteal_title`),
        detail: t(`${RC2}.today_rows_oi_wait_luteal_detail`),
        icon: "hormones" as const,
        status: "med",
        kind: "medication",
        medicationName: "Progesterone",
      },
      {
        id: "oi-avoid-early-testing",
        time: t(`${RC2}.today_rows_oi_wait_this_week`),
        title: t(`${RC2}.today_rows_oi_wait_avoid_title`),
        detail: t(`${RC2}.today_rows_oi_wait_avoid_detail`),
        icon: "panel" as const,
        status: "support",
        kind: "task",
      },
      {
        id: "oi-beta-blood-draw",
        time: "May 31",
        title: t(`${RC2}.today_rows_oi_wait_beta_title`),
        detail: t(`${RC2}.today_rows_oi_wait_beta_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Pregnancy blood test",
        appointmentResultType: "bloodwork",
      },
    ];
  }

  if (kind === "donor") {
    return [
      {
        id: "screening-labs",
        time: "10:00 AM",
        title: t(`${RC}.donor.screening_title`),
        detail: t(`${RC}.donor.screening_detail`),
        icon: "panel" as const,
        status: "today",
        kind: "appointment",
        procedureName: "Genetic carrier screening",
        appointmentResultType: "bloodwork",
      },
      {
        id: "coordinator-call",
        time: "2:30 PM",
        title: t(`${RC}.donor.coord_title`),
        detail: t(`${RC}.donor.coord_detail`),
        icon: "concierge" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Initial consultation",
        appointmentResultType: "call",
      },
    ];
  }

  if (kind === "iui-active") {
    return [
      {
        id: "ovidrel-trigger",
        time: "9:00 PM",
        title: t(`${RC2}.today_rows_iui_active_trigger_title`),
        detail: t(`${RC2}.today_rows_iui_active_trigger_detail`),
        icon: "hormones" as const,
        status: "tonight",
        kind: "medication",
        medicationName: "Ovidrel",
      },
      {
        id: "partner-sample",
        time: "May 20 8:00 AM",
        title: t(`${RC2}.today_rows_iui_active_partner_title`),
        detail: t(`${RC2}.today_rows_iui_active_partner_detail`),
        icon: "sperm" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Sperm collection",
        appointmentResultType: "collection",
      },
      {
        id: "iui-appointment",
        time: "May 20 9:30 AM",
        title: t(`${RC2}.today_rows_iui_active_iui_title`),
        detail: t(`${RC2}.today_rows_iui_active_iui_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "IUI",
        appointmentResultType: "collection",
      },
    ];
  }

  return [
    {
      id: "gonal-f-am",
      time: "7:00 AM",
      title: t(`${RC2}.today_rows_stim_gonal_title`),
      detail: t(`${RC}.stim.gonal_detail`),
      icon: "hormones" as const,
      status: "med",
      kind: "medication",
      medicationName: "Gonal-F",
      medicationDose: "225 IU",
      medicationFormat: t(`${RC}.stim.subc_pen`),
      medicationForm: "pen",
      medicationAvailabilityStatus: "on-hand",
    },
    {
      id: "monitoring-visit",
      time: "8:20 AM",
      title: t(`${RC}.stim.monitoring_title`),
      detail: t(`${RC}.stim.monitoring_detail`),
      icon: "afc" as const,
      status: "appointment",
      kind: "monitoring",
      procedureName: "Transvaginal ultrasound",
      appointmentResultType: "monitoring",
    },
    {
      id: "cetrotide-pm",
      time: "9:00 PM",
      title: "Cetrotide",
      detail: t(`${RC}.stim.cetrotide_detail`),
      icon: "hormones" as const,
      status: "med",
      kind: "medication",
      medicationName: "Cetrotide",
      medicationDose: "0.25 mg",
      medicationFormat: t(`${RC}.stim.subc_vial`),
      medicationForm: "vial",
      medicationAvailabilityStatus: "low-stock",
    },
  ];
}

function task(
  title: string,
  detail: string,
  owner: string,
  due: string,
  status: Task["status"] = "now",
): Task {
  return { title, detail, owner, due, status };
}

function timeline(
  label: string,
  detail: string,
  status: TimelineItem["status"],
): TimelineItem {
  return { label, detail, status };
}

function metrics(
  title: string,
  icon: OrganicMiniIconKind,
  values: Metric[],
  subtitle?: string,
): Widget {
  return { type: "metrics", title, subtitle, icon, metrics: values };
}

function today(title: string, rows: TodayRow[], subtitle?: string): Widget {
  return { type: "today", title, subtitle, rows };
}

function actionRows(rows: OutcomeActionRow[]): Widget {
  return { type: "action-rows", rows };
}

function testResults(
  title: string,
  subtitle?: string,
  variant?: TestResultsVariant,
): Widget {
  return { type: "test-results", title, subtitle, icon: "panel", variant };
}

function dataStatus(
  title: string,
  detail: string,
  icon: OrganicMiniIconKind,
  action?: PlanAction,
): Widget {
  return { type: "data-status", title, detail, icon, action };
}

function semenAnalysis(
  title: string,
  subtitle?: string,
  results?: SpermAnalysisResults | null,
): Widget {
  return { type: "semen-analysis", title, subtitle, results };
}

function cryoVault(): Widget {
  return { type: "cryo-vault" };
}

function futureUseEstimate(primaryUnit?: "eggs" | "embryos"): Widget {
  return { type: "future-use-estimate", primaryUnit };
}

function spermCryosurvivalEstimate(): Widget {
  return { type: "sperm-cryosurvival-estimate" };
}

function tasks(
  title: string,
  icon: OrganicMiniIconKind,
  values: Task[],
  subtitle?: string,
  action?: PlanAction,
): Widget {
  return { type: "tasks", title, subtitle, icon, tasks: values, action };
}

function taskToTodayRow(taskItem: Task, index: number): TodayRow {
  const ownerPrefix =
    taskItem.owner && taskItem.owner !== "You" ? `${taskItem.owner} - ` : "";
  return {
    id: `task-${taskItem.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`,
    time: taskItem.due,
    title: taskItem.title,
    detail: `${ownerPrefix}${taskItem.detail}`,
    icon: "panel",
    status:
      taskItem.status === "done"
        ? "done"
        : taskItem.status === "locked"
          ? "locked"
          : taskItem.status === "waiting"
            ? "waiting"
            : "now",
    kind: "task",
  };
}

// A funnel with no card title/icon/action; the section title is the heading.
function bareFunnel(stages: FunnelStage[], subtitle?: string): Widget {
  return {
    type: "funnel",
    title: "",
    icon: "eggs",
    stages,
    subtitle,
    bare: true,
  };
}

function outcomeEstimateFunnel(
  title: string,
  icon: OrganicMiniIconKind,
  variant: "egg-freezing" | "embryo-freezing",
  subtitle?: string,
): Widget {
  return { type: "outcome-estimate-funnel", title, subtitle, icon, variant };
}

function labCarousel(
  title: string,
  icon: OrganicMiniIconKind,
  cards: LabUpdateCard[],
  subtitle?: string,
): Widget {
  return { type: "lab-carousel", title, subtitle, icon, cards };
}

function cycleLogisticsCarousel(
  title: string,
  icon: OrganicMiniIconKind,
  cards: CycleLogisticsCard[],
  subtitle?: string,
): Widget {
  return { type: "cycle-logistics", title, subtitle, icon, cards };
}

function whatToKnow(
  title: string,
  icon: OrganicMiniIconKind,
  cards: WhatToKnowCard[],
  subtitle?: string,
): Widget {
  return { type: "what-to-know", title, subtitle, icon, cards };
}

function timelineWidget(
  title: string,
  icon: OrganicMiniIconKind,
  items: TimelineItem[],
  subtitle?: string,
): Widget {
  return { type: "timeline", title, subtitle, icon, items };
}

function decision(
  title: string,
  icon: OrganicMiniIconKind,
  options: DecisionOption[],
  subtitle?: string,
  action?: PlanAction,
): Widget {
  return { type: "decision", title, subtitle, icon, options, action };
}

function monitoringChart(
  t: TranslationFunction,
  title: string,
  subtitle?: string,
): Widget {
  const actions = buildActions(t);
  return {
    type: "monitoring-chart",
    title,
    subtitle,
    icon: "afc",
    primaryAction: actions.logMonitoring,
  };
}

function section(
  title: string,
  subtitle: string,
  widgets: Widget[],
  action?: PlanAction,
): ConceptSection {
  return { title, subtitle, widgets, action };
}

// All `active*` section builders below take a `TranslationFunction` so the
// inline section/widget strings are localized at render time. The top-level
// `useConcepts()` hook composes them with the per-variant inline keys.
const RC = "homepage.review_concept.inline" as const;

function buildActiveMonitoringSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.monitoring_progress_title`),
    t(`${RC}.monitoring_progress_subtitle`),
    [monitoringChart(t, t(`${RC}.monitoring_progress_title`))],
  );
}

function buildActiveStimulationSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_today_title`),
    t(`${RC}.section_today_subtitle`),
    [today(t(`${RC}.today_timeline_title`), todayRows("stimulation", t))],
    { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
  );
}

function buildActiveStimulationTestingSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_testing_title`),
    t(`${RC}.section_testing_subtitle`),
    [
      testResults(
        t(`${RC}.test_results_title`),
        t(`${RC}.test_results_subtitle`),
      ),
    ],
    { label: t(`${RC}.all_tests_action`), screen: "AtHomeFertilityTesting" },
  );
}

/**
 * The monitoring section, worded for a cycle with no retrieval at the end.
 *
 * The stimulation version's subtitle ends on "and retrieval timing", which is
 * the one thing an ovulation induction cycle is not building towards. What it
 * is timing is the trigger, or the intercourse window.
 */
function buildActiveOvulationInductionMonitoringSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.monitoring_progress_title`),
    t(`${RC}.follow_growth_and_trigger_timing`),
    [monitoringChart(t, t(`${RC}.monitoring_progress_title`))],
  );
}

function buildActiveStimulationMoreSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_good_to_know_title`),
    t(`${RC}.section_good_to_know_subtitle`),
    [
      whatToKnow(
        t(`${RC}.what_to_know_title`),
        "panel",
        buildStimulationWhatToKnowCards(t),
        t(`${RC}.what_to_know_subtitle`),
      ),
      outcomeEstimateFunnel(
        t(`${RC}.cycle_may_freeze_title`),
        "eggs",
        "egg-freezing",
        t(`${RC}.cycle_may_freeze_subtitle`),
      ),
    ],
  );
}

function buildActiveEggFreezingSections(
  t: TranslationFunction,
): ConceptSection[] {
  return [
    buildActiveStimulationSection(t),
    buildActiveStimulationTestingSection(t),
    buildActiveMonitoringSection(t),
    buildActiveStimulationMoreSection(t),
  ];
}

function buildActiveEmbryoStimulationSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_today_title`),
    t(`${RC}.section_today_subtitle`),
    [today(t(`${RC}.today_timeline_title`), todayRows("stimulation", t))],
    { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
  );
}

function buildActiveEmbryoTestingSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_testing_title`),
    t(`${RC}.section_testing_subtitle`),
    [
      testResults(
        t(`${RC}.test_results_title`),
        t(`${RC}.test_results_subtitle`),
      ),
    ],
    { label: t(`${RC}.all_tests_action`), screen: "AtHomeFertilityTesting" },
  );
}

function buildActiveEmbryoMoreSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_good_to_know_title`),
    t(`${RC}.section_good_to_know_subtitle`),
    [
      whatToKnow(
        t(`${RC}.what_to_know_title`),
        "panel",
        buildStimulationWhatToKnowCards(t),
        t(`${RC}.what_to_know_subtitle`),
      ),
      outcomeEstimateFunnel(
        t(`${RC}.cycle_may_become_title`),
        "genetics",
        "embryo-freezing",
        t(`${RC}.cycle_may_become_subtitle`),
      ),
    ],
  );
}

function buildActiveEmbryoFreezingSections(
  t: TranslationFunction,
): ConceptSection[] {
  return [
    buildActiveEmbryoStimulationSection(t),
    buildActiveEmbryoTestingSection(t),
    buildActiveMonitoringSection(t),
    buildActiveEmbryoMoreSection(t),
  ];
}

function buildPostRetrievalWhatToKnowCards(
  t: TranslationFunction,
): WhatToKnowCard[] {
  const RC2 = `${RC}.post_retrieval`;
  return [
    {
      badge: t(`${RC2}.recovery_badge`),
      title: t(`${RC2}.recovery_title`),
      body: t(`${RC2}.recovery_body`),
      bullets: [
        t(`${RC2}.recovery_b1`),
        t(`${RC2}.recovery_b2`),
        t(`${RC2}.recovery_b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.lab_badge`),
      title: t(`${RC2}.lab_title`),
      body: t(`${RC2}.lab_body`),
      bullets: [t(`${RC2}.lab_b1`), t(`${RC2}.lab_b2`), t(`${RC2}.lab_b3`)],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.safety_badge`),
      title: t(`${RC2}.safety_title`),
      body: t(`${RC2}.safety_body`),
      bullets: [
        t(`${RC2}.safety_b1`),
        t(`${RC2}.safety_b2`),
        t(`${RC2}.safety_b3`),
      ],
      tone: "caution",
    },
  ];
}

function buildPostRetrievalSections(t: TranslationFunction): ConceptSection[] {
  return [
    section(
      t(`${RC}.post_retrieval_today_section_title`),
      t(`${RC}.post_retrieval_today_section_subtitle`),
      [today(t(`${RC}.today_timeline_title`), [])],
      { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
    ),
    section(
      t(`${RC}.post_retrieval_guidance_section_title`),
      t(`${RC}.post_retrieval_guidance_section_subtitle`),
      [
        whatToKnow(
          t(`${RC}.post_retrieval_guidance_title`),
          "panel",
          buildPostRetrievalWhatToKnowCards(t),
          t(`${RC}.post_retrieval_guidance_subtitle`),
        ),
      ],
    ),
  ];
}

function buildActiveIuiTodaySection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_today_title`),
    t(`${RC}.section_today_iui_subtitle`),
    [today(t(`${RC}.today_timeline_title`), todayRows("iui-active", t))],
    { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
  );
}

function buildActiveIuiTestingSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_testing_title`),
    t(`${RC}.section_testing_iui_subtitle`),
    [
      testResults(
        t(`${RC}.test_results_title`),
        t(`${RC}.test_results_iui_subtitle`),
        "iui-active",
      ),
    ],
    { label: t(`${RC}.all_tests_action`), screen: "AtHomeFertilityTesting" },
  );
}

function buildActiveIuiMoreSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_good_to_know_title`),
    t(`${RC}.section_good_to_know_iui_subtitle`),
    [
      whatToKnow(
        t(`${RC}.what_to_know_title`),
        "panel",
        buildIuiActiveWhatToKnowCards(t),
        t(`${RC}.what_to_know_iui_subtitle`),
      ),
    ],
  );
}

function buildActiveIuiSections(t: TranslationFunction): ConceptSection[] {
  return [
    buildActiveIuiTodaySection(t),
    buildActiveIuiTestingSection(t),
    buildActiveMonitoringSection(t),
    buildActiveIuiMoreSection(t),
  ];
}

// Static English fallback for non-React consumers (e.g. fixture data in
// CareReviewConceptScreen). Localized rendering should go through
// `useConcepts()` instead.
export const activeEggFreezingSections: ConceptSection[] =
  buildActiveEggFreezingSections(tEn);

function getSemenAnalysisValue(result: {
  patientValue: string | null;
  doctorValue: string | null;
  value: string | null;
  unit: string | null;
}): string | undefined {
  const rawValue = [result.doctorValue, result.patientValue, result.value].find(
    (value) => value != null && value.trim().length > 0,
  );
  if (!rawValue) return undefined;

  const value = rawValue.trim();
  const unit = result.unit?.trim();
  if (!unit || value.toLowerCase().endsWith(unit.toLowerCase())) {
    return value;
  }
  return `${value} ${unit}`;
}

function getSemenAnalysisField(
  name: string | null,
  localizedLabels?: Partial<
    Record<"volume" | "concentration" | "motility" | "morphology", string>
  >,
): keyof SpermAnalysisResults | undefined {
  const normalize = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const normalized = normalize(name ?? "");
  const matches = (...candidates: (string | undefined)[]) =>
    candidates.some((candidate) => {
      const normalizedCandidate = candidate ? normalize(candidate) : "";
      return (
        normalizedCandidate.length > 0 &&
        (normalized === normalizedCandidate ||
          normalized.includes(normalizedCandidate))
      );
    });

  if (matches("volume", localizedLabels?.volume)) return "volume";
  if (
    matches("concentration", localizedLabels?.concentration) ||
    normalized.includes("count")
  ) {
    return "concentration";
  }
  if (matches("motility", localizedLabels?.motility)) {
    return normalized.includes("status")
      ? "motilityStatus"
      : "progressiveMotility";
  }
  if (matches("morphology", localizedLabels?.morphology)) {
    return "normalMorphology";
  }
  if (normalized.includes("dna") && normalized.includes("fragment")) {
    return "dnaFragmentation";
  }
  if (normalized.includes("methylation")) return "methylation";
  if (normalized.includes("oxidative") && normalized.includes("stress")) {
    return "oxidativeStress";
  }
  if (normalized.includes("vitality")) return "vitality";
  if (normalized.includes("aneuploidy")) return "aneuploidy";
  if (normalized.includes("acrosome")) return "acrosome";
  if (normalized.includes("head")) return "head";
  if (normalized.includes("neck")) return "neck";
  if (normalized.includes("tail")) return "tail";
  return undefined;
}

/**
 * Semen analysis values are stored as one test-result row per parameter.
 * Keep the homepage card backed by the most recently dated analysis rather
 * than the static concept fixture values used by the rest of this screen.
 */
function useSemenAnalysisResults(): SpermAnalysisResults | null {
  const t = useTranslations();
  const { data: testResults } = useTestResults();
  const localizedLabels = useMemo(
    () => ({
      volume: t("documents.semen_result.volume"),
      concentration: t("documents.semen_result.concentration"),
      motility: t("documents.semen_result.motility"),
      morphology: t("documents.semen_result.morphology"),
    }),
    [t],
  );

  return useMemo(() => {
    if (!testResults) return null;

    const semenResults = testResults.filter(
      (result) => result.type === "semenFluidAnalysis",
    );
    if (semenResults.length === 0) return null;

    const getDate = (result: (typeof semenResults)[number]) =>
      result.date?.trim() || null;
    const sorted = [...semenResults].sort((a, b) => {
      const aDate = getDate(a) ?? a.createdAt ?? "";
      const bDate = getDate(b) ?? b.createdAt ?? "";
      return bDate.localeCompare(aDate);
    });

    // Add-document saves all parameters from one report with the same date.
    // For records without a report date, use one undated group so a partially
    // populated analysis can still be shown instead of dropping its fields.
    const latestDate = getDate(sorted[0]!);
    const latestGroup = sorted.filter(
      (result) => getDate(result) === latestDate,
    );
    const mapped: SpermAnalysisResults = {};

    for (const result of latestGroup) {
      const field = getSemenAnalysisField(result.name, localizedLabels);
      const displayValue = getSemenAnalysisValue(result);
      if (field && displayValue) mapped[field] = displayValue;
    }

    return Object.keys(mapped).length > 0 ? mapped : null;
  }, [localizedLabels, testResults]);
}

function buildPostProcedureOutcomeConcept(
  t: TranslationFunction,
  contentKey: PostProcedureOutcomeContentKey,
  resultStatus:
    "positive" | "negative" | "uncertain" | "adverse" | "unavailable",
): Concept {
  const ns = `homepage.review_concept.post_procedure_outcome.${contentKey}.${resultStatus}`;
  const shared = "homepage.review_concept.post_procedure_outcome.shared";

  if (
    resultStatus === "unavailable" ||
    resultStatus === "uncertain" ||
    resultStatus === "adverse"
  ) {
    return {
      eyebrow: t(`${ns}.eyebrow`),
      title: t(`${ns}.title`),
      summary: t(`${ns}.summary`),
      icon: "process",
      sections: [
        section(t(`${ns}.section_title`), t(`${ns}.section_subtitle`), [
          today(t(`${ns}.today_section_title`), []),
          actionRows([
            {
              title: t(`${shared}.stage_action_review_records`),
              detail: t(`${shared}.stage_action_review_records_detail`),
              icon: "medical-records",
              action: {
                label: t(`${shared}.stage_action_review_records`),
                signal: "open-recorded-cycle",
              },
            },
            {
              title: t(`${shared}.stage_action_schedule_appointment`),
              detail: t(`${shared}.stage_action_schedule_appointment_detail`),
              icon: "clinics",
              action: {
                label: t(`${shared}.stage_action_schedule_appointment`),
                screen: "CareOverview",
                params: { focus: "appointment" },
              },
            },
            {
              title: t(`${shared}.stage_action_save_question`),
              detail: t(`${shared}.stage_action_save_question_detail`),
              icon: "concierge",
              action: {
                label: t(`${shared}.stage_action_save_question`),
                signal: "open-ai-chat",
                params: {
                  initialPrompt: t(
                    `${shared}.stage_action_save_question_prompt`,
                  ),
                },
              },
            },
          ]),
        ]),
      ],
    };
  }

  return {
    eyebrow: t(`${ns}.eyebrow`),
    // The negative heading is shared across modalities — see
    // `take_a_breath_after_this_cycle`. Reading `${ns}.title` here survived the
    // move and now resolves to nothing: `t` returns the path itself for a missing
    // key, so this would render a raw i18n path the day something displays it.
    title:
      resultStatus === "negative"
        ? t(`${shared}.take_a_breath_after_this_cycle`)
        : t(`${ns}.title`),
    summary: t(`${ns}.summary`),
    icon: resultStatus === "positive" ? "start" : "process",
    sections: [
      section(t(`${ns}.section_title`), t(`${ns}.section_subtitle`), [
        // The outcome page must not invent tasks. Real appointments and
        // medications are supplied by PlanHomeScreen; when there is no
        // scheduled follow-up, Today renders its canonical empty state.
        today(t(`${ns}.today_section_title`), []),
      ]),
    ],
  };
}

function buildConcepts(
  t: TranslationFunction,
  postCycleFunnels: PostCycleFunnels,
  semenAnalysisResults?: SpermAnalysisResults | null,
  cycleStatusReviewKind?: CycleStatusReviewKind | null,
  betaDateLabel?: string | null,
): Partial<Record<HomepageReviewConceptVariant, Concept>> {
  const cycleStatusReviewCopy = cycleStatusReviewKind
    ? `${RC}.cycle_status_review_${cycleStatusReviewKind}`
    : `${RC}.cycle_status_review`;
  const cycleStatusReviewHeroCopy = cycleStatusReviewKind
    ? cycleStatusReviewCopy
    : `${RC}.cycle_status_review`;
  // Named because the negative route appends a funnel section to it below, and
  // building the concept twice to do that would let the two copies drift.
  const ivfNegativeConcept = buildPostProcedureOutcomeConcept(
    t,
    "ivf",
    "negative",
  );

  return {
    "egg-freezing-active": {
      eyebrow: t(`${RC}.egg_freezing_active_eyebrow`),
      title: t(`${RC}.egg_freezing_active_title`),
      summary: t(`${RC}.egg_freezing_active_summary`),
      icon: "eggs",
      sections: buildActiveEggFreezingSections(t),
    },
    "egg-freezing-postcycle": {
      eyebrow: t(`${RC}.egg_postcycle_eyebrow`),
      title: t(`${RC}.egg_postcycle_title`),
      summary: t(`${RC}.egg_postcycle_summary`),
      icon: "cryo",
      sections: [
        section(
          t(`${RC}.egg_postcycle_section_title`),
          t(`${RC}.egg_postcycle_section_subtitle`),
          [
            // Post-cycle care can still have near-term medications or
            // appointments. WidgetView replaces these rows with the real
            // patient agenda and shows the canonical empty state when none
            // are scheduled.
            today(t(`${RC}.today_schedule_title`), []),
            // Observed results only (#1817): omitted when no retrieval
            // metrics were recorded — a missing record is not a zero.
            ...(postCycleFunnels.egg ? [bareFunnel(postCycleFunnels.egg)] : []),
            cryoVault(),
          ],
        ),
      ],
    },
    "embryo-freezing-active": {
      eyebrow: t(`${RC}.embryo_freezing_active_eyebrow`),
      title: t(`${RC}.embryo_freezing_active_title`),
      summary: t(`${RC}.embryo_freezing_active_summary`),
      icon: "genetics",
      sections: buildActiveEmbryoFreezingSections(t),
    },
    "embryo-freezing-postcycle": {
      eyebrow: t(`${RC}.embryo_postcycle_eyebrow`),
      title: t(`${RC}.embryo_postcycle_title`),
      summary: t(`${RC}.embryo_postcycle_summary`),
      icon: "genetics",
      sections: [
        section(
          t(`${RC}.embryo_postcycle_section_title`),
          t(`${RC}.embryo_postcycle_section_subtitle`),
          [
            today(t(`${RC}.today_schedule_title`), []),
            testResults(
              t(`${RC}.embryo_postcycle_testing_title`),
              t(`${RC}.embryo_postcycle_testing_subtitle`),
            ),
            // Observed results only (#1817): omitted when no retrieval
            // metrics were recorded — a missing record is not a zero.
            ...(postCycleFunnels.embryo
              ? [bareFunnel(postCycleFunnels.embryo)]
              : []),
            cryoVault(),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
      ],
    },
    "sperm-freezing-active": {
      eyebrow: t(`${RC}.sperm_active_eyebrow`),
      title: t(`${RC}.sperm_active_title`),
      summary: t(`${RC}.sperm_active_summary`),
      icon: "sperm",
      sections: [
        section(
          t(`${RC}.sperm_active_section_title`),
          t(`${RC}.sperm_active_section_subtitle`),
          [
            today(t(`${RC}.today_schedule_title`), todayRows("collection", t)),
            dataStatus(
              t(`${RC}.sperm_active_tasks_title`),
              t(`${RC}.sperm_active_data_not_recorded_detail`),
              "sperm",
              buildActions(t).careTeam,
            ),
            semenAnalysis(t(`${RC}.sperm_active_semen_title`)),
          ],
        ),
      ],
    },
    "sperm-freezing-wait": {
      eyebrow: t(`${RC}.sperm_freezing_wait_eyebrow`),
      title: t(`${RC}.sperm_freezing_wait_title`),
      summary: t(`${RC}.sperm_freezing_wait_summary`),
      icon: "sperm",
      sections: [
        section(
          t(`${RC}.sperm_wait_section_title`),
          t(`${RC}.sperm_wait_section_subtitle`),
          [
            dataStatus(
              t(`${RC}.sperm_wait_lab_status_title`),
              t(`${RC}.sperm_wait_data_not_recorded_detail`),
              "panel",
              buildActions(t).documents,
            ),
          ],
        ),
      ],
    },
    "sperm-freezing-postcycle": {
      eyebrow: t(`${RC}.sperm_freezing_postcycle_eyebrow`),
      title: t(`${RC}.sperm_freezing_postcycle_title`),
      summary: t(`${RC}.sperm_freezing_postcycle_summary`),
      icon: "cryo",
      sections: [
        section(
          t(`${RC}.sperm_postcycle_section_title`),
          t(`${RC}.sperm_postcycle_section_subtitle`),
          [
            today(t(`${RC}.today_schedule_title`), []),
            semenAnalysis(
              t(`${RC}.sperm_postcycle_semen_title`),
              undefined,
              semenAnalysisResults,
            ),
            cryoVault(),
          ],
        ),
      ],
    },
    "ivf-active": {
      eyebrow: t(`${RC}.ivf_active_eyebrow`),
      title: t(`${RC}.ivf_active_title`),
      summary: t(`${RC}.ivf_active_summary`),
      icon: "genetics",
      sections: [
        buildActiveEmbryoStimulationSection(t),
        buildActiveEmbryoTestingSection(t),
        buildActiveMonitoringSection(t),
        buildActiveEmbryoMoreSection(t),
      ],
    },
    "post-retrieval": {
      eyebrow: t(`${RC}.post_retrieval_eyebrow`),
      title: t(`${RC}.post_retrieval_title`),
      summary: t(`${RC}.post_retrieval_summary`),
      icon: "genetics",
      sections: buildPostRetrievalSections(t),
    },
    "fet-active": {
      eyebrow: t(`${RC}.fet_active_plan_eyebrow`),
      title: t(`${RC}.fet_active_plan_heading`),
      summary: t(`${RC}.fet_active_plan_summary`),
      icon: "process",
      sections: [
        section(
          t(`${RC}.fet_active_plan_section_heading`),
          t(`${RC}.fet_active_plan_section_supporting`),
          [
            dataStatus(
              t(`${RC}.fet_active_records_heading`),
              t(`${RC}.fet_active_records_detail`),
              "process",
              buildActions(t).careTeam,
            ),
            dataStatus(
              t(`${RC}.fet_active_transfer_heading`),
              t(`${RC}.fet_active_transfer_detail`),
              "cryo",
              buildActions(t).calendar,
            ),
          ],
        ),
      ],
    },
    "cycle-status-review": {
      eyebrow: t(`${RC}.cycle_status_review_eyebrow`),
      title: t(`${cycleStatusReviewHeroCopy}_title`),
      summary: t(`${cycleStatusReviewHeroCopy}_summary`),
      icon: "process",
      sections: [
        section(
          t(`${RC}.cycle_status_review_section_title`),
          t(`${RC}.cycle_status_review_section_subtitle`),
          [
            today(t(`${RC}.cycle_status_review_today_title`), []),
            actionRows([
              {
                title: t(`${RC}.cycle_status_review_records_title`),
                detail: t(`${RC}.cycle_status_review_records_detail`),
                icon: "medical-records",
                action: {
                  label: t(`${RC}.cycle_status_review_records_action`),
                  screen: "CycleDetailScreen",
                },
              },
              {
                title: t(`${RC}.cycle_status_review_clinic_title`),
                detail: t(`${RC}.cycle_status_review_clinic_detail`),
                icon: "clinics",
                action: {
                  label: t(`${RC}.cycle_status_review_clinic_action`),
                  screen: "CareOverview",
                  params: { focus: "appointment" },
                },
              },
              {
                title: t(`${RC}.cycle_status_review_question_title`),
                detail: t(`${RC}.cycle_status_review_question_detail`),
                icon: "concierge",
                action: {
                  label: t(`${RC}.cycle_status_review_question_action`),
                  signal: "open-ai-chat",
                  params: {
                    initialPrompt: t(
                      `${cycleStatusReviewHeroCopy}_question_prompt`,
                    ),
                  },
                },
              },
            ]),
          ],
        ),
      ],
    },
    "beta-wait": {
      eyebrow: t(`${RC}.beta_wait_eyebrow`),
      title: t(`${RC}.beta_wait_title`),
      summary: t(`${RC}.beta_wait_summary`),
      icon: "start",
      sections: [
        section(
          t(`${RC}.beta_wait_section_today_title`),
          t(`${RC}.beta_wait_section_today_subtitle`),
          [today(t(`${RC}.beta_wait_today_title`), todayRows("wait", t))],
          buildActions(t).calendar,
        ),
        section(
          t(`${RC}.beta_wait_section_testing_title`),
          t(`${RC}.beta_wait_section_testing_subtitle`),
          [
            cycleLogisticsCarousel(
              t(`${RC}.beta_wait_result_plan_title`),
              "start",
              buildBetaWaitResultPlanCards(t, betaDateLabel),
              t(`${RC}.beta_wait_result_plan_subtitle`),
            ),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
        section(
          t(`${RC}.beta_wait_section_more_title`),
          t(`${RC}.beta_wait_section_more_subtitle`),
          [
            whatToKnow(
              t(`${RC}.beta_wait_what_to_know_title`),
              "panel",
              buildBetaWaitWhatToKnowCards(t),
              t(`${RC}.beta_wait_what_to_know_subtitle`),
            ),
          ],
        ),
      ],
    },
    "fet-positive": buildPostProcedureOutcomeConcept(t, "fet", "positive"),
    "fet-negative": buildPostProcedureOutcomeConcept(t, "fet", "negative"),
    "fet-uncertain": buildPostProcedureOutcomeConcept(t, "fet", "uncertain"),
    "fet-adverse": buildPostProcedureOutcomeConcept(t, "fet", "adverse"),
    "fet-unavailable": buildPostProcedureOutcomeConcept(
      t,
      "fet",
      "unavailable",
    ),
    "iui-positive": buildPostProcedureOutcomeConcept(t, "iui", "positive"),
    "iui-negative": buildPostProcedureOutcomeConcept(t, "iui", "negative"),
    "iui-uncertain": buildPostProcedureOutcomeConcept(t, "iui", "uncertain"),
    "iui-adverse": buildPostProcedureOutcomeConcept(t, "iui", "adverse"),
    "iui-unavailable": buildPostProcedureOutcomeConcept(
      t,
      "iui",
      "unavailable",
    ),
    "oi-positive": buildPostProcedureOutcomeConcept(t, "oi", "positive"),
    "oi-negative": buildPostProcedureOutcomeConcept(t, "oi", "negative"),
    "oi-uncertain": buildPostProcedureOutcomeConcept(t, "oi", "uncertain"),
    "oi-adverse": buildPostProcedureOutcomeConcept(t, "oi", "adverse"),
    "oi-unavailable": buildPostProcedureOutcomeConcept(t, "oi", "unavailable"),
    "ivf-uncertain": buildPostProcedureOutcomeConcept(t, "ivf", "uncertain"),
    "ivf-adverse": buildPostProcedureOutcomeConcept(t, "ivf", "adverse"),
    "ivf-unavailable": buildPostProcedureOutcomeConcept(
      t,
      "ivf",
      "unavailable",
    ),
    "ivf-positive": buildPostProcedureOutcomeConcept(t, "ivf", "positive"),
    "ivf-negative": postCycleFunnels.ivf
      ? {
          ...ivfNegativeConcept,
          sections: [
            ...ivfNegativeConcept.sections,
            section(t(`${RC}.ivf_negative_funnel_title`), "", [
              bareFunnel(postCycleFunnels.ivf),
            ]),
          ],
        }
      : ivfNegativeConcept,
    "iui-active": {
      eyebrow: t(`${RC}.iui_active_eyebrow`),
      title: t(`${RC}.iui_active_title`),
      summary: t(`${RC}.iui_active_summary`),
      icon: "hormones",
      sections: buildActiveIuiSections(t),
    },
    "iui-wait": {
      eyebrow: t(`${RC}.iui_wait_eyebrow`),
      title: t(`${RC}.iui_wait_title`),
      summary: t(`${RC}.iui_wait_summary`),
      icon: "start",
      sections: [
        section(
          t(`${RC}.iui_wait_section_today_title`),
          t(`${RC}.iui_wait_section_today_subtitle`),
          [today(t(`${RC}.iui_wait_today_title`), todayRows("iui-wait", t))],
          buildActions(t).calendar,
        ),
        section(
          t(`${RC}.iui_wait_section_testing_title`),
          t(`${RC}.iui_wait_section_testing_subtitle`),
          [
            cycleLogisticsCarousel(
              t(`${RC}.iui_wait_result_plan_title`),
              "hormones",
              buildIuiWaitResultPlanCards(t),
              t(`${RC}.iui_wait_result_plan_subtitle`),
            ),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
        section(
          t(`${RC}.iui_wait_section_more_title`),
          t(`${RC}.iui_wait_section_more_subtitle`),
          [
            whatToKnow(
              t(`${RC}.iui_wait_what_to_know_title`),
              "panel",
              buildIuiWaitWhatToKnowCards(t),
              t(`${RC}.iui_wait_what_to_know_subtitle`),
            ),
          ],
        ),
      ],
    },
    /**
     * A stimulated cycle with no retrieval at the end of it.
     *
     * Assembled from the generic stimulation sections rather than the IVF or
     * IUI ones: the days are the same — medications, scans, a response to
     * follow — but IVF's sections speak of a retrieval and a lab timeline, and
     * IUI's of a partner sample and an insemination. Neither happens here.
     *
     * The "good to know" section is deliberately absent. The generic
     * stimulation one carries an egg-freezing outcome projection — "what this
     * cycle may freeze", eggs retrieved through to live births — and three of
     * its cards speak of IVF outcomes, a better retrieval, and the weeks near
     * retrieval. An ovulation induction cycle retrieves and freezes nothing,
     * and the hero menu already agrees: `canShowOutcomeFunnel` tests the
     * variant name and drops the outlook item for this one. A section written
     * for these days is content work, not a builder to borrow.
     */
    "oi-active": {
      eyebrow: t(`${RC}.oi_active_eyebrow`),
      title: t(`${RC}.oi_active_title`),
      summary: t(`${RC}.oi_active_summary`),
      icon: "hormones",
      sections: [
        buildActiveStimulationSection(t),
        buildActiveStimulationTestingSection(t),
        buildActiveOvulationInductionMonitoringSection(t),
      ],
    },
    "oi-wait": {
      eyebrow: t(`${RC}.oi_wait_eyebrow`),
      title: t(`${RC}.oi_wait_title`),
      summary: t(`${RC}.oi_wait_summary`),
      icon: "start",
      sections: [
        section(
          t(`${RC}.oi_wait_section_today_title`),
          t(`${RC}.oi_wait_section_today_subtitle`),
          [today(t(`${RC}.oi_wait_today_title`), todayRows("oi-wait", t))],
          buildActions(t).calendar,
        ),
        section(
          t(`${RC}.oi_wait_section_testing_title`),
          t(`${RC}.oi_wait_section_testing_subtitle`),
          [
            cycleLogisticsCarousel(
              t(`${RC}.oi_wait_result_plan_title`),
              "hormones",
              buildOiWaitResultPlanCards(t),
              t(`${RC}.oi_wait_result_plan_subtitle`),
            ),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
        section(
          t(`${RC}.oi_wait_section_more_title`),
          t(`${RC}.oi_wait_section_more_subtitle`),
          [
            whatToKnow(
              t(`${RC}.oi_wait_what_to_know_title`),
              "panel",
              buildOiWaitWhatToKnowCards(t),
              t(`${RC}.oi_wait_what_to_know_subtitle`),
            ),
          ],
        ),
      ],
    },
    "intended-parent": {
      eyebrow: t(`${RC}.intended_parent_eyebrow`),
      title: t(`${RC}.intended_parent_title`),
      summary: t(`${RC}.intended_parent_summary`),
      icon: "partner",
      heroMetrics: [
        {
          label: t(`${RC}.intended_parent_hm1_label`),
          value: t(`${RC}.intended_parent_hm1_value`),
          note: t(`${RC}.intended_parent_hm1_note`),
        },
        {
          label: t(`${RC}.intended_parent_hm2_label`),
          value: t(`${RC}.intended_parent_hm2_value`),
          note: t(`${RC}.intended_parent_hm2_note`),
        },
        {
          label: t(`${RC}.intended_parent_hm3_label`),
          value: t(`${RC}.intended_parent_hm3_value`),
          note: t(`${RC}.intended_parent_hm3_note`),
        },
      ],
      sections: [
        section(
          t(`${RC}.intended_parent_section_title`),
          t(`${RC}.intended_parent_section_subtitle`),
          [
            tasks(
              t(`${RC}.intended_parent_tasks_title`),
              "donor",
              [
                task(
                  t(`${RC}.intended_parent_t1_title`),
                  t(`${RC}.intended_parent_t1_detail`),
                  t(`${RC}.owner_you`),
                  t(`${RC}.intended_parent_t1_due`),
                  "now",
                ),
                task(
                  t(`${RC}.intended_parent_t2_title`),
                  t(`${RC}.intended_parent_t2_detail`),
                  t(`${RC}.owner_you`),
                  t(`${RC}.intended_parent_t2_due`),
                  "now",
                ),
                task(
                  t(`${RC}.intended_parent_t3_title`),
                  t(`${RC}.intended_parent_t3_detail`),
                  t(`${RC}.owner_legal`),
                  t(`${RC}.intended_parent_t3_due`),
                  "waiting",
                ),
              ],
              undefined,
              buildActions(t).findCare,
            ),
            timelineWidget(
              t(`${RC}.intended_parent_timeline_title`),
              "genetics",
              [
                timeline(
                  t(`${RC}.intended_parent_tl1_label`),
                  t(`${RC}.intended_parent_tl1_detail`),
                  "now",
                ),
                timeline(
                  t(`${RC}.intended_parent_tl2_label`),
                  t(`${RC}.intended_parent_tl2_detail`),
                  "next",
                ),
                timeline(
                  t(`${RC}.intended_parent_tl3_label`),
                  t(`${RC}.intended_parent_tl3_detail`),
                  "waiting",
                ),
                timeline(
                  t(`${RC}.intended_parent_tl4_label`),
                  t(`${RC}.intended_parent_tl4_detail`),
                  "waiting",
                ),
              ],
            ),
          ],
        ),
      ],
    },
  };
}

// ── Post-cycle observed results (#1817) ─────────────────────────────
// The post-cycle funnels previously rendered hardcoded counts in the
// production path. They now derive from the newest closed cycle's
// recorded phase fertility metrics — the same store Past Cycle Detail
// reads via extractResultsFunnel — and each stage is included only when
// its metric was actually recorded (absence is "not recorded", never 0).
// When nothing was recorded the funnel widget is omitted entirely. A missing
// record is not a zero and should not be replaced with a development fixture.

type PostCycleFunnels = {
  egg: FunnelStage[] | null;
  embryo: FunnelStage[] | null;
  ivf: FunnelStage[] | null;
};

type FunnelMetricSpec = readonly {
  metric: string;
  labelKey: string;
  detailKey: string;
}[];

const EGG_POSTCYCLE_FUNNEL_METRICS: FunnelMetricSpec = [
  {
    metric: "retrieved_oocytes",
    labelKey: "egg_postcycle_funnel_retrieved_label",
    detailKey: "egg_postcycle_funnel_retrieved_detail",
  },
  {
    metric: "m2_count",
    labelKey: "egg_postcycle_funnel_mature_label",
    detailKey: "egg_postcycle_funnel_mature_detail",
  },
  {
    metric: "frozen_embryo",
    labelKey: "egg_postcycle_funnel_frozen_label",
    detailKey: "egg_postcycle_funnel_frozen_detail",
  },
];

const EMBRYO_POSTCYCLE_FUNNEL_METRICS: FunnelMetricSpec = [
  {
    metric: "retrieved_oocytes",
    labelKey: "embryo_funnel_retrieved_label",
    detailKey: "embryo_funnel_retrieved_detail",
  },
  {
    metric: "m2_count",
    labelKey: "embryo_funnel_mature_label",
    detailKey: "embryo_funnel_mature_detail",
  },
  {
    metric: "fertilized_oocytes",
    labelKey: "embryo_funnel_fertilized_label",
    detailKey: "embryo_funnel_fertilized_detail",
  },
  {
    metric: "embryos_day_5",
    labelKey: "embryo_funnel_blastocysts_label",
    detailKey: "embryo_funnel_blastocysts_detail",
  },
];

const IVF_POSTCYCLE_FUNNEL_METRICS: FunnelMetricSpec = [
  {
    metric: "retrieved_oocytes",
    labelKey: "ivf_negative_funnel_retrieved_label",
    detailKey: "ivf_negative_funnel_retrieved_detail",
  },
  {
    metric: "m2_count",
    labelKey: "ivf_negative_funnel_mature_label",
    detailKey: "ivf_negative_funnel_mature_detail",
  },
  {
    metric: "fertilized_oocytes",
    labelKey: "ivf_negative_funnel_fertilized_label",
    detailKey: "ivf_negative_funnel_fertilized_detail",
  },
  {
    metric: "embryos_day_5",
    labelKey: "ivf_negative_funnel_blasts_label",
    detailKey: "ivf_negative_funnel_blasts_detail",
  },
  {
    metric: "transferred_embryo",
    labelKey: "ivf_negative_funnel_transferred_label",
    detailKey: "ivf_negative_funnel_transferred_detail",
  },
];

function buildFunnelFromObservedResults(
  results: ReturnType<typeof extractObservedResults>,
  spec: FunnelMetricSpec,
  t: TranslationFunction,
): FunnelStage[] | null {
  const resultsByMetric = new globalThis.Map<string, number>(
    results.map((result) => [result.metric, result.value] as const),
  );
  const stages: FunnelStage[] = [];
  for (const row of spec) {
    const value = resultsByMetric.get(row.metric);
    if (value !== undefined) {
      stages.push({
        label: t(`${RC}.${row.labelKey}`),
        count: value,
        detail: t(`${RC}.${row.detailKey}`),
      });
    }
  }
  return stages.length > 0 ? stages : null;
}

function newestClosedCycleId(
  cycles: Cycle[] | undefined,
  cycleType: Cycle["cycleType"],
): string | undefined {
  const closed = (cycles ?? []).filter(
    (c) => c.status === "closed" && c.cycleType === cycleType,
  );
  if (closed.length === 0) return undefined;
  // Same recency rule as the Future Use projection source cycle.
  const sorted = [...closed].sort((a, b) => {
    const at = (cycle: Cycle) =>
      new Date(cycle.endDate ?? cycle.updatedAt ?? cycle.createdAt).getTime();
    return at(b) - at(a);
  });
  return sorted[0]?.id;
}

function usePostCycleFunnels(): PostCycleFunnels {
  const t = useTranslations();
  const { data: cycles } = useCycles();
  const eggCycleId = newestClosedCycleId(cycles, "egg_freezing");
  const embryoCycleId = newestClosedCycleId(cycles, "embryo_freezing");
  const ivfCycleId = newestClosedCycleId(cycles, "ivf");
  const eggPhases = useCyclePhases(eggCycleId);
  const embryoPhases = useCyclePhases(embryoCycleId);
  const ivfPhases = useCyclePhases(ivfCycleId);

  return useMemo(() => {
    const liveEgg = buildFunnelFromObservedResults(
      extractObservedResults(eggPhases.data ?? []),
      EGG_POSTCYCLE_FUNNEL_METRICS,
      t,
    );
    const liveEmbryo = buildFunnelFromObservedResults(
      extractObservedResults(embryoPhases.data ?? []),
      EMBRYO_POSTCYCLE_FUNNEL_METRICS,
      t,
    );
    const liveIvf = buildFunnelFromObservedResults(
      extractObservedResults(ivfPhases.data ?? []),
      IVF_POSTCYCLE_FUNNEL_METRICS,
      t,
    );
    return {
      egg: liveEgg,
      embryo: liveEmbryo,
      ivf: liveIvf,
    };
  }, [eggPhases.data, embryoPhases.data, ivfPhases.data, t]);
}

function useConcepts(
  cycleStatusReviewKind?: CycleStatusReviewKind | null,
  betaDateLabel?: string | null,
): Partial<Record<HomepageReviewConceptVariant, Concept>> {
  const t = useTranslations();
  const semenResults = useSemenAnalysisResults();
  const postCycleFunnels = usePostCycleFunnels();
  return buildConcepts(
    t,
    postCycleFunnels,
    semenResults,
    cycleStatusReviewKind,
    betaDateLabel,
  );
}

function buildDetailedConceptSections(
  t: TranslationFunction,
): Partial<Record<HomepageReviewConceptVariant, ConceptSection[]>> {
  const RC2 = "homepage.review_concept.inline";
  const actions = buildActions(t);
  return {
    "egg-freezing-active": [],
    "egg-freezing-postcycle": [
      section(
        t(`${RC2}.egg_postcycle_future_use_title`),
        t(`${RC2}.egg_postcycle_future_use_subtitle`),
        [futureUseEstimate()],
      ),
      // The closing "decide your next step" card is not a widget here: see
      // `PostCycleHandoffCard`.
    ],
    "embryo-freezing-active": [],
    "embryo-freezing-postcycle": [
      // The closing "decide your next step" card is not a widget here: see
      // `PostCycleHandoffCard`.
      section(
        t(`${RC2}.embryo_postcycle_future_use_title`),
        t(`${RC2}.embryo_postcycle_future_use_subtitle`),
        [futureUseEstimate("embryos")],
      ),
    ],
    "sperm-freezing-active": [
      section(
        t(`${RC2}.sperm_active_collection_quality_section_title`),
        t(`${RC2}.sperm_active_collection_quality_section_subtitle`),
        [
          dataStatus(
            t(`${RC2}.sperm_active_metrics_title`),
            t(`${RC2}.sperm_active_data_not_recorded_detail`),
            "sperm",
            actions.careTeam,
          ),
        ],
      ),
    ],
    "sperm-freezing-wait": [
      section(
        t(`${RC2}.sperm_wait_section_lab_check_title`),
        t(`${RC2}.sperm_wait_section_lab_check_subtitle`),
        [
          semenAnalysis(t(`${RC2}.sperm_wait_semen_title`)),
          dataStatus(
            t(`${RC2}.sperm_wait_decision_title`),
            t(`${RC2}.sperm_wait_data_not_recorded_detail`),
            "optimization",
            actions.documents,
          ),
        ],
      ),
    ],
    "sperm-freezing-postcycle": [
      // The closing "decide your next step" card is not a widget here: see
      // `PostCycleHandoffCard`.
      section(
        t(`${RC2}.sperm_postcycle_future_use_title`),
        t(`${RC2}.sperm_postcycle_future_use_subtitle`),
        [spermCryosurvivalEstimate()],
      ),
    ],
    "ivf-active": [],
    "beta-wait": [],
    "ivf-positive": [],
    "ivf-negative": [],
    "iui-active": [],
    "iui-wait": [],
    "oi-wait": [],
    "oi-active": [],
    "intended-parent": [
      section(
        t(`${RC2}.intended_parent_match_section_title`),
        t(`${RC2}.intended_parent_match_section_subtitle`),
        [
          decision(
            t(`${RC2}.intended_parent_match_decision_title`),
            "donor",
            [
              {
                title: t(`${RC2}.intended_parent_match_d1_title`),
                detail: t(`${RC2}.intended_parent_match_d1_detail`),
                status: t(`${RC2}.intended_parent_match_d1_status`),
              },
              {
                title: t(`${RC2}.intended_parent_match_d2_title`),
                detail: t(`${RC2}.intended_parent_match_d2_detail`),
                status: t(`${RC2}.intended_parent_match_d2_status`),
              },
              {
                title: t(`${RC2}.intended_parent_match_d3_title`),
                detail: t(`${RC2}.intended_parent_match_d3_detail`),
                status: t(`${RC2}.intended_parent_match_d3_status`),
              },
            ],
            undefined,
            actions.findCare,
          ),
          tasks(t(`${RC2}.intended_parent_match_tasks_title`), "genetics", [
            task(
              t(`${RC2}.intended_parent_match_t1_title`),
              t(`${RC2}.intended_parent_match_t1_detail`),
              t(`${RC2}.owner_you`),
              t(`${RC2}.due_this_week`),
              "now",
            ),
            task(
              t(`${RC2}.intended_parent_match_t2_title`),
              t(`${RC2}.intended_parent_match_t2_detail`),
              t(`${RC2}.owner_legal`),
              t(`${RC2}.due_after_shortlist`),
              "waiting",
            ),
            task(
              t(`${RC2}.intended_parent_match_t3_title`),
              t(`${RC2}.intended_parent_match_t3_detail`),
              t(`${RC2}.owner_clinic`),
              t(`${RC2}.due_after_donor_selected`),
              "waiting",
            ),
          ]),
        ],
      ),
    ],
  };
}

function buildPostRetrievalHeroCopy(
  t: TranslationFunction,
  cycleType: CycleType | undefined,
): { eyebrow: string; title: string; summary: string } {
  const procedureKey =
    cycleType === "egg_freezing"
      ? "egg_freezing"
      : cycleType === "embryo_freezing"
        ? "embryo_freezing"
        : "ivf";
  const RC2 = "homepage.review_concept";
  return {
    eyebrow: t(`${RC2}.hero_post_retrieval_${procedureKey}_eyebrow`),
    title: t(`${RC2}.hero_post_retrieval_title`),
    summary: t(`${RC2}.hero_post_retrieval_${procedureKey}_summary`),
  };
}

function buildHeroStatusCopy(
  t: TranslationFunction,
): Partial<
  Record<HomepageReviewConceptVariant, { title: string; summary: string }>
> {
  const RC = "homepage.review_concept";
  return {
    "egg-freezing-active": {
      title: t(`${RC}.hero_egg_freezing_active_title`),
      summary: t(`${RC}.hero_egg_freezing_active_summary`),
    },
    "egg-freezing-postcycle": {
      title: t(`${RC}.hero_egg_freezing_postcycle_title`),
      summary: t(`${RC}.hero_egg_freezing_postcycle_summary`),
    },
    "embryo-freezing-active": {
      title: t(`${RC}.hero_embryo_freezing_active_title`),
      summary: t(`${RC}.hero_embryo_freezing_active_summary`),
    },
    "embryo-freezing-postcycle": {
      title: t(`${RC}.hero_embryo_freezing_postcycle_title`),
      summary: t(`${RC}.hero_embryo_freezing_postcycle_summary`),
    },
    "sperm-freezing-active": {
      title: t(`${RC}.hero_sperm_freezing_active_title`),
      summary: t(`${RC}.hero_sperm_freezing_active_summary`),
    },
    "sperm-freezing-wait": {
      title: t(`${RC}.hero_sperm_freezing_wait_title`),
      summary: t(`${RC}.hero_sperm_freezing_wait_summary`),
    },
    "sperm-freezing-postcycle": {
      title: t(`${RC}.hero_sperm_freezing_postcycle_title`),
      summary: t(`${RC}.hero_sperm_freezing_postcycle_summary`),
    },
    "ivf-active": {
      title: t(`${RC}.hero_ivf_active_title`),
      summary: t(`${RC}.hero_ivf_active_summary`),
    },
    "fet-active": {
      title: t(`${RC}.fet_active_plan_heading`),
      summary: t(`${RC}.fet_active_plan_summary`),
    },
    "cycle-status-review": {
      title: t(`${RC}.cycle_status_review_title`),
      summary: t(`${RC}.cycle_status_review_summary`),
    },
    "beta-wait": {
      title: t(`${RC}.hero_beta_wait_title_no_date`),
      summary: t(`${RC}.hero_beta_wait_summary`),
    },
    "fet-positive": {
      title: t(`${RC}.post_procedure_outcome.fet.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.positive.summary`),
    },
    "fet-negative": {
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.fet.negative.summary`),
    },
    "fet-uncertain": {
      title: t(`${RC}.post_procedure_outcome.fet.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.uncertain.summary`),
    },
    "fet-adverse": {
      title: t(`${RC}.post_procedure_outcome.fet.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.adverse.summary`),
    },
    "fet-unavailable": {
      title: t(`${RC}.post_procedure_outcome.fet.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.unavailable.summary`),
    },
    "iui-positive": {
      title: t(`${RC}.post_procedure_outcome.iui.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.positive.summary`),
    },
    "iui-negative": {
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.iui.negative.summary`),
    },
    "iui-uncertain": {
      title: t(`${RC}.post_procedure_outcome.iui.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.uncertain.summary`),
    },
    "iui-adverse": {
      title: t(`${RC}.post_procedure_outcome.iui.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.adverse.summary`),
    },
    "iui-unavailable": {
      title: t(`${RC}.post_procedure_outcome.iui.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.unavailable.summary`),
    },
    "oi-positive": {
      title: t(`${RC}.post_procedure_outcome.oi.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.positive.summary`),
    },
    "oi-negative": {
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.oi.negative.summary`),
    },
    "oi-uncertain": {
      title: t(`${RC}.post_procedure_outcome.oi.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.uncertain.summary`),
    },
    "oi-adverse": {
      title: t(`${RC}.post_procedure_outcome.oi.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.adverse.summary`),
    },
    "oi-unavailable": {
      title: t(`${RC}.post_procedure_outcome.oi.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.unavailable.summary`),
    },
    "ivf-unavailable": {
      title: t(`${RC}.post_procedure_outcome.ivf.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.unavailable.summary`),
    },
    "ivf-uncertain": {
      title: t(`${RC}.post_procedure_outcome.ivf.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.uncertain.summary`),
    },
    "ivf-adverse": {
      title: t(`${RC}.post_procedure_outcome.ivf.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.adverse.summary`),
    },
    // The last two of the twenty outcome routes still reading legacy hero copy.
    // It was written for a fixture and states things no record backs: "Positive
    // beta, repeat in 48 hours" prescribes the next test, and "Result reviewed,
    // consult is May 23" names an appointment date out of nothing. Both sat
    // directly above a body that had already been moved onto the shared copy, so
    // the hero and the section under it described different situations.
    "ivf-positive": {
      title: t(`${RC}.post_procedure_outcome.ivf.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.positive.summary`),
    },
    "ivf-negative": {
      // One title for every pathway. Naming the procedure told a patient whose
      // cycle never had one — a preservation cycle falls through
      // `resolveModality` to the IVF default — to take a breath "after your IVF
      // transfer". The summary was already shared word for word; only the
      // heading claimed a procedure, and an ending with no pregnancy does not
      // depend on which procedure preceded it.
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.ivf.negative.summary`),
    },
    "iui-active": {
      title: t(`${RC}.hero_iui_active_title`),
      summary: t(`${RC}.hero_iui_active_summary`),
    },
    "iui-wait": {
      title: t(`${RC}.hero_iui_wait_title`),
      summary: t(`${RC}.hero_iui_wait_summary`),
    },
    "oi-wait": {
      title: t(`${RC}.hero_oi_wait_title`),
      summary: t(`${RC}.hero_oi_wait_summary`),
    },
    "oi-active": {
      title: t(`${RC}.hero_oi_active_title`),
      summary: t(`${RC}.hero_oi_active_summary`),
    },
    "intended-parent": {
      title: t(`${RC}.hero_intended_parent_title`),
      summary: t(`${RC}.hero_intended_parent_summary`),
    },
  };
}

function buildPostCycleHeroCopy(
  t: TranslationFunction,
  variant: HomepageReviewConceptVariant,
  counts: { eggs: number; embryos: number; sperm: number },
) {
  const RC = "homepage.review_concept";
  const config =
    variant === "egg-freezing-postcycle"
      ? {
          count: counts.eggs,
          storedTitle: "hero_egg_freezing_postcycle_stored_title",
          storedSummary: "hero_egg_freezing_postcycle_stored_summary",
          title: "hero_egg_freezing_postcycle_title",
          summary: "hero_egg_freezing_postcycle_summary",
        }
      : variant === "embryo-freezing-postcycle"
        ? {
            count: counts.embryos,
            storedTitle: "hero_embryo_freezing_postcycle_stored_title",
            storedSummary: "hero_embryo_freezing_postcycle_stored_summary",
            title: "hero_embryo_freezing_postcycle_title",
            summary: "hero_embryo_freezing_postcycle_summary",
          }
        : {
            count: counts.sperm,
            storedTitle: "hero_sperm_freezing_postcycle_stored_title",
            storedSummary: "hero_sperm_freezing_postcycle_stored_summary",
            title: "hero_sperm_freezing_postcycle_title",
            summary: "hero_sperm_freezing_postcycle_summary",
          };

  return {
    title:
      config.count > 0
        ? t(`${RC}.${config.storedTitle}`, {
            params: { count: String(config.count) },
          })
        : t(`${RC}.${config.title}`),
    summary:
      config.count > 0
        ? t(`${RC}.${config.storedSummary}`)
        : t(`${RC}.${config.summary}`),
  };
}

function WidgetFrame({
  title,
  subtitle,
  icon,
  children,
  action,
  onAction,
}: {
  title: string;
  subtitle?: string;
  icon: OrganicMiniIconKind;
  children: React.ReactNode;
  action?: PlanAction;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as Record<string, unknown>),
        borderRadius: 18,
        padding: 15,
        gap: 12,
      }}
    >
      <CardHeader
        leading={<OrganicMiniIcon kind={icon} size={36} />}
        title={title}
        subtitle={subtitle}
      />

      {children}

      {action ? (
        <Pressable
          onPress={() => onAction(action)}
          accessibilityRole="button"
          accessibilityLabel={action.label}
          style={({ pressed }) => ({
            alignSelf: "flex-start",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            opacity: pressed ? 0.72 : 1,
          })}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 15,
              color: EMBER.colors.text,
              fontWeight: "800",
            }}
          >
            {action.label}
          </Text>
          <ArrowRight size={13} color={EMBER.colors.text} strokeWidth={1.8} />
        </Pressable>
      ) : null}
    </View>
  );
}

function MetricGrid({ metrics: values }: { metrics: Metric[] }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {values.map((metric) => (
        <View
          key={`${metric.label}-${metric.value}`}
          style={{
            flexGrow: 1,
            flexBasis: 116,
            borderRadius: 14,
            backgroundColor: "rgba(255,255,255,0.48)",
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.08)",
            padding: 11,
          }}
        >
          <MetricLabelText>{metric.label}</MetricLabelText>
          <MetricValueText style={{ marginTop: 3 }}>
            {metric.value}
          </MetricValueText>
          {metric.note ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 16,
                color: EMBER.colors.textMid,
                marginTop: 1,
              }}
            >
              {metric.note}
            </Text>
          ) : null}
        </View>
      ))}
    </View>
  );
}

function resolvePreviewTestFactSheet(testName: string) {
  const normalizedName = testName.toLowerCase();
  if (normalizedName === "amh") {
    return getTestByName("AMH blood test");
  }
  if (normalizedName === "fsh") {
    return getTestByName("FSH Blood Test");
  }
  if (normalizedName === "vitamin d") {
    return getTestByName("Vitamin D (25-OH)");
  }
  if (normalizedName === "tsh") {
    return getTestByName("Thyroid Panel");
  }
  return getTestByName(testName);
}

function TestResultsPreview({
  results,
  title,
  subtitle,
  queryState,
  onAction,
  onOpenTestFactSheet,
}: {
  results?: ResultInterpretation[];
  title?: string;
  subtitle?: string;
  queryState?: TestResultInsightsQueryState;
  onAction: (action: PlanAction) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
}) {
  const t = useTranslations();
  const rows = results ?? [];
  const uploadAction: PlanAction = {
    label: t("homepage.test_result_document.upload_action"),
    signal: "open-document-upload",
    params: { initialCategory: "medical_records" },
  };
  const handleUpload = () => onAction(uploadAction);

  if (queryState?.isLoading) {
    return (
      <View
        testID="home-test-results-loading"
        accessibilityLiveRegion="polite"
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          minHeight: 86,
          paddingHorizontal: 16,
          paddingVertical: 16,
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <ActivityIndicator color={EMBER.colors.textLight} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textLight,
            fontWeight: "700",
          }}
        >
          {t("clinical_data.loading")}
        </Text>
      </View>
    );
  }

  if (queryState?.isError) {
    return (
      <ClinicalDataStatus
        state="unavailable"
        title={t("clinical_data.unavailable_title")}
        message={t("clinical_data.unavailable_body")}
        retryLabel={t("clinical_data.retry")}
        retryingLabel={t("clinical_data.retrying")}
        onRetry={queryState.onRetry}
        isRetrying={queryState.isFetching}
        testID="home-test-results-unavailable"
      />
    );
  }

  if (rows.length === 0) {
    return (
      <ActionRowCard
        leading={
          <Image
            source={testTubesIllustration}
            style={{ width: 32, height: 32 }}
            resizeMode="contain"
          />
        }
        title={title ?? t("homepage.review_concept.inline.test_results_title")}
        subtitle={
          subtitle ??
          t("homepage.review_concept.inline.test_results_not_recorded_detail")
        }
        onPress={handleUpload}
        accessibilityLabel={`${
          title ?? t("homepage.review_concept.inline.test_results_title")
        }. ${
          subtitle ??
          t("homepage.review_concept.inline.test_results_not_recorded_detail")
        }`}
      />
    );
  }

  return (
    <View style={{ gap: 9 }}>
      {rows.map((interpretation, index) => {
        const factsheet = resolvePreviewTestFactSheet(interpretation.testName);
        return (
          <TestResultInsightCard
            key={`review-test-result-${resultInterpretationKey(interpretation, index)}`}
            interpretation={interpretation}
            state={{ kind: "new-result" }}
            onPress={
              factsheet && onOpenTestFactSheet
                ? () =>
                    onOpenTestFactSheet({
                      item: factsheet,
                      testName: interpretation.testName,
                    })
                : undefined
            }
          />
        );
      })}
    </View>
  );
}

function buildMedicationLogOptions(t: TranslationFunction) {
  const RC = "homepage.review_concept";
  return [
    {
      id: "on-time" as const,
      label: t(`${RC}.medication_log_on_time_label`),
      detail: t(`${RC}.medication_log_on_time_detail`),
    },
    {
      id: "late" as const,
      label: t(`${RC}.medication_log_late_label`),
      detail: t(`${RC}.medication_log_late_detail`),
    },
    {
      id: "missed" as const,
      label: t(`${RC}.medication_log_missed_label`),
      detail: t(`${RC}.medication_log_missed_detail`),
    },
    {
      id: "skipped" as const,
      label: t(`${RC}.medication_log_skipped_label`),
      detail: t(`${RC}.medication_log_skipped_detail`),
    },
  ];
}

function getMedicationLogIllustration(row: TodayRow): number {
  const format = row.medicationFormat?.toLowerCase() ?? "";
  if (row.medicationForm === "pen" || format.includes("pen")) {
    return medicationPenIllustration;
  }
  if (row.medicationForm === "vial" || format.includes("vial")) {
    return medicationVialIllustration;
  }
  if (format.includes("syringe")) return medicationVialIllustration;
  if (format.includes("inject") || format.includes("subcutaneous")) {
    return medicationPenIllustration;
  }

  const contentMedication = getMedicationByBrandName(
    row.medicationName ?? row.title,
  );
  const commonForms = contentMedication?.body.commonForms ?? [];
  if (commonForms.includes("pen")) return medicationPenIllustration;
  if (
    commonForms.includes("vial") ||
    commonForms.includes("pre_filled_syringe")
  ) {
    return medicationVialIllustration;
  }

  return medicationPillIllustration;
}

export type PeekAheadDay = {
  label: string;
  rows: TodayRow[];
  emptyTitle?: string;
  emptyDetail?: string;
};

export type MedicationLogReadState = {
  status: "loading" | "ready" | "error";
  isRetrying: boolean;
  retry: () => void;
};

function buildMedicationContext(
  row: TodayRow,
): HomepageMedicationFactSheetPayload["userContext"] {
  return {
    originalName: row.title,
    dosage: row.medicationDose,
    ...(row.medicationRoute !== undefined && {
      route: row.medicationRoute,
    }),
    ...(row.notes ? { notes: row.notes } : {}),
    ...(row.medicationEndDate !== undefined && {
      endDate: row.medicationEndDate,
    }),
    timeTaken: row.time === "Any time" ? undefined : [row.time],
  };
}

function buildAppointmentContext(
  row: TodayRow,
): HomepageProcedureFactSheetPayload["appointment"] {
  return {
    id: row.id,
    appointmentId: row.appointmentId,
    title: row.title,
    subtitle: row.detail,
    time: row.time,
    date: "Today",
    notes: row.notes,
    ownership: row.ownership,
    ownerId: row.ownerId,
  };
}

function getTodayActionId(row: TodayRow): string {
  return row.appointmentId ?? row.id;
}

export function TodayList({
  rows,
  onOpenMedicationFactSheet,
  onOpenAppointment,
  onOpenProcedureFactSheet,
  onRequestDocumentAttachment,
  completionSource = "home",
  onCompletionFeedback,
  onMedicationLogReadStateChange,
  showCompletion = true,
  rowAppearance = "default",
  peekAheadDays,
}: {
  rows: TodayRow[];
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  /**
   * Appointment-management entry point. Care supplies this so a saved
   * appointment opens its details first; callers that omit it retain the
   * existing procedure-education behavior.
   */
  onOpenAppointment?: (row: TodayRow) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  /** Opens the canonical document picker with the row's provenance tags. */
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  completionSource?: "home" | "care";
  onCompletionFeedback?: () => void;
  onMedicationLogReadStateChange?: (state: MedicationLogReadState) => void;
  showCompletion?: boolean;
  rowAppearance?: "default" | "calendar-glass";
  peekAheadDays?: PeekAheadDay[];
}) {
  const timezone = useTimezone();
  const today = getTodayString(timezone);
  const medicationLogDateRange = useMemo(() => {
    const dates = rows
      .map((row) => row.date)
      .filter((date): date is string => Boolean(date))
      .sort();
    const fallback = today;
    return {
      startDate: dates[0] ?? fallback,
      endDate: dates[dates.length - 1] ?? fallback,
    };
  }, [rows, today]);
  const todayLogs = useMedicationLogs({
    startDate: medicationLogDateRange.startDate,
    endDate: medicationLogDateRange.endDate,
    enabled: showCompletion,
  });
  const medicationLogsRefetch = useRef(todayLogs.refetch);
  medicationLogsRefetch.current = todayLogs.refetch;
  useEffect(() => {
    if (!onMedicationLogReadStateChange) return;
    onMedicationLogReadStateChange({
      status: todayLogs.error
        ? "error"
        : todayLogs.isLoading
          ? "loading"
          : "ready",
      isRetrying: todayLogs.isFetching,
      retry: () => {
        void medicationLogsRefetch.current();
      },
    });
  }, [
    onMedicationLogReadStateChange,
    todayLogs.error,
    todayLogs.isFetching,
    todayLogs.isLoading,
  ]);
  const { data: patientNotes } = usePatientNotes();

  const [completionDialogRow, setCompletionDialogRow] =
    useState<TodayRow | null>(null);

  const doseStatusFor = (row: TodayRow) =>
    row.kind === "medication"
      ? getTodayDoseStatus(todayLogs.data, row, row.date ?? today, timezone)
      : undefined;

  const isRowComplete = (row: TodayRow) =>
    row.kind === "medication"
      ? Boolean(row.medicationIsStopped) || Boolean(doseStatusFor(row))
      : isCompletableTodayRow(row)
        ? Boolean(getTodayActionCompletion(patientNotes, getTodayActionId(row)))
        : false;

  const allComplete =
    showCompletion && rows.length > 0 && rows.every(isRowComplete);

  const handleRowPress = (row: TodayRow) => {
    haptic("light");
    if (row.kind === "medication") {
      const medicationName = row.medicationName ?? row.title;
      const payload: HomepageMedicationFactSheetPayload = {
        item: getMedicationByBrandName(medicationName) ?? null,
        fallbackName: row.title,
        medicationId: row.medicationId,
        userContext: buildMedicationContext(row),
      };
      if (onOpenMedicationFactSheet) {
        onOpenMedicationFactSheet({
          ...payload,
          onLogDose: () => setCompletionDialogRow(row),
        });
      } else {
        setCompletionDialogRow(row);
      }
      return;
    }

    if (row.kind === "appointment" && onOpenAppointment) {
      onOpenAppointment(row);
      return;
    }

    if (row.kind === "appointment" || row.kind === "monitoring") {
      const procedure = getProcedureByName(
        row.procedureName ??
          (row.kind === "monitoring"
            ? "Transvaginal ultrasound"
            : "Initial Fertility Consultation"),
      );
      if (procedure && onOpenProcedureFactSheet) {
        onOpenProcedureFactSheet({
          item: procedure,
          appointment: buildAppointmentContext(row),
        });
        return;
      }
    }

    if (isCompletableTodayRow(row)) setCompletionDialogRow(row);
  };

  const handleCheckboxPress = (row: TodayRow) => {
    if (row.kind !== "medication" && !isCompletableTodayRow(row)) return;
    haptic("selection");
    setCompletionDialogRow(row);
  };

  const handleCompleteRow = (_row: TodayRow) => {
    setCompletionDialogRow(null);
    haptic("nudge");
    onCompletionFeedback?.();
  };

  return (
    <View style={{ gap: 9, position: "relative", overflow: "visible" }}>
      {allComplete ? <TodayDoneBanner peekAheadDays={peekAheadDays} /> : null}
      {rows.length === 0 ? (
        <TodayEmptyState peekAheadDays={peekAheadDays} />
      ) : null}
      {rows.map((row) => {
        const isStopped = Boolean(row.medicationIsStopped);
        const rowDoseStatus = doseStatusFor(row);
        const isComplete = showCompletion && isRowComplete(row);
        const isPartnerOwned = Boolean(row.partnerName);
        if (row.kind === "medication") {
          return (
            <MedicationRow
              key={row.id}
              name={row.medicationName ?? row.title}
              dose={row.medicationDose}
              format={row.medicationFormat}
              subtitle={row.detail}
              form={row.medicationForm}
              availabilityStatus={row.medicationAvailabilityStatus}
              time={row.time}
              surface="standalone"
              appearance={rowAppearance}
              completed={isComplete}
              doseStatus={rowDoseStatus}
              isStopped={isStopped}
              partnerName={row.partnerName}
              rightAction={
                isPartnerOwned
                  ? "partnerTag"
                  : showCompletion && !isStopped
                    ? "checkbox"
                    : "none"
              }
              onPress={() => handleRowPress(row)}
              onCheckboxPress={() => handleCheckboxPress(row)}
            />
          );
        }

        return (
          <AppointmentRow
            key={row.id}
            row={row}
            isComplete={isComplete}
            showCheckbox={showCompletion}
            appearance={rowAppearance}
            onPress={() => handleRowPress(row)}
            onCheckboxPress={() => handleCheckboxPress(row)}
          />
        );
      })}
      {showCompletion ? (
        <TodayRowCompletionDialog
          row={completionDialogRow}
          visible={Boolean(completionDialogRow)}
          onClose={() => setCompletionDialogRow(null)}
          onRequestDocumentAttachment={onRequestDocumentAttachment}
          completionSource={completionSource}
          onComplete={handleCompleteRow}
          existingMedicationLog={
            completionDialogRow?.kind === "medication"
              ? getTodayDoseLog(
                  todayLogs.data,
                  completionDialogRow,
                  completionDialogRow.date ?? today,
                  timezone,
                )
              : undefined
          }
        />
      ) : null}
    </View>
  );
}

function TodayDoneBanner({
  peekAheadDays,
}: {
  peekAheadDays?: PeekAheadDay[];
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TodayDoneBanner");
  const [peekVisible, setPeekVisible] = useState(false);

  return (
    <>
      <View
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          paddingHorizontal: 15,
          paddingVertical: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <View
          style={{
            width: 28,
            height: 28,
            borderRadius: 14,
            backgroundColor: "rgba(121,70,51,0.13)",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Check size={16} color="#794633" strokeWidth={3} />
        </View>
        <View style={{ flex: 1 }}>
          <EmptyStateTitleText>
            {t("homepage.review_concept.today_done_banner_title")}
          </EmptyStateTitleText>
          <EmptyStateSubtitleText>
            {t("homepage.review_concept.today_done_banner_subtitle")}
          </EmptyStateSubtitleText>
        </View>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("homepage.review_concept.peek_at_tomorrow")),
              "homepage.review_concept.peek_at_tomorrow",
            );
            setPeekVisible(true);
          }}
          accessibilityRole="button"
          accessibilityLabel={t("homepage.review_concept.peek_a11y")}
          style={({ pressed }) => ({
            borderRadius: 999,
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.1)",
            backgroundColor: "rgba(255,255,255,0.34)",
            paddingHorizontal: 10,
            paddingVertical: 7,
            flexDirection: "row",
            alignItems: "center",
            gap: 5,
            opacity: pressed ? 0.72 : 1,
          })}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 15,
              color: EMBER.colors.text,
              fontWeight: "800",
            }}
          >
            {t("homepage.review_concept.peek_at_tomorrow")}
          </Text>
          <ArrowRight size={13} color={EMBER.colors.text} strokeWidth={1.8} />
        </Pressable>
      </View>
      <PeekAheadDialog
        visible={peekVisible}
        onClose={() => setPeekVisible(false)}
        days={peekAheadDays}
      />
    </>
  );
}

function TodayEmptyState({
  peekAheadDays,
}: {
  peekAheadDays?: PeekAheadDay[];
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TodayEmptyState");
  const [peekVisible, setPeekVisible] = useState(false);

  return (
    <>
      <View
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          paddingHorizontal: 15,
          paddingVertical: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <CalendarCheck
          size={20}
          color={EMBER.colors.textMid}
          strokeWidth={1.8}
        />
        <View style={{ flex: 1 }}>
          <EmptyStateTitleText>
            {t("homepage.review_concept.today_empty_banner_title")}
          </EmptyStateTitleText>
          <EmptyStateSubtitleText>
            {t("homepage.review_concept.today_empty_banner_subtitle")}
          </EmptyStateSubtitleText>
        </View>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("homepage.review_concept.peek_at_tomorrow")),
              "homepage.review_concept.peek_at_tomorrow",
            );
            setPeekVisible(true);
          }}
          accessibilityRole="button"
          accessibilityLabel={t("homepage.review_concept.peek_a11y")}
          style={({ pressed }) => ({
            borderRadius: 999,
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.1)",
            backgroundColor: "rgba(255,255,255,0.34)",
            paddingHorizontal: 10,
            paddingVertical: 7,
            flexDirection: "row",
            alignItems: "center",
            gap: 5,
            opacity: pressed ? 0.72 : 1,
          })}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 15,
              color: EMBER.colors.text,
              fontWeight: "800",
            }}
          >
            {t("homepage.review_concept.peek_at_tomorrow")}
          </Text>
          <ArrowRight size={13} color={EMBER.colors.text} strokeWidth={1.8} />
        </Pressable>
      </View>
      <PeekAheadDialog
        visible={peekVisible}
        onClose={() => setPeekVisible(false)}
        days={peekAheadDays}
      />
    </>
  );
}

function PeekAheadDialog({
  visible,
  onClose,
  days,
}: {
  visible: boolean;
  onClose: () => void;
  days?: PeekAheadDay[];
}) {
  const t = useTranslations();
  // An empty schedule is a real state. Never fall back to the old stimulation
  // preview here: post-cycle and result homes can legitimately have nothing
  // scheduled, and showing a fabricated monitoring visit or medication is
  // clinically misleading.
  const peekDays =
    days && days.length > 0
      ? days
      : [
          {
            label: t("homepage.review_concept.inline.peek_day_tomorrow"),
            rows: [],
            emptyTitle: t("homepage.review_concept.inline.peek_empty_title"),
            emptyDetail: t("homepage.review_concept.inline.peek_empty_detail"),
          },
        ];
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const selectedDay = peekDays[selectedDayIndex] ?? peekDays[0]!;
  const capturePress = usePressCapture("PeekAheadDialog");

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
    >
      <View style={{ maxHeight: Dimensions.get("window").height * 0.72 }}>
        <DialogHeader
          title="A peek ahead"
          subtitle="The next few days may shift after monitoring, but this is the current plan."
          titleStyle={{ paddingRight: 28 }}
          subtitleStyle={{ marginBottom: 10 }}
        />

        <View
          style={{
            flexDirection: "row",
            gap: 7,
            marginBottom: 12,
          }}
        >
          {peekDays.map((day, index) => {
            const selected = index === selectedDayIndex;
            return (
              <Pressable
                key={day.label}
                onPress={() => {
                  capturePress("peek_ahead_day");
                  setSelectedDayIndex(index);
                }}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                style={({ pressed }) => ({
                  flex: 1,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: selected
                    ? "rgba(121,70,51,0.28)"
                    : "rgba(92,52,32,0.1)",
                  backgroundColor: selected
                    ? "rgba(121,70,51,0.11)"
                    : "rgba(255,255,255,0.42)",
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  alignItems: "center",
                  opacity: pressed ? 0.72 : 1,
                })}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    lineHeight: 15,
                    color: selected ? "#794633" : EMBER.colors.textMid,
                    fontWeight: "800",
                  }}
                >
                  {day.label}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 9, paddingBottom: 4 }}
        >
          {selectedDay.rows.length > 0 ? (
            selectedDay.rows.map((row) => (
              <PeekAheadRow key={row.id} row={row} />
            ))
          ) : (
            <View style={{ ...(GLASS_CARD_STYLE as object), padding: 14 }}>
              <View
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 17,
                  backgroundColor: "rgba(121,70,51,0.1)",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 8,
                }}
              >
                <CalendarClock
                  size={17}
                  color="rgba(121,70,51,0.78)"
                  strokeWidth={2}
                />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  lineHeight: 18,
                  color: EMBER.colors.text,
                  fontWeight: "900",
                }}
              >
                {selectedDay.emptyTitle ?? "Nothing scheduled yet."}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  color: EMBER.colors.textMid,
                  marginTop: 3,
                }}
              >
                {selectedDay.emptyDetail ??
                  "If the clinic adds something, it will show up here."}
              </Text>
            </View>
          )}
        </ScrollView>
      </View>
    </Dialog>
  );
}

function PeekAheadRow({ row }: { row: TodayRow }) {
  const rowIllustration = getTodayRowIllustration(row);

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as Record<string, unknown>),
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 13,
      }}
    >
      {row.time ? (
        <View style={{ width: 58, flexShrink: 0 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 15,
              color: EMBER.colors.textLight,
              fontWeight: "800",
            }}
          >
            {row.time}
          </Text>
        </View>
      ) : null}
      <View
        style={{
          width: 34,
          height: 34,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {rowIllustration ? (
          <Image
            source={rowIllustration}
            resizeMode="contain"
            style={{ width: 32, height: 32 }}
          />
        ) : (
          <OrganicMiniIcon kind={row.icon} size={30} />
        )}
      </View>
      <View style={{ flex: 1 }}>
        <PrimaryTitleText>{row.title}</PrimaryTitleText>
        <SupportingSubtitleText>{row.detail}</SupportingSubtitleText>
      </View>
    </View>
  );
}

function TodayRowCompletionDialog({
  row,
  visible,
  onClose,
  onRequestDocumentAttachment,
  completionSource,
  onComplete,
  existingMedicationLog,
}: {
  row: TodayRow | null;
  visible: boolean;
  onClose: () => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  completionSource: "home" | "care";
  onComplete: (row: TodayRow) => void;
  existingMedicationLog?: MedicationLog;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TodayRowCompletionDialog");
  const logDose = useLogMedicationDose();
  const createPatientNote = useCreatePatientNote();
  const { showToast } = useToast();
  const timezone = useTimezone();
  const medicationLogOptions = useMemo(() => buildMedicationLogOptions(t), [t]);
  const [medicationStatus, setMedicationStatus] = useState("on-time");
  const [medicationNotes, setMedicationNotes] = useState("");
  const [actionStatus, setActionStatus] =
    useState<TodayActionCompletionStatus>("completed");
  const [actionNotes, setActionNotes] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const medicationSubmissionRef = useRef<{
    signature: string;
    key: string;
    payload: CreateMedicationLogPayload;
  } | null>(null);
  const actionSubmissionRef = useRef<{
    signature: string;
    key: string;
    payload: CreateNotePayload;
  } | null>(null);

  const existingStatus = existingMedicationLog?.status;
  const existingNotes = existingMedicationLog?.notes;
  useEffect(() => {
    setMedicationStatus(
      existingStatus === "skipped"
        ? "skipped"
        : existingStatus === "late"
          ? "late"
          : existingStatus === "missed"
            ? "missed"
            : "on-time",
    );
    setMedicationNotes(existingNotes ?? "");
    setActionStatus("completed");
    setActionNotes("");
    setSaveError(null);
    medicationSubmissionRef.current = null;
    actionSubmissionRef.current = null;
  }, [row?.id, visible, existingStatus, existingNotes]);

  if (!row) return null;

  const isMedication = row.kind === "medication";
  const isMonitoring =
    row.kind === "monitoring" || row.appointmentResultType === "monitoring";
  const isAppointment = row.kind === "appointment" || row.kind === "monitoring";
  const medicationIllustration = isMedication
    ? getMedicationLogIllustration(row)
    : null;
  const title = isMonitoring
    ? t("homepage.review_concept.log_monitoring_visit_title")
    : t("homepage.review_concept.log_prefix", {
        params: { title: row.title },
      });
  const summary = isMedication
    ? t("homepage.review_concept.log_medication_summary")
    : isMonitoring
      ? t("homepage.review_concept.log_monitoring_summary")
      : isAppointment
        ? t("homepage.review_concept.log_appointment_summary")
        : t("homepage.review_concept.log_task_summary");

  const actionOptions = isMonitoring
    ? [
        {
          id: "attended" as const,
          label: t("homepage.review_concept.monitoring_log_attended_label"),
          detail: t("homepage.review_concept.monitoring_log_attended_detail"),
        },
        {
          id: "needs_help" as const,
          label: t("homepage.review_concept.appointment_log_issue_label"),
          detail: t("homepage.review_concept.appointment_log_issue_detail"),
        },
      ]
    : isAppointment
      ? [
          {
            id: "completed" as const,
            label: t("homepage.review_concept.appointment_log_complete_label"),
            detail: t(
              "homepage.review_concept.appointment_log_complete_detail",
            ),
          },
          {
            id: "rescheduled" as const,
            label: t(
              "homepage.review_concept.appointment_log_rescheduled_label",
            ),
            detail: t(
              "homepage.review_concept.appointment_log_rescheduled_detail",
            ),
          },
          {
            id: "needs_help" as const,
            label: t("homepage.review_concept.appointment_log_issue_label"),
            detail: t("homepage.review_concept.appointment_log_issue_detail"),
          },
        ]
      : [
          {
            id: "completed" as const,
            label: t("homepage.review_concept.task_log_complete_label"),
            detail: t("homepage.review_concept.task_log_complete_detail"),
          },
        ];

  const handleSave = async () => {
    if (isMedication && !row.medicationId) {
      setSaveError(
        t("homepage.review_concept.medication_log_missing_medication_id"),
      );
      return;
    }

    setSaveError(null);
    try {
      if (isMedication) {
        const apiStatus: DoseStatus =
          medicationStatus === "late"
            ? "late"
            : medicationStatus === "missed"
              ? "missed"
              : medicationStatus === "skipped"
                ? "skipped"
                : "taken";
        const medicationIntent = {
          patientMedicationId: row.medicationId!,
          medicationName: row.medicationName ?? row.title,
          dosage: row.medicationDose ?? null,
          scheduledFor: row.scheduledFor ?? undefined,
          status: apiStatus,
          notes: medicationNotes.trim() ? medicationNotes.trim() : null,
          reminderId: null,
        } satisfies Omit<CreateMedicationLogPayload, "takenAt">;
        const signature = JSON.stringify(medicationIntent);
        const existingSubmission = medicationSubmissionRef.current;
        const submission =
          existingSubmission?.signature === signature
            ? existingSubmission
            : {
                signature,
                key: createIdempotencyKey(),
                payload: {
                  ...medicationIntent,
                  takenAt: new Date().toISOString(),
                },
              };
        medicationSubmissionRef.current = submission;
        await logDose.mutateAsync({
          ...submission.payload,
          idempotencyKey: submission.key,
        });
      } else {
        const date = row.date ?? getTodayString(timezone);
        const actionIntent = {
          actionId: getTodayActionId(row),
          kind:
            row.kind === "monitoring"
              ? "monitoring"
              : row.kind === "task"
                ? "task"
                : "appointment",
          status: actionStatus,
          date,
          notes: actionNotes.trim(),
          source: completionSource,
        } as const;
        const signature = JSON.stringify(actionIntent);
        const existingSubmission = actionSubmissionRef.current;
        const submission =
          existingSubmission?.signature === signature
            ? existingSubmission
            : {
                signature,
                key: createIdempotencyKey(),
                payload: {
                  cycleId: null,
                  date,
                  text: serializeTodayActionCompletion({
                    ...actionIntent,
                    recordedAt: new Date().toISOString(),
                  }),
                },
              };
        actionSubmissionRef.current = submission;
        await createPatientNote.mutateAsync({
          ...submission.payload,
          idempotencyKey: submission.key,
        });
      }
    } catch (error) {
      console.error(
        "[TodayRowCompletionDialog] Failed to save completion:",
        error,
      );
      if (isMedication) {
        showToast({
          variant: "error",
          message: t("homepage.review_concept.medication_log_save_failed"),
        });
      } else {
        setSaveError(t("homepage.review_concept.action_log_save_failed"));
      }
      return;
    }

    onComplete(row);
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
    >
      <View style={{ maxHeight: Dimensions.get("window").height * 0.78 }}>
        {medicationIllustration ? (
          <DialogIcon
            source={medicationIllustration}
            width={96}
            height={88}
            style={{ marginBottom: 10 }}
            testID="medication-log-illustration"
          />
        ) : null}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 20,
            lineHeight: 25,
            color: EMBER.colors.text,
            fontWeight: "900",
            paddingRight: medicationIllustration ? 0 : 28,
            textAlign: medicationIllustration ? "center" : "left",
          }}
        >
          {title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 18,
            color: EMBER.colors.textMid,
            marginTop: 6,
            marginBottom: 16,
            textAlign: medicationIllustration ? "center" : "left",
          }}
        >
          {summary}
        </Text>

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            gap: 14,
            paddingBottom: 18,
          }}
        >
          {isMedication ? (
            <>
              <View style={{ gap: 8 }}>
                {medicationLogOptions.map((option) => {
                  const visual = MEDICATION_LOG_VISUALS[option.id];
                  return (
                    <TodayCompletionChoice
                      key={option.id}
                      label={option.label}
                      detail={option.detail}
                      selected={medicationStatus === option.id}
                      icon={visual.icon}
                      accentColor={visual.color}
                      iconBackgroundColor={visual.iconBackgroundColor}
                      selectedBackgroundColor={visual.selectedBackgroundColor}
                      selectedBorderColor={visual.selectedBorderColor}
                      onPress={() => {
                        haptic("selection");
                        setMedicationStatus(option.id);
                      }}
                    />
                  );
                })}
              </View>
              <DialogField
                label={t("homepage.review_concept.field_notes")}
                value={medicationNotes}
                onChangeText={setMedicationNotes}
                placeholder={t(
                  "homepage.review_concept.placeholder_medication_notes",
                )}
                multiline
              />
            </>
          ) : (
            <>
              <View style={{ gap: 8 }}>
                {actionOptions.map((option) => (
                  <TodayCompletionChoice
                    key={option.id}
                    label={option.label}
                    detail={option.detail}
                    selected={actionStatus === option.id}
                    onPress={() => {
                      haptic("selection");
                      setActionStatus(option.id);
                    }}
                  />
                ))}
              </View>
              <DialogField
                label={t("homepage.review_concept.field_notes")}
                value={actionNotes}
                onChangeText={setActionNotes}
                placeholder={t(
                  isMonitoring
                    ? "homepage.review_concept.placeholder_monitoring_notes"
                    : isAppointment
                      ? "homepage.review_concept.placeholder_appointment_result"
                      : "homepage.review_concept.placeholder_generic_notes",
                )}
                multiline
              />
              {onRequestDocumentAttachment ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    capturePress("attach_document");
                    onRequestDocumentAttachment(row);
                  }}
                  style={({ pressed }) => ({
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor: "rgba(121,70,51,0.16)",
                    backgroundColor: "rgba(255,255,255,0.46)",
                    paddingHorizontal: 13,
                    paddingVertical: 11,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 9,
                    opacity: pressed ? 0.72 : 1,
                  })}
                >
                  <Paperclip size={16} color="#794633" strokeWidth={2} />
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.text,
                        fontSize: 13,
                        lineHeight: 17,
                        fontWeight: "800",
                      }}
                    >
                      {t("homepage.review_concept.attach_document")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textMid,
                        fontSize: 11.5,
                        lineHeight: 15,
                        marginTop: 1,
                      }}
                    >
                      {t("homepage.review_concept.attach_document_detail")}
                    </Text>
                  </View>
                </Pressable>
              ) : null}
            </>
          )}
        </ScrollView>

        {saveError ? (
          <Text
            className="font-dm-sans"
            style={{
              color: "#A44B3B",
              fontSize: 12,
              lineHeight: 16,
              fontWeight: "700",
              paddingTop: 2,
            }}
          >
            {saveError}
          </Text>
        ) : null}

        <View
          style={{
            flexDirection: "row",
            gap: 10,
            paddingTop: 6,
          }}
        >
          {isMedication ? (
            <>
              <SoftSecondaryButton
                label={t("homepage.review_concept.dialog_cancel")}
                onPress={onClose}
                flex={1}
              />
              <DialogPrimaryButton
                label={t("homepage.review_concept.dialog_save")}
                onPress={() => void handleSave()}
                loading={logDose.isPending}
                flex={1}
              />
            </>
          ) : (
            <>
              <SoftSecondaryButton
                label={t("homepage.review_concept.dialog_cancel")}
                onPress={onClose}
                flex={1}
              />
              <DialogPrimaryButton
                label={t("homepage.review_concept.dialog_save")}
                onPress={() => void handleSave()}
                loading={createPatientNote.isPending}
                flex={1}
              />
            </>
          )}
        </View>
      </View>
    </Dialog>
  );
}

function TodayCompletionChoice({
  label,
  detail,
  selected,
  icon: Icon,
  accentColor = "#794633",
  iconBackgroundColor = "rgba(121,70,51,0.08)",
  selectedBackgroundColor = "rgba(121,70,51,0.09)",
  selectedBorderColor = "rgba(121,70,51,0.32)",
  onPress,
}: {
  label: string;
  detail: string;
  selected: boolean;
  icon?: LucideIcon;
  accentColor?: string;
  iconBackgroundColor?: string;
  selectedBackgroundColor?: string;
  selectedBorderColor?: string;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("TodayCompletionChoice");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label));
        onPress();
      }}
      style={{
        borderRadius: 16,
        borderWidth: 1,
        borderColor: selected ? selectedBorderColor : "rgba(0,0,0,0.08)",
        backgroundColor: selected
          ? selectedBackgroundColor
          : "rgba(255,255,255,0.52)",
        paddingHorizontal: 12,
        paddingVertical: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
      }}
    >
      {Icon ? (
        <View
          style={{
            width: 30,
            height: 30,
            borderRadius: 15,
            borderWidth: 1,
            borderColor: selected ? selectedBorderColor : "rgba(0,0,0,0.08)",
            backgroundColor: iconBackgroundColor,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={16} color={accentColor} strokeWidth={2.2} />
        </View>
      ) : (
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 10,
            borderWidth: 1.4,
            borderColor: selected ? "rgba(121,70,51,0.46)" : "rgba(0,0,0,0.14)",
            backgroundColor: selected ? "rgba(121,70,51,0.16)" : "transparent",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {selected ? (
            <Check size={12} color="#794633" strokeWidth={3} />
          ) : null}
        </View>
      )}
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 17,
            color: selected && Icon ? accentColor : EMBER.colors.text,
            fontWeight: "800",
          }}
        >
          {label}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textMid,
            marginTop: 1,
          }}
        >
          {detail}
        </Text>
      </View>
    </Pressable>
  );
}

function TaskList({
  values,
  action,
  onAction,
  onCompletionFeedback,
}: {
  values: Task[];
  action?: PlanAction;
  onAction: (action: PlanAction) => void;
  onCompletionFeedback?: () => void;
}) {
  const rows = values.map(taskToTodayRow);

  return (
    <View style={{ gap: 10 }}>
      <TodayList
        rows={rows}
        onCompletionFeedback={onCompletionFeedback}
        showCompletion
      />
      {action ? (
        <View style={{ alignSelf: "flex-start" }}>
          <CarePillActionButton
            label={action.label}
            trailingIcon={ArrowRight}
            onPress={() => onAction(action)}
          />
        </View>
      ) : null}
    </View>
  );
}

/**
 * A quiet text action inside the hero, for the steps the single primary button
 * cannot hold. Extracted so the recorded-result hero can carry more than one
 * without the two drifting apart in size or colour.
 */
function HeroInlineLink({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("HeroInlineLink");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label));
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed }) => ({
        paddingVertical: 8,
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 16,
          color: "rgba(255,255,255,0.9)",
          fontWeight: "700",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function TimelineView({ items }: { items: TimelineItem[] }) {
  return (
    <View style={{ gap: 0 }}>
      {items.map((item, index) => {
        const isDone = item.status === "done";
        const isNow = item.status === "now";
        return (
          <View
            key={`${item.label}-${index}`}
            style={{ flexDirection: "row", gap: 10 }}
          >
            <View style={{ alignItems: "center", width: 20 }}>
              <View
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 999,
                  backgroundColor: isDone
                    ? "#71A77C"
                    : isNow
                      ? EMBER.colors.text
                      : "rgba(107,85,72,0.24)",
                  marginTop: 3,
                }}
              />
              {index < items.length - 1 ? (
                <View
                  style={{
                    width: 1,
                    flex: 1,
                    minHeight: 24,
                    backgroundColor: "rgba(107,85,72,0.18)",
                    marginTop: 4,
                    marginBottom: 4,
                  }}
                />
              ) : null}
            </View>
            <View style={{ flex: 1, paddingBottom: 13 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  lineHeight: 18,
                  color: EMBER.colors.text,
                  fontWeight: "800",
                }}
              >
                {item.label}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: EMBER.colors.textMid,
                  marginTop: 2,
                }}
              >
                {item.detail}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function OutcomeActionRowsView({
  rows,
  onAction,
}: {
  rows: OutcomeActionRow[];
  onAction: (action: PlanAction) => void;
}) {
  return (
    <View style={{ gap: 10 }}>
      {rows.map((row) => (
        <ActionRowCard
          key={row.title}
          leading={<OrganicMiniIcon kind={row.icon} size={30} />}
          title={row.title}
          subtitle={row.detail}
          onPress={row.action ? () => onAction(row.action!) : undefined}
        />
      ))}
    </View>
  );
}

/**
 * The carousel of informative cards, exported because a first pregnancy
 * announcement shows the same cards in a window on the cycle screen. One
 * presentation, so the two places cannot drift.
 */
export function WhatToKnowView({ cards }: { cards: WhatToKnowCard[] }) {
  return (
    <HorizontalSnapCarousel
      items={cards}
      equalizeItemHeights
      measureKey="active-cycle-what-to-know-v2"
      gap={10}
      fallbackCardWidth={260}
      getCardWidth={(containerWidth) =>
        Math.min(288, Math.max(236, containerWidth * 0.82))
      }
      keyExtractor={(card) => card.title}
      renderItem={(card, width, _index, equalizedHeight) => (
        <WhatToKnowCarouselCard
          card={card}
          width={width}
          equalizedHeight={equalizedHeight}
        />
      )}
      contentContainerStyle={{ paddingRight: 4 }}
    />
  );
}

function WhatToKnowCarouselCard({
  card,
  width,
  equalizedHeight,
}: {
  card: WhatToKnowCard;
  width: number;
  equalizedHeight?: number;
}) {
  const isCaution = card.tone === "caution";
  return (
    <View
      style={{
        width,
        minHeight: equalizedHeight,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: isCaution ? "rgba(121,70,51,0.2)" : "rgba(92,52,32,0.1)",
        backgroundColor: isCaution
          ? "rgba(121,70,51,0.07)"
          : "rgba(255,255,255,0.5)",
        paddingHorizontal: 12,
        paddingVertical: 12,
        gap: 9,
      }}
    >
      {/* The badge, and the card's own glyph beside it when it has one —
          drawn in the app's hand, and large enough to be read at a glance
          rather than decorate. Space-between so the two sit at the card's
          corners instead of crowding each other on a narrow card. */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <View
          style={{
            alignSelf: "flex-start",
            borderRadius: 999,
            backgroundColor: isCaution
              ? "rgba(121,70,51,0.11)"
              : "rgba(255,255,255,0.56)",
            borderWidth: 1,
            borderColor: isCaution
              ? "rgba(121,70,51,0.16)"
              : "rgba(92,52,32,0.08)",
            paddingHorizontal: 8,
            paddingVertical: 4,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 12,
              color: EMBER.colors.textLight,
              fontWeight: "900",
              textTransform: "uppercase",
            }}
          >
            {card.badge}
          </Text>
        </View>
        {card.illustration ? (
          <OrganicMiniIcon kind={card.illustration} size={34} />
        ) : null}
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          lineHeight: 19,
          color: EMBER.colors.text,
          fontWeight: "900",
        }}
      >
        {card.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textMid,
        }}
      >
        {card.body}
      </Text>
      <View style={{ gap: 7 }}>
        {card.bullets.map((entry) => {
          const bullet =
            typeof entry === "string" ? { label: entry, tag: null } : entry;
          // A tagged step is one we could work out for her, so its marker is the
          // accent rather than the same faint dot as the prose around it. The
          // untagged lines stay quiet: colouring everything would be colouring
          // nothing.
          const hasTag = !!bullet.tag;
          return (
            <View
              key={bullet.label}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 7,
              }}
            >
              {bullet.icon ? (
                <View style={{ marginTop: -3, marginLeft: -2, flexShrink: 0 }}>
                  <OrganicMiniIcon kind={bullet.icon} size={22} />
                </View>
              ) : (
                <View
                  style={{
                    width: hasTag ? 6 : 5,
                    height: hasTag ? 6 : 5,
                    borderRadius: 999,
                    backgroundColor: isCaution
                      ? "rgba(121,70,51,0.58)"
                      : hasTag
                        ? EMBER.colors.warm
                        : "rgba(92,52,32,0.36)",
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
              )}
              <View style={{ flex: 1, gap: 4 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    lineHeight: 16,
                    color: EMBER.colors.text,
                    fontWeight: "700",
                  }}
                >
                  {bullet.label}
                </Text>
                {bullet.tag ? (
                  <View
                    style={{
                      alignSelf: "flex-start",
                      borderRadius: 999,
                      paddingHorizontal: 9,
                      paddingVertical: 3,
                      backgroundColor: "rgba(121,70,51,0.09)",
                      borderWidth: 1,
                      borderColor: "rgba(121,70,51,0.16)",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        lineHeight: 15,
                        color: EMBER.colors.warmDark,
                        fontWeight: "800",
                      }}
                    >
                      {bullet.tag}
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function DialogField({
  label,
  value,
  onChangeText,
  placeholder,
  unit,
  multiline = false,
}: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  unit?: string;
  multiline?: boolean;
}) {
  return (
    <View style={{ gap: 5 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          lineHeight: 14,
          color: EMBER.colors.textLight,
          fontWeight: "800",
        }}
      >
        {label}
      </Text>
      <View
        style={{
          flexDirection: "row",
          alignItems: multiline ? "flex-start" : "center",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: "rgba(92,52,32,0.11)",
          backgroundColor: "rgba(255,255,255,0.48)",
          paddingHorizontal: 11,
          paddingVertical: multiline ? 9 : 0,
        }}
      >
        <TextInput
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor="rgba(107,85,72,0.42)"
          multiline={multiline}
          keyboardType={unit && !multiline ? "decimal-pad" : "default"}
          className="font-dm-sans"
          style={{
            flex: 1,
            minHeight: multiline ? 62 : 38,
            paddingVertical: 0,
            color: EMBER.colors.text,
            fontSize: 13,
            lineHeight: multiline ? 18 : undefined,
            fontWeight: "700",
          }}
        />
        {unit ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 14,
              color: EMBER.colors.textLight,
              fontWeight: "800",
              marginLeft: 8,
            }}
          >
            {unit}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function DecisionView({ options }: { options: DecisionOption[] }) {
  return (
    <View style={{ gap: 9 }}>
      {options.map((option) => (
        <View
          key={option.title}
          style={{
            borderRadius: 14,
            backgroundColor: "rgba(255,255,255,0.5)",
            padding: 10,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: 18,
                color: EMBER.colors.text,
                fontWeight: "800",
              }}
            >
              {option.title}
            </Text>
            <View
              style={{
                borderRadius: 999,
                backgroundColor: "rgba(92,52,32,0.08)",
                paddingHorizontal: 8,
                paddingVertical: 4,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  lineHeight: 13,
                  color: EMBER.colors.textLight,
                  fontWeight: "800",
                  textTransform: "uppercase",
                }}
              >
                {option.status}
              </Text>
            </View>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: EMBER.colors.textMid,
              marginTop: 4,
            }}
          >
            {option.detail}
          </Text>
        </View>
      ))}
    </View>
  );
}

function LabMetricPill({ metric }: { metric: LabUpdateMetric }) {
  const isPending = metric.value.toLowerCase() === "pending";

  return (
    <View
      style={{
        borderRadius: 14,
        borderWidth: 1,
        borderStyle: isPending ? "dashed" : "solid",
        borderColor: isPending ? "rgba(92,52,32,0.14)" : "rgba(92,52,32,0.1)",
        backgroundColor: isPending
          ? "rgba(255,255,255,0.28)"
          : "rgba(255,255,255,0.46)",
        paddingHorizontal: 10,
        paddingVertical: 9,
        gap: 2,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          lineHeight: 13,
          color: EMBER.colors.textLight,
          fontWeight: "900",
          textTransform: "uppercase",
        }}
      >
        {metric.label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          lineHeight: 19,
          color: isPending ? EMBER.colors.textLight : EMBER.colors.text,
          fontWeight: "900",
        }}
      >
        {metric.value}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          lineHeight: 14,
          color: EMBER.colors.textMid,
          fontWeight: "500",
        }}
      >
        {metric.note}
      </Text>
    </View>
  );
}

function LabUpdateCarouselCard({
  card,
  width,
  equalizedHeight,
}: {
  card: LabUpdateCard;
  width: number;
  equalizedHeight?: number;
}) {
  const t = useTranslations();
  const isPending = card.status === "pending";

  return (
    <View
      style={{
        width,
        minHeight: equalizedHeight,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: isPending ? "rgba(92,52,32,0.08)" : "rgba(92,52,32,0.12)",
        backgroundColor: isPending
          ? "rgba(255,255,255,0.3)"
          : "rgba(255,255,255,0.48)",
        padding: 13,
        gap: 11,
        opacity: isPending ? 0.78 : 1,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <CardHeader
          style={{ flex: 1 }}
          title={card.title}
          subtitle={card.timeframe}
          subtitleVariant="meta"
          titleStyle={{
            fontSize: 16,
            lineHeight: 20,
            fontWeight: "900",
          }}
          subtitleStyle={{ marginTop: 2, fontWeight: "800" }}
        />
        <View
          style={{
            borderRadius: 999,
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.1)",
            backgroundColor:
              card.status === "next"
                ? "rgba(121,70,51,0.1)"
                : "rgba(255,255,255,0.36)",
            paddingHorizontal: 8,
            paddingVertical: 4,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 13,
              color:
                card.status === "next" ? "#794633" : EMBER.colors.textLight,
              fontWeight: "900",
              textTransform: "uppercase",
            }}
          >
            {card.status === "next"
              ? t("homepage.review_concept.next_label")
              : t("homepage.review_concept.not_yet_label")}
          </Text>
        </View>
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textMid,
          fontWeight: "500",
        }}
      >
        {card.body}
      </Text>

      <View style={{ gap: 8 }}>
        {card.metrics.map((metric) => (
          <LabMetricPill
            key={`${card.title}-${metric.label}`}
            metric={metric}
          />
        ))}
      </View>
    </View>
  );
}

export function LabUpdateCarousel({ cards }: { cards: LabUpdateCard[] }) {
  return (
    <HorizontalSnapCarousel
      items={cards}
      gap={10}
      fallbackCardWidth={252}
      getCardWidth={(containerWidth) =>
        Math.min(282, Math.max(238, containerWidth * 0.82))
      }
      keyExtractor={(card) => card.title}
      equalizeItemHeights
      measureKey="embryo-lab-update-carousel"
      renderItem={(card, cardWidth, _index, equalizedHeight) => (
        <LabUpdateCarouselCard
          card={card}
          width={cardWidth}
          equalizedHeight={equalizedHeight}
        />
      )}
    />
  );
}

function CycleLogisticsStatusIcon({
  status,
}: {
  status: CycleLogisticsChecklistItem["status"];
}) {
  const isDone = status === "done";

  return (
    <View
      style={{
        width: 20,
        height: 20,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: isDone ? "rgba(121,70,51,0.22)" : "rgba(92,52,32,0.12)",
        backgroundColor: isDone
          ? "rgba(121,70,51,0.12)"
          : "rgba(255,255,255,0.42)",
        marginTop: 1,
      }}
    >
      {isDone ? (
        <Check size={12} color="#794633" strokeWidth={2.3} />
      ) : (
        <Clock3
          size={11}
          color={
            status === "next" ? EMBER.colors.textMid : EMBER.colors.textLight
          }
          strokeWidth={1.9}
        />
      )}
    </View>
  );
}

function CycleLogisticsCarouselCard({
  card,
  width,
  equalizedHeight,
  onAction,
}: {
  card: CycleLogisticsCard;
  width: number;
  equalizedHeight?: number;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <View
      style={{
        width,
        minHeight: equalizedHeight,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.1)",
        backgroundColor: "rgba(255,255,255,0.48)",
        padding: 13,
        gap: 11,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <OrganicMiniIcon kind={card.icon} size={36} />
        </View>
        <CardHeader
          style={{ flex: 1 }}
          title={card.title}
          subtitle={card.timeframe}
          subtitleVariant="meta"
          titleStyle={{
            fontSize: 16,
            lineHeight: 20,
            fontWeight: "900",
          }}
          subtitleStyle={{ marginTop: 2, fontWeight: "800" }}
        />
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textMid,
          fontWeight: "500",
        }}
      >
        {card.body}
      </Text>

      <View style={{ gap: 8 }}>
        {card.items.map((item) => (
          <View
            key={`${card.title}-${item.label}`}
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 9,
            }}
          >
            <CycleLogisticsStatusIcon status={item.status} />
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12.5,
                  lineHeight: 16,
                  color: EMBER.colors.text,
                  fontWeight: "800",
                }}
              >
                {item.label}
              </Text>
              <MetaText style={{ marginTop: 1, fontWeight: "600" }}>
                {item.detail}
              </MetaText>
            </View>
          </View>
        ))}
      </View>

      {card.action ? (
        <Pressable
          onPress={() => onAction(card.action!)}
          accessibilityRole="button"
          accessibilityLabel={card.action.label}
          style={({ pressed }) => ({
            alignSelf: "flex-start",
            flexDirection: "row",
            alignItems: "center",
            gap: 5,
            opacity: pressed ? 0.72 : 1,
            marginTop: "auto",
          })}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 15,
              color: EMBER.colors.text,
              fontWeight: "800",
            }}
          >
            {card.action.label}
          </Text>
          <ArrowRight size={13} color={EMBER.colors.text} strokeWidth={1.8} />
        </Pressable>
      ) : null}
    </View>
  );
}

function CycleLogisticsCarousel({
  cards,
  onAction,
}: {
  cards: CycleLogisticsCard[];
  onAction: (action: PlanAction) => void;
}) {
  return (
    <HorizontalSnapCarousel
      items={cards}
      gap={10}
      fallbackCardWidth={252}
      getCardWidth={(containerWidth) =>
        Math.min(282, Math.max(238, containerWidth * 0.82))
      }
      keyExtractor={(card) => card.title}
      equalizeItemHeights
      measureKey="active-cycle-logistics-carousel"
      renderItem={(card, cardWidth, _index, equalizedHeight) => (
        <CycleLogisticsCarouselCard
          card={card}
          width={cardWidth}
          equalizedHeight={equalizedHeight}
          onAction={onAction}
        />
      )}
    />
  );
}

function CollapsibleOutcomeEstimateFunnelWidget({
  widget,
  onAction,
}: {
  widget: Extract<Widget, { type: "outcome-estimate-funnel" }>;
  onAction: (action: PlanAction) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const capturePress = usePressCapture(
    "CollapsibleOutcomeEstimateFunnelWidget",
  );
  const { data: patient } = usePatient();
  const userAge = calculateAge(patient?.dateOfBirth);

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as Record<string, unknown>),
        borderRadius: 18,
        padding: 15,
      }}
    >
      <Pressable
        onPress={() => {
          capturePress(pressLabel(widget.title));
          setExpanded((current) => !current);
        }}
        accessibilityRole="button"
        accessibilityLabel={`${expanded ? "Collapse" : "Expand"} ${widget.title}`}
        style={({ pressed }) => ({
          opacity: pressed ? 0.72 : 1,
        })}
      >
        <CardHeader
          leading={<OrganicMiniIcon kind={widget.icon} size={36} />}
          title={widget.title}
          subtitle={widget.subtitle}
          trailing={
            <CollapsibleChevronButton
              expanded={expanded}
              color={EMBER.colors.textLight}
              style={{ marginTop: 2 }}
            />
          }
        />
      </Pressable>

      <Collapsible expanded={expanded}>
        <View style={{ paddingTop: 12 }}>
          <OutcomeEstimateFunnelSection
            userAge={userAge}
            onAction={onAction}
            variant={widget.variant}
          />
        </View>
      </Collapsible>
    </View>
  );
}

function CryoVaultWidget({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const { storageGroups } = useFrozenSpecimens();
  const { data: oocyteCohorts = [] } = useOocyteCohorts();
  const buildSnapshots = useCryoSpecimenSnapshotsBuilder();
  const liveSpecimens = buildSnapshots(storageGroups, oocyteCohorts);
  const openVault = () =>
    onAction({
      label: t("homepage.review_concept.inline.action_view_cryo_vault"),
      screen: "CryoVaultScreen",
    });
  return (
    <CryoVaultOverviewCard
      specimens={liveSpecimens}
      onPress={openVault}
      onAddSpecimen={openVault}
      onNoSpecimens={openVault}
    />
  );
}

function WidgetView({
  widget,
  onAction,
  renderMonitoringCard,
  realTestResults,
  testResultsQueryState,
  projectionSourceCycleId,
  onOpenMedicationFactSheet,
  onOpenProcedureFactSheet,
  onOpenTestFactSheet,
  onRequestDocumentAttachment,
  onCompletionFeedback,
  todayRows,
  peekAheadDays,
}: {
  widget: Widget;
  onAction: (action: PlanAction) => void;
  renderMonitoringCard?: (
    widget: Extract<Widget, { type: "monitoring-chart" }>,
  ) => ReactNode;
  realTestResults?: ResultInterpretation[];
  testResultsQueryState?: TestResultInsightsQueryState;
  projectionSourceCycleId?: string | null;
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  onCompletionFeedback?: () => void;
  /** Real today agenda from the patient's meds/appointments. When provided
   *  (even empty), it replaces the variant's fixture rows so the Today section
   *  reflects real data and falls back to its empty state. */
  todayRows?: TodayRow[];
  peekAheadDays?: PeekAheadDay[];
}) {
  if (widget.type === "today") {
    return (
      <TodayList
        rows={todayRows ?? widget.rows}
        onOpenMedicationFactSheet={onOpenMedicationFactSheet}
        onOpenProcedureFactSheet={onOpenProcedureFactSheet}
        onRequestDocumentAttachment={onRequestDocumentAttachment}
        onCompletionFeedback={onCompletionFeedback}
        peekAheadDays={peekAheadDays}
      />
    );
  }

  if (widget.type === "action-rows") {
    return <OutcomeActionRowsView rows={widget.rows} onAction={onAction} />;
  }

  if (widget.type === "outcome-estimate-funnel") {
    return (
      <CollapsibleOutcomeEstimateFunnelWidget
        widget={widget}
        onAction={onAction}
      />
    );
  }

  if (widget.type === "test-results") {
    return (
      <TestResultsPreview
        results={realTestResults}
        title={widget.title}
        subtitle={widget.subtitle}
        queryState={testResultsQueryState}
        onAction={onAction}
        onOpenTestFactSheet={onOpenTestFactSheet}
      />
    );
  }

  if (widget.type === "monitoring-chart") {
    if (renderMonitoringCard) return renderMonitoringCard(widget);
    return (
      <MonitoringEmptyStateCard
        title={widget.title}
        onAddData={() => onAction(widget.primaryAction)}
        showSectionTitle={false}
        marginTop={0}
      />
    );
  }

  if (widget.type === "data-status") {
    return (
      <ActionRowCard
        leading={<OrganicMiniIcon kind={widget.icon} size={30} />}
        title={widget.title}
        subtitle={widget.detail}
        onPress={widget.action ? () => onAction(widget.action!) : undefined}
        accessibilityLabel={`${widget.title}. ${widget.detail}`}
      />
    );
  }

  if (widget.type === "what-to-know") {
    return <WhatToKnowView cards={widget.cards} />;
  }

  if (widget.type === "cycle-logistics") {
    return <CycleLogisticsCarousel cards={widget.cards} onAction={onAction} />;
  }

  if (widget.type === "semen-analysis") {
    return (
      <SpermAnalysisSkeletonCard
        onAction={onAction}
        layout="single"
        results={widget.results}
      />
    );
  }

  if (widget.type === "cryo-vault") {
    return <CryoVaultWidget onAction={onAction} />;
  }

  if (widget.type === "future-use-estimate") {
    return (
      <FutureUseEstimateTabs
        primaryUnit={widget.primaryUnit}
        sourceCycleId={projectionSourceCycleId}
        onAction={onAction}
      />
    );
  }

  if (widget.type === "sperm-cryosurvival-estimate") {
    return <SpermCryosurvivalEstimateWidget onAction={onAction} />;
  }

  if (widget.type === "handoff") {
    /*
     * No list builds one of these today. The rule is narrower than "don't":
     * a handoff meant to *close the page* cannot be placed from a list, because
     * a list renders above the records section `PlanHomeScreen` appends — which
     * is why the post-cycle card became `PostCycleHandoffCard` above. A
     * mid-page handoff is still a coherent thing to want, and this renders it.
     */
    return (
      <JourneyHandoffCard
        title={widget.title}
        body={widget.body}
        ctaLabel={widget.ctaLabel}
        checklist={widget.checklist}
        backgroundImage={handoffBackground}
        illustration={<OrganicExplanatoryIllustration kind="follicle-cohort" />}
        onPress={() => onAction(widget.action)}
      />
    );
  }

  if (widget.type === "funnel" && widget.bare) {
    // OutcomeDotFunnelRows already provides its own card, so render it without
    // an extra wrapper to avoid a double box under the section title.
    return (
      <>
        {widget.subtitle ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.textLight,
              marginBottom: 8,
            }}
          >
            {widget.subtitle}
          </Text>
        ) : null}
        <OutcomeDotFunnelRows
          stages={widget.stages.map((stage, index) => ({
            id: `${stage.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`,
            label: stage.label,
            value: stage.count,
            detail: stage.detail,
          }))}
          totalDots={widget.stages[0]?.count ?? 1}
        />
      </>
    );
  }

  if (widget.type === "tasks") {
    // Framed like every other widget so the list keeps its heading. Returning a
    // bare `TaskList` dropped `title` and `subtitle` on the floor, which left
    // rows like the post-positive red flags on screen with nothing saying what
    // they were — "Severe one-sided pain" reads as a task to do rather than a
    // reason to call the clinic. The action stays with `TaskList`, which renders
    // it as a pill button, so the frame is not given one to draw twice.
    return (
      <WidgetFrame
        title={widget.title}
        subtitle={widget.subtitle}
        icon={widget.icon}
        onAction={onAction}
      >
        <TaskList
          values={widget.tasks}
          action={widget.action}
          onAction={onAction}
          onCompletionFeedback={onCompletionFeedback}
        />
      </WidgetFrame>
    );
  }

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      icon={widget.icon}
      action={"action" in widget ? widget.action : undefined}
      onAction={onAction}
    >
      {widget.type === "metrics" ? (
        <MetricGrid metrics={widget.metrics} />
      ) : widget.type === "timeline" ? (
        <TimelineView items={widget.items} />
      ) : widget.type === "funnel" ? (
        <OutcomeDotFunnelRows
          stages={widget.stages.map((stage, index) => ({
            id: `${stage.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`,
            label: stage.label,
            value: stage.count,
            detail: stage.detail,
          }))}
          totalDots={widget.stages[0]?.count ?? 1}
        />
      ) : widget.type === "lab-carousel" ? (
        <LabUpdateCarousel cards={widget.cards} />
      ) : (
        <DecisionView options={"options" in widget ? widget.options : []} />
      )}
    </WidgetFrame>
  );
}

export function ConceptSectionView({
  section: conceptSection,
  onAction,
  renderMonitoringCard,
  hideSectionTitle = false,
  realTestResults,
  testResultsQueryState,
  projectionSourceCycleId,
  onOpenMedicationFactSheet,
  onOpenProcedureFactSheet,
  onOpenTestFactSheet,
  onRequestDocumentAttachment,
  onCompletionFeedback,
  topSpacing = 18,
  todayRows,
  peekAheadDays,
}: {
  section: ConceptSection;
  onAction: (action: PlanAction) => void;
  renderMonitoringCard?: (
    widget: Extract<Widget, { type: "monitoring-chart" }>,
  ) => ReactNode;
  /**
   * The canonical monitoring widget owns its own title row. This is an
   * explicit Home-only layout decision; other ConceptSectionView consumers
   * should keep their section title even when previewing a monitoring widget.
   */
  hideSectionTitle?: boolean;
  realTestResults?: ResultInterpretation[];
  testResultsQueryState?: TestResultInsightsQueryState;
  projectionSourceCycleId?: string | null;
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  onCompletionFeedback?: () => void;
  topSpacing?: number;
  todayRows?: TodayRow[];
  peekAheadDays?: PeekAheadDay[];
}) {
  const t = useTranslations();
  const localizedActions = useMemo(() => buildActions(t), [t]);
  const sectionTitleIcons: Record<string, LucideIcon> = {
    "cycle logistics": CalendarClock,
    "good to know": Info,
    "monitoring progress": TrendingUp,
    testing: FlaskConical,
    today: CalendarCheck,
  };
  const sectionTitleIcon =
    sectionTitleIcons[conceptSection.title.toLowerCase()];
  const todayTitle = t(
    "homepage.review_concept.section.today_title",
  ).toLowerCase();
  const sectionAction =
    conceptSection.action ??
    (conceptSection.title.toLowerCase() === todayTitle ||
    conceptSection.title.toLowerCase() === "today"
      ? localizedActions.calendar
      : undefined);

  return (
    <View style={{ gap: 10, marginTop: topSpacing }}>
      {conceptSection.title && !hideSectionTitle ? (
        <SectionTitleRow
          title={conceptSection.title}
          icon={sectionTitleIcon}
          action={
            sectionAction
              ? {
                  label: sectionAction.label,
                  onPress: () => onAction(sectionAction),
                }
              : undefined
          }
          style={{ marginBottom: 0 }}
        />
      ) : null}
      {conceptSection.widgets.map((widget, index) => (
        <WidgetView
          key={`${conceptSection.title}-${widget.type}-${index}`}
          widget={widget}
          onAction={onAction}
          renderMonitoringCard={renderMonitoringCard}
          realTestResults={realTestResults}
          testResultsQueryState={testResultsQueryState}
          projectionSourceCycleId={projectionSourceCycleId}
          onOpenMedicationFactSheet={onOpenMedicationFactSheet}
          onOpenProcedureFactSheet={onOpenProcedureFactSheet}
          onOpenTestFactSheet={onOpenTestFactSheet}
          onRequestDocumentAttachment={onRequestDocumentAttachment}
          onCompletionFeedback={onCompletionFeedback}
          todayRows={todayRows}
          peekAheadDays={peekAheadDays}
        />
      ))}
    </View>
  );
}

type PostProcedureOutcomeStageView = {
  title: string;
  summary: string;
  sections: ConceptSection[];
};

type PostProcedureOutcomeMetadata = {
  source?: "provider" | "patient" | "partner" | "system" | null;
  verificationStatus?: "unverified" | "verified" | "refuted" | null;
  /** What the record says the outcome was, for copy that must not be guessed. */
  recordedType?: PostProcedureOutcomeResult | null;
  /**
   * The fields that change when a pregnancy stops being one without `type`
   * changing: a retraction, and a birth or a loss recorded on the same row. The
   * acknowledged stage is decided once and never revisited, so any content that
   * only holds while a pregnancy is ongoing has to read these itself. See
   * `shouldShowEarlyPregnancyGuidance`.
   */
  resultStatus?: string | null;
  recordStatus?: string | null;
  pregnancyOutcome?: string | null;
  miscarriageOutcome?: string | null;
};

function buildPostProcedureOutcomeStageView(
  t: TranslationFunction,
  stage: PostProcedureOutcomeStage,
  contentKey: PostProcedureOutcomeContentKey | null,
  metadata: PostProcedureOutcomeMetadata,
  pregnancyTrackingActionLabel: string,
  route: PostProcedureOutcomeRoute,
  viewerIsSupporting: boolean,
): PostProcedureOutcomeStageView | null {
  /**
   * A standing pregnancy does not wait to be acknowledged.
   *
   * Everything this view builds — the record row, what the first weeks hold, the
   * offer to follow the pregnancy, somewhere to put a question — is information,
   * and none of it depends on a week count. Behind `stage === "recorded"` it
   * existed only once she had pressed "Let's look at what's next", a button whose
   * label promises nothing and which disappears when pressed. So a patient who
   * had reported a pregnancy and given no weeks had a page with her medication
   * list on it and nothing else. Withholding what the first weeks usually hold —
   * including which symptoms are worth a call — is not a thing to make her earn.
   *
   * The two-beat flow stays for every other ending. A negative or unsettled result
   * is a decision waiting to be made, and that is what the pause was designed for.
   */
  const standingPregnancy = shouldShowEarlyPregnancyGuidance({
    outcome: {
      type: metadata.recordedType,
      resultStatus: metadata.resultStatus,
      recordStatus: metadata.recordStatus,
      verificationStatus: metadata.verificationStatus,
      pregnancyOutcome: metadata.pregnancyOutcome,
      miscarriageOutcome: metadata.miscarriageOutcome,
    },
    viewerIsSupporting,
  });
  if (stage === "recorded" && !standingPregnancy) return null;

  const shared = "homepage.review_concept.post_procedure_outcome.shared";
  const modalityKey = contentKey ?? "ivf";
  const resultVariant = route.endsWith("-positive")
    ? "positive"
    : route.endsWith("-negative")
      ? "negative"
      : route.endsWith("-unavailable")
        ? "unavailable"
        : route.endsWith("-uncertain")
          ? "uncertain"
          : "adverse";
  const isPositive = resultVariant === "positive";
  const isNegative = resultVariant === "negative";
  const resultCopy = `homepage.review_concept.post_procedure_outcome.${modalityKey}.${resultVariant}`;
  const sourceKey = metadata.source ?? "unknown";
  const verificationKey = metadata.verificationStatus ?? "unverified";
  const saveQuestionAction: PlanAction = {
    label: t(`${shared}.stage_action_save_question`),
    signal: "open-ai-chat",
    params: {
      initialPrompt: t(`${shared}.stage_action_save_question_prompt`),
    },
  };
  const pregnancyTrackingAction: PlanAction = {
    label: pregnancyTrackingActionLabel,
    signal: "open-pregnancy-tracking-setup",
  };
  const readyRows: OutcomeActionRow[] = [
    ...(isPositive && !viewerIsSupporting
      ? [
          {
            title: pregnancyTrackingActionLabel,
            detail: t(`${shared}.stage_action_tracking_detail`),
            icon: "clinics" as const,
            action: pregnancyTrackingAction,
          },
        ]
      : []),
    {
      title: t(`${shared}.stage_action_save_question`),
      detail: t(`${shared}.stage_action_save_question_detail`),
      icon: "concierge",
      action: saveQuestionAction,
    },
  ];
  const resultValueKey = isPositive
    ? "known_result_value_pregnancy"
    : isNegative
      ? "known_result_value_no_pregnancy"
      : "known_result_value_not_confirmed";
  // "Unverified" is the assumed default for a patient-entered record, so
  // repeating it under a positive result reads as "this result isn't confirmed"
  // and undermines what she just recorded. Only surface the verification clause
  // when it carries new information — a clinic mark of verified, or a refuted
  // correction she should know about.
  const showsVerification =
    verificationKey === "verified" || verificationKey === "refuted";
  const knownResultDetail = [
    t(`${shared}.${resultValueKey}`),
    t(`${shared}.known_result_source_${sourceKey}`),
    showsVerification
      ? t(`${shared}.known_result_verification_${verificationKey}`)
      : null,
  ]
    .filter(Boolean)
    .join(" ");
  const knownResultRow: OutcomeActionRow = {
    title: t(`${resultCopy}.result_label`),
    detail: knownResultDetail,
    icon: "medical-records",
    // Opens PastCycleDetail for a closed cycle (or CycleDetail focused on the
    // result for an open one) so tapping the row lands on the recorded result
    // instead of doing nothing.
    action: {
      label: t(`${shared}.stage_action_review_records`),
      signal: "open-recorded-cycle",
      params: { focus: "outcome" },
    },
  };
  return {
    title: t(`${resultCopy}.title`),
    summary: t(`${resultCopy}.summary`),
    sections: [
      section(
        t(`${resultCopy}.today_section_title`),
        t(`${resultCopy}.section_subtitle`),
        [today(t(`${resultCopy}.today_section_title`), [])],
      ),
      section(t(`${shared}.known_title`), t(`${shared}.known_subtitle`), [
        actionRows([knownResultRow]),
      ]),
      section(
        t(`${shared}.when_ready_title`),
        t(`${shared}.when_ready_subtitle`),
        [actionRows(readyRows)],
      ),
    ],
  };
}

export function HomepageReviewConceptHome({
  variant,
  onAction,
  renderMonitoringCard,
  testInsights,
  testResultsQueryState,
  projectionSourceCycleId,
  onOpenSchedule,
  onOpenJourneyMap,
  onOpenOutcomeFunnel,
  onOpenMedicationFactSheet,
  onOpenProcedureFactSheet,
  onOpenTestFactSheet,
  onRequestDocumentAttachment,
  todayRows: realTodayRows,
  peekAheadDays,
  afterTodaySlot,
  endOfPageSlot,
  onOpenPregnancyTrackingSetup,
  cycleStatusReviewKind,
  outcomeCycleId,
  endedCycleId,
  endingInterruptsAnotherSubject = false,
  isAcknowledgingEnding = false,
  acknowledgeEndingFailed = false,
  viewerIsSupporting = false,
  onScrollToY,
}: {
  variant: HomepageReviewConceptVariant;
  cycleStatusReviewKind?: CycleStatusReviewKind | null;
  onAction: (action: PlanAction) => void;
  renderMonitoringCard?: (
    widget: Extract<Widget, { type: "monitoring-chart" }>,
  ) => ReactNode;
  /** Test results recorded for this patient; empty means no result is available. */
  testInsights?: ResultInterpretation[];
  /** Query state for the real Test Results read used by the Testing section. */
  testResultsQueryState?: TestResultInsightsQueryState;
  /** Closed cycle whose stored specimens back a post-cycle projection. */
  projectionSourceCycleId?: string | null;
  /**
   * Cycle that owns the outcome this home is presenting.
   *
   * `/active-cycle` returns nothing once a cycle closes, so the hero cannot find
   * a closed cycle's outcome on its own — without this the acknowledged-negative
   * pause is unreachable and the stage pins to `recorded` forever. The screen
   * resolves it from the same closed-cycle reading that chose the variant, so the
   * hero can never address a different cycle than the variant was derived from.
   */
  outcomeCycleId?: string | null;
  /**
   * The viewer is supporting someone else's cycle, not living it.
   *
   * The ending belongs to both of them — `acknowledgedAt` is on the shared
   * outcome — but the words for it do not. Defaults to the patient's voice, so a
   * surface that forgets to pass this addresses the person whose cycle it is.
   */
  viewerIsSupporting?: boolean;
  /**
   * Scroll the page that owns this block to an absolute offset.
   *
   * The scroll view belongs to the screen, not here, so reaching a section is
   * asked for rather than done. Absent, the hero action still acknowledges and
   * simply does not move the page.
   */
  onScrollToY?: (y: number) => void;
  onOpenSchedule?: () => void;
  onOpenJourneyMap?: () => void;
  onOpenOutcomeFunnel?: () => void;
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  /** Real today agenda (meds + appointments) for the in-cycle Today section.
   *  When provided it replaces the variant's fixture rows. */
  todayRows?: TodayRow[];
  peekAheadDays?: PeekAheadDay[];
  /** Content rendered between the first (Today) section and the rest, so the
   *  two-week-wait home can surface the chat + readings above Testing / Good
   *  to know. */
  afterTodaySlot?: ReactNode;
  /** Content rendered after all concept sections, near the foot of the page —
   *  used by outcome + stim homes to surface a curated reading below the rest
   *  of the surface, close to Documents. */
  endOfPageSlot?: ReactNode;
  onOpenPregnancyTrackingSetup?: () => void;
  /**
   * The cycle the ending on screen belongs to, when Home is reporting on a closed
   * one.
   *
   * Separate from `outcomeCycleId`, which is only set on the post-result routes
   * because it decides which record the hero *reads*. This one only names a
   * destination, so it is also set for the cancelled surface — whose variant is
   * `cycle-status-review`, not a post-procedure route, and which therefore has no
   * cycle id of its own to link to.
   */
  endedCycleId?: string | null;
  /**
   * Whether acknowledging this ending hands Home back to something else. When it
   * does not, the acknowledgement changes nothing on screen — see
   * `closedEndingInterruptsAnotherSubject` — so the hero offers the cycle instead
   * of a button that appears dead.
   */
  endingInterruptsAnotherSubject?: boolean;
  isAcknowledgingEnding?: boolean;
  /**
   * A refused acknowledgement used to revert in silence, which is what made this
   * look like a button that does nothing. The carousel that used to own this
   * press at least had a pending state; the hero needs both.
   */
  acknowledgeEndingFailed?: boolean;
}) {
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const t = useTranslations();
  const captureConceptPress = usePressCapture("HomepageReviewConceptHome");
  const { locale } = useLocale();
  const cycleHeaderState = useCycleHeaderState();
  const { cycle: activeCycleForHero, phases: heroPhases = [] } =
    useActiveCycleWithPhases({ cycleId: outcomeCycleId ?? null });
  const allFrozenSpecimens = useFrozenSpecimens();
  const cycleFrozenSpecimens = useFrozenSpecimensForCycle(
    projectionSourceCycleId,
  );
  const isOutcomeVariant = isPostProcedureOutcomeVariant(variant);
  // Server-held. A week count that changes with the device is not a week count.
  const pregnancyFollowUp = useCyclePregnancyFollowUp(
    isOutcomeVariant && variant.endsWith("-positive")
      ? activeCycleForHero?.id
      : null,
  );
  const outcomeStageState = usePostProcedureOutcomeStage({
    route: isOutcomeVariant ? variant : "ivf-negative",
    cycleId: activeCycleForHero?.id,
    // Acknowledgement belongs to the outcome event, not the cycle shell. A
    // corrected or additional result must not inherit the old cycle state.
    outcomeId: activeCycleForHero?.outcome?.id,
    acknowledgement:
      activeCycleForHero?.outcome?.id &&
      activeCycleForHero.outcome.acknowledgedAt
        ? {
            outcomeId: activeCycleForHero.outcome.id,
            acknowledgedAt: activeCycleForHero.outcome.acknowledgedAt,
          }
        : null,
  });
  /**
   * The same audience and still-ongoing test the guidance cards use, asked in
   * the component so the dated section answers to it too. One rule, two
   * surfaces: a partner must not be shown either, and neither must someone whose
   * pregnancy has already ended.
   */
  const showsEarlyPregnancyGuidance = shouldShowEarlyPregnancyGuidance({
    outcome: {
      type: activeCycleForHero?.outcome?.type ?? null,
      resultStatus: activeCycleForHero?.outcome?.resultStatus ?? null,
      recordStatus: activeCycleForHero?.outcome?.recordStatus ?? null,
      verificationStatus:
        activeCycleForHero?.outcome?.verificationStatus ?? null,
      pregnancyOutcome: activeCycleForHero?.outcome?.pregnancyOutcome ?? null,
      miscarriageOutcome:
        activeCycleForHero?.outcome?.miscarriageOutcome ?? null,
    },
    viewerIsSupporting,
  });

  /**
   * What she has said about the pregnancy, if anything.
   *
   * `summarisePregnancyStatus` is the existing answer to "has she said so",
   * and it is deliberately strict: `type: "pregnancy"` is written by the beta
   * recording, not by her, so only the update step's week count counts as her
   * word. Gestational age is gated on the same evidence.
   */
  const announcedPregnancyStands =
    summarisePregnancyStatus({
      type: activeCycleForHero?.outcome?.type ?? null,
      pregnancyOutcome: activeCycleForHero?.outcome?.pregnancyOutcome ?? null,
      miscarriageOutcome:
        activeCycleForHero?.outcome?.miscarriageOutcome ?? null,
      weekCount: activeCycleForHero?.outcome?.weekCount ?? null,
      liveBirthCount: activeCycleForHero?.outcome?.liveBirthCount ?? null,
    })?.stands ?? null;

  /**
   * The dates the "what comes next" steps carry, when there are any.
   *
   * Her reported weeks are the dating; the follow-up's own gestation refines it
   * where it exists, because it knows the day within the week. Undefined is the
   * ordinary case, and the card then reads exactly as it did before any of this.
   */
  const earlyPregnancyDating = useMemo(() => {
    const serverGestation = pregnancyFollowUp.data?.gestation ?? null;
    const reported = gestationFromReportedWeeks({
      weekCount: activeCycleForHero?.outcome?.weekCount ?? null,
      // Local, because `today` is. `updatedAt` is an instant, and slicing its
      // ISO form yields a UTC calendar date: west of UTC in the evening that is
      // tomorrow, which trips the "reported after today" guard and removes every
      // date for the rest of her day. East of UTC it adds one to her age.
      reportedOn: activeCycleForHero?.outcome?.updatedAt
        ? toLocalDateString(new Date(activeCycleForHero.outcome.updatedAt))
        : null,
      today: toLocalDateString(new Date()),
    });
    return buildEarlyPregnancyDating({
      t,
      locale,
      announcement: {
        followUpStatus: pregnancyFollowUp.data?.status ?? null,
        pregnancyStands: announcedPregnancyStands,
      },
      gestation: serverGestation
        ? {
            asOf: serverGestation.asOf,
            gestationalDays: serverGestation.gestationalDays,
          }
        : reported,
      preciseAge: serverGestation
        ? {
            weeks: serverGestation.gestationalWeeks,
            dayOfWeek: serverGestation.gestationalDayOfWeek,
          }
        : null,
      resultDate: activeCycleForHero?.outcome?.dateRecorded ?? null,
      today: toLocalDateString(new Date()),
    });
  }, [
    activeCycleForHero?.outcome?.dateRecorded,
    activeCycleForHero?.outcome?.updatedAt,
    activeCycleForHero?.outcome?.weekCount,
    announcedPregnancyStands,
    locale,
    pregnancyFollowUp.data?.gestation,
    pregnancyFollowUp.data?.status,
    t,
  ]);

  /**
   * "Good to know" belongs to the pregnancy, not to a stage of the page.
   *
   * It used to live inside the acknowledged-stage view, so it existed only once
   * she had pressed "Regardons la suite" — and pressing it replaced the surface
   * that carried the button. The content is the same either way: what the first
   * weeks usually hold, how a beta is read, when not to wait. She should be able
   * to reach it by scrolling, and the hero action should take her to it rather
   * than conjure it.
   */
  const earlyPregnancySection: ConceptSection | null = useMemo(() => {
    // Both halves matter. The audience test reads the outcome record, which an
    // ordinary Home can also carry — without the variant check, any page whose
    // cycle holds an ongoing pregnancy grew this section, labelled with IVF copy
    // by the `?? "ivf"` fallback below.
    if (!isOutcomeVariant || !showsEarlyPregnancyGuidance) return null;
    const shared = "homepage.review_concept.post_procedure_outcome.shared";
    return section(
      t(`${shared}.good_to_know`),
      t(`${shared}.what_happens_next_and_who_to_ask`),
      [
        whatToKnow(
          t(`${shared}.the_first_few_weeks`),
          "panel",
          buildEarlyPregnancyWhatToKnowCards(
            t,
            getPostProcedureOutcomeContentKey(variant) ?? "ivf",
            earlyPregnancyDating,
          ),
        ),
      ],
    );
  }, [
    earlyPregnancyDating,
    isOutcomeVariant,
    showsEarlyPregnancyGuidance,
    t,
    variant,
  ]);

  /** Where this block starts in the page, and where the section starts in it. */
  const [homeTopY, setHomeTopY] = useState(0);
  const [earlyPregnancyY, setEarlyPregnancyY] = useState<number | null>(null);

  /**
   * Take her to the guidance rather than summoning it.
   *
   * The hero action used to acknowledge and, in doing so, replace the surface it
   * was on — the button vanished and the content it promised appeared somewhere
   * she had not been looking. It still acknowledges, because that is a real
   * thing the record carries, but what she sees is the page moving to the
   * section she asked for.
   */
  const scrollToEarlyPregnancy = useCallback(() => {
    if (earlyPregnancyY === null) return;
    onScrollToY?.(Math.max(0, homeTopY + earlyPregnancyY - 12));
  }, [earlyPregnancyY, homeTopY, onScrollToY]);

  /**
   * A scroll that waits for the page to stop moving.
   *
   * Acknowledging adds the stage's own sections above the guidance, so a scroll
   * fired in the same handler lands correctly and is then pushed down by content
   * that arrived after it. Asking again once the section reports a new offset is
   * what makes the page settle where she was sent.
   */
  const pendingScrollRef = useRef<number | null>(null);
  useEffect(() => {
    const measuredWhenAsked = pendingScrollRef.current;
    if (measuredWhenAsked === null || earlyPregnancyY === null) return;
    // Only once the section has actually moved. Waiting on a re-render would
    // fire before the new sections have laid out — the very thing this defers
    // past — so the offset changing is the signal, not the render.
    if (earlyPregnancyY === measuredWhenAsked) return;
    pendingScrollRef.current = null;
    scrollToEarlyPregnancy();
  }, [earlyPregnancyY, scrollToEarlyPregnancy]);

  const outcomeMetadata = useMemo<PostProcedureOutcomeMetadata>(
    () => ({
      source: activeCycleForHero?.outcome?.source,
      verificationStatus: activeCycleForHero?.outcome?.verificationStatus,
      recordedType: activeCycleForHero?.outcome?.type ?? null,
      resultStatus: activeCycleForHero?.outcome?.resultStatus ?? null,
      recordStatus: activeCycleForHero?.outcome?.recordStatus ?? null,
      pregnancyOutcome: activeCycleForHero?.outcome?.pregnancyOutcome ?? null,
      miscarriageOutcome:
        activeCycleForHero?.outcome?.miscarriageOutcome ?? null,
    }),
    [
      activeCycleForHero?.outcome?.source,
      activeCycleForHero?.outcome?.verificationStatus,
      activeCycleForHero?.outcome?.type,
      activeCycleForHero?.outcome?.resultStatus,
      activeCycleForHero?.outcome?.recordStatus,
      activeCycleForHero?.outcome?.pregnancyOutcome,
      activeCycleForHero?.outcome?.miscarriageOutcome,
    ],
  );
  /**
   * A birth recorded against this cycle, when there is one.
   *
   * Read from the record rather than from the route, because the route cannot
   * tell: a birth keeps `type: "pregnancy"`, so it arrives on the same
   * `*-outcome-positive` Home as a beta drawn this morning. See
   * `resolveRecordedBirth`.
   */
  const recordedBirth = useMemo(
    () =>
      resolveRecordedBirth({
        type: activeCycleForHero?.outcome?.type ?? null,
        resultStatus: activeCycleForHero?.outcome?.resultStatus ?? null,
        recordStatus: activeCycleForHero?.outcome?.recordStatus ?? null,
        verificationStatus:
          activeCycleForHero?.outcome?.verificationStatus ?? null,
        pregnancyOutcome: activeCycleForHero?.outcome?.pregnancyOutcome ?? null,
        miscarriageOutcome:
          activeCycleForHero?.outcome?.miscarriageOutcome ?? null,
        liveBirthCount: activeCycleForHero?.outcome?.liveBirthCount ?? null,
      }),
    [
      activeCycleForHero?.outcome?.type,
      activeCycleForHero?.outcome?.resultStatus,
      activeCycleForHero?.outcome?.recordStatus,
      activeCycleForHero?.outcome?.verificationStatus,
      activeCycleForHero?.outcome?.pregnancyOutcome,
      activeCycleForHero?.outcome?.miscarriageOutcome,
      activeCycleForHero?.outcome?.liveBirthCount,
    ],
  );

  /**
   * What the hero says once a baby has been born: the birth, and nothing about a
   * result whose next step has already happened.
   */
  const recordedBirthHeroCopy = useMemo(() => {
    if (!recordedBirth) return null;
    const keys = recordedBirthCopyKeys({
      birth: recordedBirth,
      viewerIsSupporting,
    });
    return { title: t(keys.titleKey), summary: t(keys.bodyKey) };
  }, [recordedBirth, t, viewerIsSupporting]);

  /**
   * And what it says while the pregnancy is still going.
   *
   * `showsEarlyPregnancyGuidance` is the same question the guidance section asks —
   * is there a standing pregnancy, and is this the person who carried the cycle —
   * so the heading and the content it heads can never disagree. A birth takes its
   * voice ahead of this one, and cannot collide with it: a birth sets
   * `pregnancyOutcome`, which is what makes the guidance test false.
   */
  const standingPregnancyHeroCopy = useMemo(() => {
    if (!showsEarlyPregnancyGuidance) return null;
    const keys = standingPregnancyCopyKeys({ viewerIsSupporting });
    return { title: t(keys.titleKey), summary: t(keys.bodyKey) };
  }, [showsEarlyPregnancyGuidance, t, viewerIsSupporting]);

  /**
   * And what it says when she closed the cycle without naming an ending.
   *
   * Read from the record for the same reason the two above are: the route is
   * resolved from an outcome state that maps `other` onto "uncertain", which is
   * a different claim — "we can't call this result" rather than "there was no
   * result to call" — and it is the one the modality copy is written for.
   */
  const closedWithoutResultHeroCopy = useMemo(() => {
    if (
      !wasClosedWithoutARecordedEnding({
        cycleStatus: activeCycleForHero?.status,
        outcomeType: activeCycleForHero?.outcome?.type,
      })
    ) {
      return null;
    }
    const keys = closedWithoutResultCopyKeys({ viewerIsSupporting });
    return { title: t(keys.titleKey), summary: t(keys.bodyKey) };
  }, [
    activeCycleForHero?.status,
    activeCycleForHero?.outcome?.type,
    t,
    viewerIsSupporting,
  ]);

  /**
   * The burst that says the app noticed.
   *
   * Fired once per visit, not once per record: the ref is what keeps a re-render
   * from setting it off again while she reads the page. It repeats on her next
   * visit, which is the same thing the recorded-result card in the treatment hub
   * does — and it only lasts as long as the cycle stays open, because closing it
   * is what this hero is asking for.
   */
  const celebratedBirthRef = useRef(false);
  useEffect(() => {
    if (!recordedBirth || celebratedBirthRef.current) return;
    celebratedBirthRef.current = true;
    setConfettiTrigger((current) => current + 1);
  }, [recordedBirth]);

  const dynamicHeroCopy = useMemo(
    () => presentCycleHeaderForHero({ state: cycleHeaderState, t, locale }),
    [cycleHeaderState, t, locale],
  );

  // ─── Hero metrics widget ────────────────────────────────────────────────
  const treatmentDashboard = useTreatmentDashboard();
  const postRetrievalHeroCopy = useMemo(
    () => buildPostRetrievalHeroCopy(t, treatmentDashboard.rawCycleType),
    [t, treatmentDashboard.rawCycleType],
  );
  const { data: heroMedications = [] } = usePatientMedications({
    enabled: !!activeCycleForHero,
  });
  const dynamicHeroMetrics = useMemo(() => {
    const todayIso = toLocalDateString(new Date());
    let todayMedDoseCount = 0;
    for (const med of heroMedications) {
      if (!med.startDate || !med.timeTaken || med.timeTaken.length === 0) {
        continue;
      }
      if (med.startDate > todayIso) continue;
      if (med.endDate && med.endDate < todayIso) continue;
      todayMedDoseCount += med.timeTaken.length;
    }
    let daysPostTransfer: number | null = null;
    // luteal_phase.startDate is set to the transfer date when the user
    // enters the wait — that's the anchor for "days since transfer".
    const lutealPhase = heroPhases.find((p) => p.type === "luteal_phase");
    if (lutealPhase?.startDate) {
      const start = new Date(`${lutealPhase.startDate}T00:00:00`);
      const today = new Date(`${todayIso}T00:00:00`);
      const diff = Math.floor((today.getTime() - start.getTime()) / 86_400_000);
      if (diff >= 0) daysPostTransfer = diff;
    }
    return buildHeroMetrics({
      state: cycleHeaderState,
      t,
      locale,
      context: {
        leadFollicleMm:
          treatmentDashboard.monitoringSnapshot?.leadFollicleMm ?? null,
        estradiol: treatmentDashboard.monitoringSnapshot?.estradiol ?? null,
        endometriumMm:
          treatmentDashboard.monitoringSnapshot?.endometriumMm ?? null,
        estimatedRetrievalDate:
          treatmentDashboard.estimatedRetrieval?.date ?? null,
        daysPostTransfer,
        todayMedDoseCount,
      },
    });
  }, [
    cycleHeaderState,
    treatmentDashboard.monitoringSnapshot,
    treatmentDashboard.estimatedRetrieval,
    heroMedications,
    heroPhases,
    t,
    locale,
  ]);
  // ─── Two-week-wait hero treatment ───────────────────────────────────────
  // During the wait the hero's job is to carry "where am I": the day of the
  // wait (as a felt progress bar + title), the beta anchor date, and one
  // reassurance line that adapts to how far along the wait is. This overrides
  // the generic beta copy for the beta-wait / iui-wait homepage surfaces.
  const isBetaWait =
    cycleHeaderState.kind === "beta_scheduled" ||
    cycleHeaderState.kind === "beta_pending";
  const isSpermResultsWait = variant === "sperm-freezing-wait";
  const waitCopySuffix =
    cycleHeaderState.kind === "beta_scheduled" ||
    cycleHeaderState.kind === "beta_pending"
      ? getPostProcedureWaitContext(cycleHeaderState.cycleType).heroCopySuffix
      : null;
  const betaDateForHero =
    cycleHeaderState.kind === "beta_scheduled"
      ? cycleHeaderState.betaDate
      : null;
  const betaDateLabel = betaDateForHero
    ? formatRelativePhaseDate(betaDateForHero, locale, t)
    : null;
  const baseConcept = useConcepts(cycleStatusReviewKind, betaDateLabel)[
    variant
  ];
  const betaTimeForHero =
    cycleHeaderState.kind === "beta_scheduled"
      ? cycleHeaderState.betaTime
      : null;
  const waitProgress = useMemo(() => {
    if (!isBetaWait) return null;
    const luteal = heroPhases.find((p) => p.type === "luteal_phase");
    const start = luteal?.startDate ?? null;
    if (!start) return null;
    const startMs = new Date(`${start}T00:00:00`).getTime();
    const todayMs = new Date(
      `${toLocalDateString(new Date())}T00:00:00`,
    ).getTime();
    const dayOfWait = Math.max(0, Math.round((todayMs - startMs) / 86_400_000));
    let totalWaitDays: number | null = null;
    if (betaDateForHero) {
      const betaMs = new Date(`${betaDateForHero}T00:00:00`).getTime();
      const total = Math.round((betaMs - startMs) / 86_400_000);
      if (total > 0) totalWaitDays = total;
    }
    return { dayOfWait, totalWaitDays };
  }, [isBetaWait, heroPhases, betaDateForHero]);
  const HERO_DYNAMIC = "homepage.review_concept.hero_dynamic";
  const isPostProcedureOutcomeHome = isOutcomeVariant;
  const isUnresolvedOutcome =
    variant.endsWith("-unavailable") ||
    variant.endsWith("-uncertain") ||
    variant.endsWith("-adverse");
  const outcomeStage =
    isPostProcedureOutcomeHome && !isUnresolvedOutcome
      ? outcomeStageState.stage
      : "recorded";
  const outcomeStageView = useMemo(() => {
    if (!isPostProcedureOutcomeHome) return null;
    // A birth has its own hero, and the acknowledged stage view would talk over
    // it: its title is the positive result the birth grew out of, and its "When
    // you're ready" section offers to turn on pregnancy follow-up — a week count
    // and a due date for a pregnancy that has already ended in a baby. Dropping
    // it leaves the plain result concept underneath, which is a Today section and
    // nothing else.
    if (recordedBirth) return null;
    return buildPostProcedureOutcomeStageView(
      t,
      outcomeStage,
      getPostProcedureOutcomeContentKey(variant),
      outcomeMetadata,
      pregnancyTrackingSetupStatusLabel(
        t,
        pregnancyFollowUp.data?.status ?? null,
      ),
      variant,
      viewerIsSupporting,
    );
  }, [
    isPostProcedureOutcomeHome,
    outcomeStage,
    outcomeMetadata,
    pregnancyFollowUp.data?.status,
    recordedBirth,
    t,
    variant,
    viewerIsSupporting,
  ]);
  const waitHeroCopy = useMemo(() => {
    if (
      isPostProcedureOutcomeHome ||
      isSpermResultsWait ||
      !isBetaWait ||
      !waitProgress
    ) {
      return null;
    }
    const { dayOfWait, totalWaitDays } = waitProgress;
    const reassureKey =
      dayOfWait <= 3
        ? "wait_reassure_early"
        : totalWaitDays && dayOfWait >= totalWaitDays - 2
          ? "wait_reassure_late"
          : "wait_reassure_mid";
    const copyKey = (base: string) =>
      waitCopySuffix
        ? `${HERO_DYNAMIC}.${base}_${waitCopySuffix}`
        : `${HERO_DYNAMIC}.${base}`;
    const reassuranceKey = waitCopySuffix
      ? `${HERO_DYNAMIC}.${reassureKey}_${waitCopySuffix}`
      : `${HERO_DYNAMIC}.${reassureKey}`;
    return {
      eyebrow: { label: t(copyKey("wait_eyebrow")) },
      title: totalWaitDays
        ? t(`${HERO_DYNAMIC}.wait_day_title`, {
            params: { day: String(dayOfWait) },
          })
        : t(copyKey("wait_day_title_no_total"), {
            params: { day: String(dayOfWait) },
          }),
      summary: t(reassuranceKey),
    };
  }, [
    isBetaWait,
    isPostProcedureOutcomeHome,
    isSpermResultsWait,
    waitCopySuffix,
    waitProgress,
    t,
  ]);

  const heroStatusCopy = useMemo(() => buildHeroStatusCopy(t), [t]);
  const postCycleHeroCopy = useMemo(() => {
    if (!isPostCycleVariant(variant)) return null;
    const counts = projectionSourceCycleId
      ? {
          eggs: cycleFrozenSpecimens.cycleTotalEggs,
          embryos: cycleFrozenSpecimens.cycleTotalEmbryos,
          sperm: cycleFrozenSpecimens.cycleTotalSperm,
        }
      : {
          eggs: allFrozenSpecimens.totalEggs,
          embryos: allFrozenSpecimens.totalEmbryos,
          sperm: allFrozenSpecimens.totalSperm,
        };
    return buildPostCycleHeroCopy(t, variant, counts);
  }, [
    allFrozenSpecimens.totalEggs,
    allFrozenSpecimens.totalEmbryos,
    allFrozenSpecimens.totalSperm,
    cycleFrozenSpecimens.cycleTotalEggs,
    cycleFrozenSpecimens.cycleTotalEmbryos,
    cycleFrozenSpecimens.cycleTotalSperm,
    projectionSourceCycleId,
    t,
    variant,
  ]);
  const detailedSections = useMemo(() => buildDetailedConceptSections(t), [t]);
  const concept = outcomeStageView
    ? {
        ...baseConcept,
        title: outcomeStageView.title,
        summary: outcomeStageView.summary,
        sections: outcomeStageView.sections,
      }
    : baseConcept;
  // All three read the record, so all three come before the chain that reads the
  // route — which cannot tell a pregnancy in its tenth week from a beta drawn
  // this morning, and gave them the same heading, nor a cycle closed with no
  // ending from a result it could not call.
  const heroCopy = recordedBirthHeroCopy
    ? recordedBirthHeroCopy
    : standingPregnancyHeroCopy
      ? standingPregnancyHeroCopy
      : closedWithoutResultHeroCopy
        ? closedWithoutResultHeroCopy
        : outcomeStageView
          ? outcomeStageView
          : isPostCycleVariant(variant)
            ? postCycleHeroCopy
            : isPostProcedureOutcomeHome
              ? heroStatusCopy[variant]
              : isSpermResultsWait
                ? heroStatusCopy[variant]
                : variant === "post-retrieval"
                  ? postRetrievalHeroCopy
                  : variant === "cycle-status-review"
                    ? concept
                      ? { title: concept.title, summary: concept.summary }
                      : heroStatusCopy[variant]
                    : (dynamicHeroCopy ?? heroStatusCopy[variant]);
  // Pre-cycle variants (setup, learn-explorer, *-precycle) render through the
  // stepper home (GuidedPreCycleConceptHome), so they have no entry here. This
  // component is only ever rendered with review-dashboard variants; the guard
  // keeps the lookups type-safe now that the maps are Partial.
  if (!concept || !heroCopy) return null;
  /**
   * The page under the hero, when the hero says the cycle closed with no ending
   * recorded.
   *
   * `heroCopy` only replaces the title and summary, so the sections kept the ones
   * written for an unclear result: "Clarify what was recorded" sat directly below
   * "This cycle is closed", heading the day's medication list. The hero and the
   * page were answering different questions about the same cycle.
   */
  const conceptForHero =
    closedWithoutResultHeroCopy && concept
      ? {
          ...concept,
          sections: concept.sections.map((conceptSection, index) =>
            index === 0
              ? {
                  ...conceptSection,
                  title: t(
                    "homepage.closed_without_a_result.what_stays_in_your_records",
                  ),
                  subtitle: t(
                    "homepage.closed_without_a_result.nothing_here_needs_deciding_today",
                  ),
                }
              : conceptSection,
          ),
        }
      : concept;
  const handleConceptAction = (action: PlanAction) => {
    captureConceptPress(pressLabel(action.label));
    if (action.signal === "open-pregnancy-tracking-setup") {
      onOpenPregnancyTrackingSetup?.();
      return;
    }
    onAction(action);
  };
  const sections = [
    ...conceptForHero.sections,
    ...(detailedSections[variant] ?? []),
    ...(earlyPregnancySection ? [earlyPregnancySection] : []),
  ];
  const canShowOutcomeFunnel =
    variant.includes("egg") ||
    variant.includes("embryo") ||
    variant.includes("ivf") ||
    variant.includes("iui") ||
    variant.includes("sperm") ||
    variant.includes("family");
  /**
   * The fallback names the same destination the label promises.
   *
   * It is reachable — `HomepageCanvasScreen` renders this component with no
   * `onOpenSchedule` — though nothing navigates there today: that screen's
   * `handleAction` only records the action and handles two signals. So this is not
   * a bug being fixed, it is a fallback that no longer contradicts its button, for
   * whatever consumer comes next.
   *
   * It duplicates the literal `PlanHomeScreen` passes, and can therefore drift from
   * it. Worth one shared builder if a third consumer ever appears; two copies did
   * not seem to earn the indirection.
   */
  const openSchedule =
    onOpenSchedule ??
    (() =>
      onAction({
        label: t(`${HERO_DYNAMIC}.cta.record_my_beta`),
        screen: "CycleDetail",
        params: { focus: "outcome" },
      }));
  const betaEndLabel = betaDateForHero
    ? [
        t(`${HERO_DYNAMIC}.wait_progress_end`),
        formatRelativePhaseDate(betaDateForHero, locale, t),
        betaTimeForHero ? formatPhaseTime(betaTimeForHero, locale) : null,
      ]
        .filter(Boolean)
        .join(" ")
    : t(`${HERO_DYNAMIC}.wait_progress_end`);
  const waitProgressStartLabel = waitCopySuffix
    ? t(`${HERO_DYNAMIC}.wait_progress_start_${waitCopySuffix}`)
    : t(`${HERO_DYNAMIC}.wait_progress_start`);
  /**
   * The hero menu's Calendar item opens the Care page, where the calendar
   * actually lives.
   *
   * Kept separate from `openSchedule`, which the beta-wait CTA uses to reach the
   * cycle's outcome step — where the result is entered. Sharing one handler sent
   * Calendar to the cycle instead. No `focus` param here: that opens the
   * add-appointment modal, which is not what a calendar link should do.
   */
  const openCareCalendar = () =>
    onAction({
      label: t("homepage.review_concept.calendar_label"),
      screen: "CareOverview",
    });

  const heroActions: HeroMenuAction[] = [
    {
      icon: CalendarClock,
      label: t("homepage.review_concept.calendar_label"),
      onPress: openCareCalendar,
    },
    ...(canShowOutcomeFunnel && onOpenOutcomeFunnel
      ? [
          {
            icon: Funnel,
            label: t("homepage.review_concept.outlook_label"),
            onPress: onOpenOutcomeFunnel,
          },
        ]
      : []),
    ...(onOpenJourneyMap
      ? [
          {
            icon: MapIcon,
            label: t("homepage.review_concept.journey_map_label"),
            onPress: onOpenJourneyMap,
          },
        ]
      : []),
  ];
  /**
   * A result is recorded but the cycle is still open. Closing it is the hero's
   * primary action — the ending belongs in the hero rather than in a card stacked
   * above it.
   *
   * Now that Home resolves a recorded result on an open cycle, the outcome
   * surface can be showing at the same time, and it has its own next step:
   * acknowledging the result, which is what opens the way to pregnancy follow-up.
   * Only one primary action fits, so closing keeps it and acknowledging sits
   * beside "Review cycle" below — see `bottomContent`. Without that the
   * acknowledge action had no route to the screen at all.
   */
  const isRecordedResultHero = cycleHeaderState.kind === "result_recorded";

  /**
   * The tap that ends the interruption, and the ways out beside it.
   *
   * `homeSubjectClosedCycle` lets a cycle she has just closed take Home from a
   * pregnancy it was following, and hands it back the moment `acknowledgedAt` is
   * set. The closing carousel normally writes that field, and it is suppressed on
   * a Home already built around this ending — otherwise the two state it twice,
   * in different words, one of them in a light card whose stacked buttons are in
   * none of the hero's design language. Which left the ending with no way to be
   * dismissed at all: the acknowledge path in `usePostProcedureOutcomeStage`
   * refuses any route with no acknowledged surface, and `-uncertain` has none.
   *
   * So the hero carries it. The write is not route-gated — it is the same
   * mutation the carousel calls — so it travels as a signal like the other hero
   * side-effects.
   *
   * `endedCycleId` is the gate, not the variant. `cycle-status-review` is
   * reachable with the cycle still *open*: a positive beta the clinic later
   * withdraws resolves to `cancelled` without closing anything, and that Home's
   * hero owns "Close this cycle" — the only route to closing it from here. Keyed
   * on the variant alone, this action displaced it and pointed instead at a list
   * of closed cycles that could not contain the open one. The id is non-null only
   * where a closed ending drives the experience, which is exactly where this hero
   * has an ending to acknowledge.
   */
  const endsInAnAcknowledgeableHero =
    !!closedWithoutResultHeroCopy ||
    (variant === "cycle-status-review" && !!endedCycleId);

  const openEndedCycleAction = (
    label: string,
    cycleId: string,
  ): PlanAction => ({
    label,
    screen: "PastCycleDetail",
    params: { cycleId },
  });

  const endedCycleHeroAction: HeroPrimaryAction | undefined =
    !endsInAnAcknowledgeableHero || !endedCycleId
      ? undefined
      : endingInterruptsAnotherSubject
        ? {
            label: t("homepage.cycle_closing.ui.done"),
            icon: Check,
            onPress: () =>
              isAcknowledgingEnding
                ? undefined
                : onAction({
                    label: t("homepage.cycle_closing.ui.done"),
                    signal: "acknowledge-closed-cycle",
                  }),
          }
        : {
            // Nothing to hand Home back to, so nothing to acknowledge. The way
            // into the closed cycle is the one thing left worth offering.
            label: t("homepage.closed_without_a_result.review_this_cycle"),
            icon: CalendarCheck,
            onPress: () =>
              onAction(
                openEndedCycleAction(
                  t("homepage.closed_without_a_result.review_this_cycle"),
                  endedCycleId,
                ),
              ),
          };

  const recordedResultHeroAction: HeroPrimaryAction | undefined =
    isRecordedResultHero
      ? {
          label: t("homepage.cycle_closing.ui.close_cycle"),
          icon: CalendarCheck,
          // Closing is a mutation the screen owns, so it travels as a signal the
          // same way the other hero side-effects do.
          onPress: () =>
            onAction({
              label: t("homepage.cycle_closing.ui.close_cycle"),
              signal: "close-recorded-cycle",
            }),
        }
      : undefined;

  /**
   * Open the cycle this result belongs to.
   *
   * A `CycleDetailScreen` action carrying no cycleId is read by the action router
   * as a request to create a cycle, so a "review what was recorded" button became
   * the start-treatment flow. The id is always sent; a closed cycle is reviewed
   * through history, an open one through its detail screen, and with no id at all
   * the patient lands on the cycle list rather than in a creation flow.
   */
  const reviewRecordedCycleAction = (label: string): PlanAction => {
    const cycleId = activeCycleForHero?.id;
    if (!cycleId) return { label, screen: "AllPastCycles" };
    return activeCycleForHero?.status === "closed"
      ? { label, screen: "PastCycleDetail", params: { cycleId } }
      : { label, screen: "CycleDetailScreen", params: { cycleId } };
  };

  const postRetrievalResultsAction = reviewRecordedCycleAction(
    t("homepage.review_concept.inline.post_retrieval.add_or_edit_results"),
  );
  const postRetrievalFunnelStages =
    variant === "post-retrieval"
      ? buildPostRetrievalFunnelStages(
          heroPhases,
          activeCycleForHero?.cycleType,
          t,
          () => handleConceptAction(postRetrievalResultsAction),
        )
      : [];

  /**
   * Which links sit under "Close this cycle". The rule is four states wide, so it
   * lives in `resolveOutcomeHeroLinks` where it can be asserted; `outcomeHeroAction`
   * is resolved just below and only its existence matters here.
   */
  const heroLinks = resolveOutcomeHeroLinks({
    variant,
    outcomeStage,
    hasOutcomeAction: isPostProcedureOutcomeHome,
  });

  const outcomeHeroAction = isPostProcedureOutcomeHome
    ? (() => {
        const isPositive = variant.endsWith("-positive");
        const isUnavailable = variant.endsWith("-unavailable");
        const isUncertain = variant.endsWith("-uncertain");
        const isAdverse = variant.endsWith("-adverse");
        const isUnresolved = isUnavailable || isUncertain || isAdverse;
        const shared = "homepage.review_concept.post_procedure_outcome.shared";
        // Once a baby has been born, closing the cycle is the only thing left to
        // offer. What this would add is either "Let's look at what's next" —
        // which acknowledges the result to reach pregnancy follow-up, and there
        // is no follow-up to set up for a pregnancy that has ended in a birth —
        // or a second route into the cycle the hero already links to.
        //
        // Only when closing is actually on screen. On a cycle that is already
        // closed there is nothing to press instead, and suppressing this as well
        // would leave the hero with no action at all.
        if (recordedBirth && isRecordedResultHero) return undefined;
        // The hero owns the primary entry into the cycle. Secondary actions
        // below should not repeat the same destination, so the setup/pause
        // state uses the precise cycle-review label here and the action group
        // stays focused on questions and optional pregnancy tracking.
        // Nothing to acknowledge and nowhere to go: the hero would be offering a
        // press that changes nothing, so it names the cycle instead — and only
        // then, because the link beside it already goes there and two identical
        // links is the hero telling her twice.
        const canReachGuidance = Boolean(earlyPregnancySection);
        if (
          !isUnresolved &&
          !canReachGuidance &&
          (outcomeStage !== "recorded" || !outcomeStageState.canAcknowledge)
        ) {
          // Saying nothing is only right when the inline link beside the hero
          // already names the cycle — two identical links is the hero telling
          // her twice. Everywhere else, returning null left a closed or negative
          // outcome, and a partner on any of them, with no route into the cycle.
          if (isRecordedResultHero) {
            return null;
          }
          const reviewLabel = t(`${shared}.stage_action_review_records`);
          return {
            label: reviewLabel,
            icon: CalendarClock,
            onPress: () => onAction(reviewRecordedCycleAction(reviewLabel)),
          };
        }
        const label = isUnresolved
          ? t(
              `${shared}.${isAdverse ? "hero_adverse_action" : isUncertain ? "hero_uncertain_action" : "hero_unavailable_action"}`,
            )
          : t(`${shared}.hero_${isPositive ? "positive" : "negative"}_action`);
        return {
          label,
          icon: isUnresolved || isPositive ? CalendarCheck : CalendarClock,
          onPress: () => {
            if (isUnresolved) {
              onAction(reviewRecordedCycleAction(label));
              return;
            }
            // Acknowledging is a fact about the record and still happens, but it
            // is no longer what she gets for pressing: the page goes to the
            // guidance, which stays where it is whether or not she has pressed
            // anything.
            if (
              outcomeStage === "recorded" &&
              outcomeStageState.canAcknowledge
            ) {
              outcomeStageState.acknowledge();
              // The sections it adds sit above the guidance, so wait for the
              // section to re-measure rather than scrolling to where it was.
              // Remembering where it was is what lets us tell "it moved" from
              // "something else re-rendered"; a refused acknowledgement simply
              // never moves it, and the request lapses instead of firing later.
              pendingScrollRef.current = earlyPregnancyY;
              return;
            }
            scrollToEarlyPregnancy();
          },
        };
      })()
    : undefined;

  return (
    <View
      // Two measurements, because a section's own layout is relative to this
      // container and the container sits below other content in the page's
      // scroll view. Summed, they are where "Bon à savoir" actually starts.
      onLayout={(event) => setHomeTopY(event.nativeEvent.layout.y)}
      style={{ gap: 18, position: "relative", overflow: "visible" }}
    >
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: -80,
          height: Dimensions.get("window").height,
          zIndex: 50,
          overflow: "visible",
        }}
      >
        <ConfettiBurst trigger={confettiTrigger} />
      </View>
      <Hero
        title={waitHeroCopy ? waitHeroCopy.title : heroCopy.title}
        summary={waitHeroCopy ? waitHeroCopy.summary : heroCopy.summary}
        eyebrow={
          waitHeroCopy
            ? waitHeroCopy.eyebrow
            : heroCopy === dynamicHeroCopy
              ? dynamicHeroCopy?.eyebrow
              : undefined
        }
        metrics={
          !isPostProcedureOutcomeHome &&
          !isSpermResultsWait &&
          variant !== "post-retrieval" &&
          variant !== "cycle-status-review" &&
          dynamicHeroMetrics.length > 0
            ? dynamicHeroMetrics
            : concept.heroMetrics
        }
        metricVariant={
          isPostProcedureOutcomeHome ||
          isSpermResultsWait ||
          variant === "post-retrieval"
            ? undefined
            : isBetaWait
              ? "cards"
              : dynamicHeroMetrics.length > 0 &&
                  variant !== "cycle-status-review"
                ? "tags"
                : undefined
        }
        metricsFillRow={
          !isBetaWait &&
          !isPostProcedureOutcomeHome &&
          !isSpermResultsWait &&
          variant !== "post-retrieval"
        }
        backgroundSource={
          !isPostProcedureOutcomeHome &&
          !isSpermResultsWait &&
          variant !== "cycle-status-review" &&
          dynamicHeroCopy
            ? inCycleHeroBackground
            : undefined
        }
        primaryAction={
          // In the order the hero copy itself is resolved: the surface that owns
          // the words owns the button under them.
          endedCycleHeroAction ??
          recordedResultHeroAction ??
          outcomeHeroAction ??
          undefined
        }
        menuActions={heroActions}
        bottomContent={
          <>
            {/* A refused acknowledgement reverts in silence unless something says
              so, and the press that can be refused is offered on more surfaces
              than the recorded-result hero — a closed ending drives a different
              header state and still gets the button. So the message sits beside
              whatever the hero is showing, not inside one of its shapes. */}
            {outcomeStageState.didFail ||
            (endsInAnAcknowledgeableHero && acknowledgeEndingFailed) ? (
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: "rgba(255,255,255,0.72)",
                  marginBottom: 8,
                }}
              >
                {t(
                  "homepage.review_concept.post_procedure_outcome.shared.could_not_save_that_try_again",
                )}
              </Text>
            ) : null}
            {
              // The other ways out of a recorded-but-open cycle sit in the hero
              // beside the first, rather than in a card above it. They precede the
              // wait content deliberately: the wait is over once a result exists.
              //
              // `outcomeHeroAction` is here because the primary slot is taken. On the
              // outcome surface it is the acknowledge step, and acknowledging is what
              // reaches the setup stage and pregnancy follow-up — with only "Close
              // cycle" on screen there was no way to get there at all.
              //
              // Only when it is that step, though. See `resolveOutcomeHeroLinks`:
              // everywhere else it lands on the same screen as the link beside it,
              // so rendering both is the hero offering the same thing twice.
              // A birth is the exception: there is no follow-up left to reach,
              // so the hero carries "Close this cycle" and nothing beside it.
              // Absent when the primary action already goes here: the hero must
              // not offer the same destination twice.
              endedCycleHeroAction &&
              endingInterruptsAnotherSubject &&
              endedCycleId ? (
                <HeroInlineLink
                  label={t("homepage.cycle_closing.ui.open_cycle")}
                  onPress={() =>
                    onAction(
                      openEndedCycleAction(
                        t("homepage.cycle_closing.ui.open_cycle"),
                        endedCycleId,
                      ),
                    )
                  }
                />
              ) : isRecordedResultHero && !recordedBirth ? (
                <View style={{ gap: 8 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    {outcomeHeroAction && heroLinks.showsOutcomeAction ? (
                      <HeroInlineLink
                        label={outcomeHeroAction.label}
                        onPress={outcomeHeroAction.onPress}
                      />
                    ) : null}
                    {/* Through the same resolver the rest of the surface uses. Built by
                  hand, this link named `CycleDetail` with no cycleId whenever the
                  hero had no cycle to name — which `resolveCycleDetailAction`
                  reads as a request to start one, so "Revoir ce cycle" answered
                  with the start-treatment flow. It also sent a closed cycle to
                  the open-cycle route, which cannot show it.

                  Absent when the link above already asks for the result: that one
                  goes to the same screen and says why. */}
                    {heroLinks.showsReviewCycleLink ? (
                      <HeroInlineLink
                        label={t("homepage.cycle_closing.ui.review_cycle")}
                        onPress={() =>
                          onAction(
                            reviewRecordedCycleAction(
                              t("homepage.cycle_closing.ui.review_cycle"),
                            ),
                          )
                        }
                      />
                    ) : null}
                  </View>
                </View>
              ) : waitHeroCopy && waitProgress?.totalWaitDays ? (
                <HeroWaitProgress
                  day={waitProgress.dayOfWait}
                  total={waitProgress.totalWaitDays}
                  startLabel={waitProgressStartLabel}
                  endLabel={betaEndLabel}
                />
              ) : isBetaWait && !isPostProcedureOutcomeHome ? (
                <Pressable
                  onPress={() => {
                    captureConceptPress(
                      pressLabel(t(`${HERO_DYNAMIC}.cta.record_my_beta`)),
                    );
                    openSchedule();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={t(`${HERO_DYNAMIC}.cta.record_my_beta`)}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 16,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: pressed
                      ? "rgba(255,255,255,0.38)"
                      : "rgba(255,255,255,0.2)",
                    backgroundColor: pressed
                      ? "rgba(255,255,255,0.16)"
                      : "rgba(255,255,255,0.08)",
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                  })}
                >
                  {/* A flask, not a clock. The label and the destination now say
                      "put the result in", and a calendar glyph beside them still
                      said "book a date". */}
                  <FlaskConical
                    size={15}
                    color="rgba(255,255,255,0.86)"
                    strokeWidth={1.8}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      color: "rgba(255,255,255,0.9)",
                      fontSize: 12,
                      fontWeight: "700",
                    }}
                  >
                    {t(`${HERO_DYNAMIC}.cta.record_my_beta`)}
                  </Text>
                  <ArrowRight
                    size={15}
                    color="rgba(255,255,255,0.72)"
                    strokeWidth={1.8}
                  />
                </Pressable>
              ) : undefined
            }
          </>
        }
      />

      {sections.map((conceptSection, index) => (
        <Fragment key={`${conceptSection.title}-${index}`}>
          <View
            onLayout={
              conceptSection === earlyPregnancySection
                ? (event) => setEarlyPregnancyY(event.nativeEvent.layout.y)
                : undefined
            }
          >
            <ConceptSectionView
              section={conceptSection}
              onAction={handleConceptAction}
              renderMonitoringCard={renderMonitoringCard}
              hideSectionTitle={
                conceptSection.widgets.length === 1 &&
                conceptSection.widgets[0]?.type === "monitoring-chart"
              }
              realTestResults={testInsights}
              testResultsQueryState={testResultsQueryState}
              projectionSourceCycleId={projectionSourceCycleId}
              onOpenMedicationFactSheet={onOpenMedicationFactSheet}
              onOpenProcedureFactSheet={onOpenProcedureFactSheet}
              onOpenTestFactSheet={onOpenTestFactSheet}
              onRequestDocumentAttachment={onRequestDocumentAttachment}
              onCompletionFeedback={() =>
                setConfettiTrigger((current) => current + 1)
              }
              todayRows={realTodayRows}
              peekAheadDays={peekAheadDays}
            />
          </View>
          {index === 0 && variant === "post-retrieval" ? (
            <View style={{ marginTop: 18, gap: 10 }}>
              <SectionTitleRow
                title={t(
                  "homepage.review_concept.inline.post_retrieval.lab_section_title",
                )}
                icon={FlaskConical}
              />
              <OutcomeDotFunnelRows
                stages={postRetrievalFunnelStages}
                totalDots={postRetrievalFunnelStages[0]?.value ?? 1}
              />
            </View>
          ) : null}
          {/* Wait extras land right after the Today section, above Testing /
              Good to know. */}
          {index === 0 && afterTodaySlot ? afterTodaySlot : null}
        </Fragment>
      ))}
      {endOfPageSlot}
    </View>
  );
}
